import { Link, useSearchParams } from 'react-router-dom';
import { useEffect, useState, Fragment, type ReactNode, type ChangeEvent } from 'react';
import { api, type Me, type WxcUser, type WxcLocation, type WxcNumber, type WxcQueue, type WxccQueue, type WxccAgent, type WxccSkill, type WxccEntryPoint, type WxccInUseFlow, type WxccEntryPointNumbers, type SpokeRow, type CucmPhone, type CucmEndUser, type CucmLine, type CucmRoutePattern, type CucmDevicePool, type WxccAudioFile, type LgwGateway, type LgwCert, type LgwRenewal, type LgwSyncRun, type CubeDialPeer, type CubeSipTrunk, type CubeActiveCall, type CubeLogEvent, type LgwCdr, type WxcLocalGateway, type WxcRouteList, type WxcDialPlan, type LgwCertValidateResponse, type CubeDeviceCert, type LgwTrunk, type LgwTrunkCsrFetchResponse, type LgwTrunkCertUploadResponse, type ConnectorHealth } from '../lib/api';
import { WebexInventoryCard } from './Configuration';
import { LgwMergedCard } from './LgwMergedView';
import SipToolsPanel from './SipToolsPanel';
import SecurityMonitorPanel from './SecurityMonitorPanel';
import WxccCleanupPanel from './WxccCleanupPanel';

/**
 * Core Cisco/Webex module dashboards with sub-nav (feature areas) and
 * skeleton tables per area. WxC + WxCC tabs follow Webex Control Hub layout
 * (left-aligned tabs, dense table, primary CTA on the right). CUCM/UCCX/CUBE
 * follow Cisco CCM/CCX/CUBE patterns. All data is empty until the spoke-side
 * bridge adapter populates it; the shell + endpoints are wired so it lights
 * up the moment data arrives.
 */

type ModuleKey = 'wxc' | 'wxcc' | 'cucm' | 'uccx' | 'lgw' | 'cube' | 'sip' | 'security';

interface FeatureArea {
  key: string;
  label: string;
  columns: string[];
  primary?: { label: string; tone?: 'cisco' | 'webex' };
  api_note?: string;     // small caption under the table indicating the backing API
  // CUBE/LGW only — which half of the CUBE | LGW toggle this tab belongs to.
  // 'lgw' = cloud-reachable via Webex Calling. 'cube' = device-level via spoke.
  group?: 'cube' | 'lgw';
}

interface ModuleSpec {
  key: ModuleKey;
  title: string;
  subtitle: string;
  vendor: 'Cisco' | 'Webex';
  intent: string;
  tiles: { label: string; placeholder: string }[];
  areas: FeatureArea[];
}

const MODULES: Record<ModuleKey, ModuleSpec> = {
  security: {
    key: 'security',
    title: 'Security',
    subtitle: 'Live threat monitor — risk-score IPs, block attackers & AI scanners, kill sessions',
    vendor: 'Cisco',
    intent: 'Per-app Security Monitor: watch Cloudflare + app telemetry, risk-score every IP/session, detect AI scanners and attacks, and Block/Deny/Allow/Challenge an IP or kill active sessions — with shared threat intel across your apps.',
    tiles: [
      { label: 'Critical',      placeholder: '—' },
      { label: 'High',          placeholder: '—' },
      { label: 'Suspicious',    placeholder: '—' },
      { label: 'Blocked today', placeholder: '—' },
    ],
    areas: [
      { key: 'monitor',  label: 'Live Monitor',  columns: [], api_note: 'Risk-scored IP/session feed + per-threat response actions (security-monitor worker)' },
      { key: 'offenders', label: 'Top Offenders', columns: [], api_note: 'Highest-risk IPs with one-click Block / Deny / Challenge / Allow' },
      { key: 'sessions', label: 'Active Sessions', columns: ['User', 'Session', 'IP', 'Started', 'Risk', 'Actions'], api_note: 'Live application sessions — click a row for session details (username, device, last activity) and kill the session' },
      { key: 'posture',  label: 'Edge Posture',  columns: ['Setting', 'Now', 'Want', 'OK'], api_note: 'Cloudflare WAF / DDoS / SSL / Bot / Geo zones (security skill — Domain B)' },
      { key: 'audit',    label: 'Code Audit',    columns: ['Sev', 'Control', 'File', 'CWE', 'Fix'], api_note: 'AppSec scan findings (security skill — Domain A: SQLi, XSS, secrets, mass-assignment…)' },
    ],
  },
  sip: {
    key: 'sip',
    title: 'SIP Tools',
    subtitle: 'Super SIP testing & tracing — signaling 1xx-6xx · media + two-way · voice quality',
    vendor: 'Cisco',
    intent: 'Prove any PSTN number end-to-end across CCP / CUBE-LGW / CUCM: every SIP response code decoded, two-way media + MOS, single or bulk, inbound + outbound — or Upload SIP Logs for offline forensic analysis.',
    tiles: [
      { label: 'Tests today',     placeholder: '—' },
      { label: 'Pass rate',       placeholder: '—' },
      { label: 'Open WARN/FAIL',  placeholder: '—' },
      { label: 'Avg MOS',         placeholder: '—' },
    ],
    areas: [
      { key: 'single',  label: 'Single Trace',    columns: ['Number', 'Direction', 'Path', 'SIP result', 'Media / MOS', 'Verdict', 'Actions'],
        primary: { label: '+ Run test', tone: 'cisco' }, api_note: 'Single-number deep trace via CUBE SIP (OPTIONS-keepalive + test INVITE)' },
      { key: 'bulk',    label: 'Bulk DIDs',       columns: ['Block', 'Count', 'Pass', 'Warn', 'Fail', 'Started', 'Actions'],
        primary: { label: '+ Bulk test', tone: 'cisco' }, api_note: 'API-orchestrated bulk testing; pass/fail roster + export' },
      { key: 'upload',  label: 'Upload SIP Logs',  columns: ['File(s)', 'Source', 'Sessions', 'Codes', 'Verdict', 'Uploaded', 'Actions'],
        primary: { label: '+ Upload logs', tone: 'cisco' }, api_note: 'Consolidated analysis of CUBE/LGW, CUCM, WxC or PSTN-provider logs → decoded SIP ladder' },
      { key: 'signaling', label: 'SIP Signaling', columns: ['Time', 'Dir', 'Message', 'Code', 'Meaning', 'Route'],
        api_note: 'Full SIP response-code dictionary 1xx → 6xx' },
      { key: 'media',   label: 'Media / Two-way',  columns: ['Number', 'Direction', 'RTP', 'Two-way', 'MOS', 'Codec', 'Actions'],
        api_note: 'VoIPmonitor RTP/MOS · ThousandEyes RTP + UDP bidirectional · PESQ vs AudioPrompt reference' },
      { key: 'quality', label: 'Voice Quality / QA', columns: ['Location', 'Path', 'CScan', 'Loss', 'Jitter', 'MOS', 'BGP/ASN'],
        api_note: 'CScan network readiness + ThousandEyes continuous monitoring, dashboards & alerts' },
      { key: 'profiles', label: 'Test Profiles',  columns: ['Profile', 'Direction', 'Path', 'Pass criteria', 'Schedule', 'Actions'],
        primary: { label: '+ New profile', tone: 'cisco' }, api_note: 'Inbound/outbound SIP testing profiles' },
    ],
  },
  wxc: {
    key: 'wxc',
    title: 'Webex Calling',
    subtitle: 'Cloud calling — numbers, users, locations, queues, voicemail, recording',
    vendor: 'Webex',
    intent: 'Manage your Webex Calling org from one console — DIDs, users, locations, queues, voicemail, recording.',
    tiles: [
      { label: 'Users',           placeholder: '—' },
      { label: 'Phone numbers',   placeholder: '—' },
      { label: 'Locations',       placeholder: '—' },
      { label: 'Call queues',     placeholder: '—' },
    ],
    areas: [
      { key: 'numbers',   label: 'Numbers',        columns: ['Number', 'Location', 'Assigned to', 'Type', 'State', 'Actions'],
        primary: { label: '+ Add numbers', tone: 'webex' }, api_note: 'Backed by Webex Calling Numbers API' },
      { key: 'users',     label: 'Users',          columns: ['Name', 'Email', 'Location', 'License', 'Extension', 'Voicemail PIN', 'Actions'],
        primary: { label: '+ Add user', tone: 'webex' }, api_note: 'Backed by Webex Identity + Calling API' },
      { key: 'locations', label: 'Locations',      columns: ['Name', 'Country', 'Time zone', 'Emergency address', 'Numbers', 'Users'],
        primary: { label: '+ Add location', tone: 'webex' }, api_note: 'Backed by Webex Calling Locations API' },
      { key: 'queues',    label: 'Call queues',    columns: ['Queue', 'Extension', 'Agents', 'Music on hold', 'Overflow', 'Actions'],
        primary: { label: '+ Create queue', tone: 'webex' }, api_note: 'Backed by Webex Calling Call Queues API' },
      { key: 'aa',        label: 'Auto attendants', columns: ['Name', 'Number', 'Schedule', 'Menu', 'Last edited', 'Actions'],
        primary: { label: '+ Create AA', tone: 'webex' }, api_note: 'Backed by Webex Calling Auto Attendants API' },
      { key: 'vm',        label: 'Voicemail',      columns: ['User', 'Extension', 'Storage used', 'Forward to email', 'PIN', 'Actions'] },
      { key: 'rec',       label: 'Recording',      columns: ['User', 'Mode', 'Storage retention', 'Pause/resume', 'Last call', 'Actions'] },
    ],
  },
  wxcc: {
    key: 'wxcc',
    title: 'Webex Contact Center',
    subtitle: 'Flows · queues · skills · agents · routing · AI Agent',
    vendor: 'Webex',
    intent: 'Visual flow designer + live queue and agent state with one console for skills, routing, and historical reporting.',
    // KPI tiles show live data from the WxCC Configuration API. Flows/Skills
    // are surfaced via separate endpoints (Flow Designer API, Skill API) —
    // those are scheduled for Phase 2. For Phase 1 we show the four core
    // configuration entities that wxcc-svc proxies today.
    tiles: [
      { label: 'Queues',         placeholder: '—' },
      { label: 'Teams',          placeholder: '—' },
      { label: 'Agents online',  placeholder: '—' },
      { label: 'Sites',          placeholder: '—' },
    ],
    areas: [
      { key: 'flows',    label: 'Flows',           columns: ['Flow', 'Version', 'Entry points', 'Last published', 'Active calls', 'Actions'],
        primary: { label: '+ Create flow', tone: 'webex' }, api_note: 'Backed by Webex Contact Center Flow Designer API' },
      { key: 'queues',   label: 'Queues',          columns: ['Queue', 'Service level %', 'In queue', 'Longest wait', 'Skill', 'Actions'],
        primary: { label: '+ Create queue', tone: 'webex' }, api_note: 'Backed by Webex Contact Center Queues API' },
      { key: 'skills',   label: 'Skills',          columns: ['Skill', 'Type', 'Proficiency 1-10', 'Agents', 'Actions'],
        primary: { label: '+ Add skill', tone: 'webex' } },
      { key: 'agents',   label: 'Agents',          columns: ['Agent', 'Team', 'Current state', 'Skills', 'Active calls', 'Actions'],
        primary: { label: '+ Add agent', tone: 'webex' }, api_note: 'Backed by Webex Contact Center Agents API' },
      { key: 'routing',  label: 'Entry points',    columns: ['DNIS', 'Entry point', 'Flow', 'Time-of-day', 'Last call', 'Actions'],
        primary: { label: '+ Map DNIS', tone: 'webex' } },
      { key: 'ai',       label: 'AI Agent',        columns: ['Capability', 'Status', 'Model', 'Used in flows', 'Actions'],
        primary: { label: 'Configure AI Agent', tone: 'webex' }, api_note: 'Backed by Cisco AI Assistant for WxCC' },
      { key: 'reports',  label: 'Reports',         columns: ['Report', 'Cadence', 'Last run', 'Recipients', 'Format', 'Actions'] },
      { key: 'emcl',     label: 'EM/CL Flow',      columns: [],
        api_note: 'Emergency + Closure subgraph injector for any WxCC call flow' },
      { key: 'audio',    label: 'Audio',           columns: ['Name', 'Description', 'Used In Flow(s)', 'Size', 'Actions'],
        api_note: 'Live audio file inventory + bulk download / upload / transcribe' },
      { key: 'newflow',  label: 'Build New Flow from Template', columns: [],
        api_note: 'Generate a publish-ready WxCC flow from the master template + a filled config sheet — runs through freshener + validator automatically' },
      { key: 'desktoplayout', label: 'Desktop_layout', columns: [],
        api_note: 'Customize, role-scope and validate WxCC Agent Desktop layout JSON — supervisor-only pages, default resets, widget retargeting, team-assignment rules' },
      { key: 'cleanup', label: 'Clean Up Tools', columns: [],
        api_note: 'Dependency-ordered, dry-run-first bulk cleanup of WxCC config objects — exclusion filters, system-object protection, multi-pass, approval-gated' },
    ],
  },
  cucm: {
    key: 'cucm',
    title: 'Cisco Unified Communications Manager',
    subtitle: 'On-prem CUCM — phones, users, route patterns, dial plan',
    vendor: 'Cisco',
    intent: 'Hub-side console for CUCM clusters, with a migration path to Webex Calling when ready.',
    tiles: [
      { label: 'Phones',           placeholder: '—' },
      { label: 'End users',        placeholder: '—' },
      { label: 'Route patterns',   placeholder: '—' },
      { label: 'Device pools',     placeholder: '—' },
    ],
    areas: [
      { key: 'phones',  label: 'Phones',            columns: ['Device name', 'Model', 'Description', 'Status', 'Line 1', 'IP', 'Actions'],
        primary: { label: '+ Add phone', tone: 'cisco' }, api_note: 'Backed by CUCM AXL · getPhone / addPhone' },
      { key: 'users',   label: 'End users',         columns: ['User ID', 'First name', 'Last name', 'Mail ID', 'Lines', 'Status', 'Actions'],
        primary: { label: '+ Add end user', tone: 'cisco' }, api_note: 'Backed by CUCM AXL · listEndUser' },
      { key: 'lines',   label: 'Directory numbers', columns: ['Pattern', 'Partition', 'Description', 'Calling search space', 'Active devices', 'Actions'],
        primary: { label: '+ Add DN', tone: 'cisco' } },
      { key: 'routes',  label: 'Route patterns',    columns: ['Pattern', 'Partition', 'Route list / Gateway', 'CSS', 'Pre-dot', 'Actions'],
        primary: { label: '+ Add route pattern', tone: 'cisco' } },
      { key: 'trans',   label: 'Translation patterns', columns: ['Pattern', 'Partition', 'CSS', 'Called party transform', 'Actions'] },
      { key: 'pools',   label: 'Device pools',      columns: ['Name', 'Region', 'Date/Time group', 'CSS', 'Member phones', 'Actions'] },
      { key: 'css',     label: 'Partitions & CSS',  columns: ['Name', 'Type', 'Members', 'Last modified', 'Actions'] },
      { key: 'health',  label: 'Service health',    columns: ['Service', 'Node', 'Status', 'Activated', 'Last restart', 'Actions'] },
      { key: 'migration', label: 'Migration → WxC', columns: ['CUCM User ID', 'Name', 'Email', 'CUCM Extension', 'WxC Match', 'WxC Extension', 'Migration Status', 'Actions'],
        api_note: 'Read-only gap analysis — joins CUCM end_users with WxC people by email' },
    ],
  },
  uccx: {
    key: 'uccx',
    title: 'Cisco Unified Contact Center Express',
    subtitle: 'On-prem UCCX — applications, scripts, CSQs, agents, prompts',
    vendor: 'Cisco',
    intent: 'Operate your UCCX deployment and plan a guided migration to Webex Contact Center.',
    tiles: [
      { label: 'Applications', placeholder: '—' },
      { label: 'Scripts',      placeholder: '—' },
      { label: 'CSQs',         placeholder: '—' },
      { label: 'Resources',    placeholder: '—' },
    ],
    areas: [
      { key: 'apps',     label: 'Applications',    columns: ['Name', 'Type', 'Script', 'Triggers', 'Description', 'Actions'],
        primary: { label: '+ Add application', tone: 'cisco' }, api_note: 'Backed by UCCX REST API · Applications' },
      { key: 'scripts',  label: 'Scripts',         columns: ['Filename', 'Version', 'Modified', 'Used in', 'Size', 'Actions'],
        primary: { label: 'Upload script', tone: 'cisco' } },
      { key: 'csqs',     label: 'CSQs',            columns: ['Name', 'Resource selection', 'Skills', 'Agents', 'Wrap-up', 'Actions'],
        primary: { label: '+ Add CSQ', tone: 'cisco' } },
      { key: 'skills',   label: 'Skills',          columns: ['Skill', 'Competency 1-10', 'Resources', 'Last assigned', 'Actions'] },
      { key: 'agents',   label: 'Resources (agents)', columns: ['User ID', 'First name', 'Last name', 'Team', 'Skills', 'State', 'Actions'] },
      { key: 'prompts',  label: 'Prompts',         columns: ['Filename', 'Language', 'Type', 'Used in scripts', 'Size', 'Actions'] },
      { key: 'triggers', label: 'Triggers',        columns: ['DN', 'Application', 'Type', 'Session count', 'Actions'] },
      { key: 'reports',  label: 'Reporting',       columns: ['Report', 'Type', 'Cadence', 'Last run', 'Format', 'Actions'] },
      { key: 'migration', label: 'Migration → WxCC', columns: ['UCCX User ID', 'Name', 'Email', 'UCCX Skills', 'WxCC Match', 'WxCC Team', 'Migration Status', 'Actions'],
        api_note: 'Read-only gap analysis — joins UCCX agents with WxCC agents by email' },
    ],
  },
  // ── Local Gateway — every tab is wired to the WxC Hub (cloud / wxc-svc) ─
  // Cloud-only view. No spoke. Pairs with the CUBE module which renders the
  // same 8 areas from the on-prem device's perspective.
  lgw: {
    key: 'lgw',
    title: 'Local Gateway',
    subtitle: 'Webex Calling cloud view of your Local Gateways (premise-PSTN trunks)',
    vendor: 'Webex',
    intent: 'Operate the Webex Calling side of every Local Gateway — trunks, routes, dial plans, locations, certs — without touching the on-prem device.',
    tiles: [
      { label: 'Trunks',      placeholder: '—' },
      { label: 'Route lists', placeholder: '—' },
      { label: 'Dial plans',  placeholder: '—' },
      { label: 'Locations',   placeholder: '—' },
    ],
    areas: [
      { key: 'status',    label: 'Status',           columns: ['Name', 'Location', 'Trunk type', 'Outbound proxy', 'Max calls', 'In use', 'Status'],
        api_note: 'Live — Webex Control Hub premise-PSTN trunks (wxc-svc /api/wxc/local-gateways)' },
      { key: 'routes',    label: 'Route Lists',      columns: ['Route list', 'Location', 'Route group'],
        api_note: 'Live — Webex Calling premise-PSTN route lists (wxc-svc /api/wxc/route-lists)' },
      { key: 'dialplans', label: 'Dial Plans',       columns: ['Dial plan', 'Route', 'Route type'],
        api_note: 'Live — Webex Calling premise-PSTN dial plans (wxc-svc /api/wxc/dial-plans)' },
      { key: 'locations', label: 'Locations',        columns: ['Location', 'City', 'State', 'Country', 'Time zone'],
        api_note: 'Live — Webex Calling locations (wxc-svc /api/wxc/locations)' },
      { key: 'peers',     label: 'Dial peers',       columns: ['Tag', 'Leg', 'Direction', 'Destination pattern', 'Session target', 'Codec', 'State', 'Gateway'],
        api_note: 'Live — IOS-XE dial peers on the Local Gateway (shared with the CUBE module), classified by leg (Toward CUCM / Toward LGW-PSTN). Read-only here; edit in CUBE. Cloud trunk context shown below.' },
      { key: 'calls',     label: 'Active calls',     columns: ['Trunk', 'Location', 'Concurrent / Max', 'Active', 'Status'],
        api_note: 'Honest — Webex Calling exposes per-trunk concurrent-call capacity + in-use indicator only. Per-call detail lives on the device (CUBE module).' },
      { key: 'backup',    label: 'Backup config',    columns: [],
        api_note: 'Live — JSON snapshot of cloud LGW config (trunks + route lists + dial plans + locations) via wxc-svc /api/wxc/lgw-backup' },
      { key: 'certs',     label: 'TLS Certificates', columns: ['CN', 'Gateway', 'Issuer', 'Expires', 'Status', 'Actions'],
        api_note: 'Live — cert-monitor-svc inventory + per-LGW validate-match upload (lgw-svc /api/lgw/certs). Paired with Webex trunk outboundProxy hostname.' },
    ],
  },
  // ── CUBE — every tab is wired to the on-prem device via the cube-svc spoke
  // (lgw-svc / spoke-svc). Device truth. Pairs with the Local Gateway module
  // which renders the same 8 areas from the cloud perspective.
  cube: {
    key: 'cube',
    title: 'CUBE',
    subtitle: 'On-prem IOS-XE CUBE — device-level config and live telemetry via the spoke',
    vendor: 'Cisco',
    intent: 'Operate the on-box side of each Local Gateway — dial peers, active calls, running-config backup, on-box certs — sourced from the cube-svc spoke.',
    tiles: [
      { label: 'Gateways',      placeholder: '—' },
      { label: 'Dial peers',    placeholder: '—' },
      { label: 'SIP trunks',    placeholder: '—' },
      { label: 'Active calls',  placeholder: '—' },
    ],
    areas: [
      { key: 'status',    label: 'Status',           columns: ['Gateway', 'Host', 'Mode', 'Status', 'Last seen', 'IOS version', 'Last spoke sync'],
        api_note: 'Live — lgw-svc /api/lgw/gateways + /api/lgw/sync-status (cube-svc spoke heartbeat)' },
      { key: 'routes',    label: 'Route Lists',      columns: ['Pattern', 'Match type', 'Destination', 'State'],
        api_note: 'Empty — device routing config (the cube-svc spoke does not push route-list rows yet). Cloud Webex route lists live in the Local Gateway module.' },
      { key: 'dialplans', label: 'Dial Plans',       columns: ['Dial plan', 'Pattern', 'Route', 'State'],
        api_note: 'Empty — device dial-plan config (the cube-svc spoke does not push dial-plan rows yet). Cloud Webex dial plans live in the Local Gateway module.' },
      { key: 'locations', label: 'Locations',        columns: ['Location', 'Bound to', 'Source'],
        api_note: 'Locations are a Webex Calling concept — managed in Webex (Local Gateway → Locations). The CUBE device has no equivalent.' },
      { key: 'peers',     label: 'Dial peers',       columns: ['Tag', 'Leg', 'Direction', 'Destination pattern', 'Session target', 'Codec', 'State', 'Gateway', 'Actions'],
        primary: { label: '+ Add dial peer', tone: 'cisco' },
        api_note: 'Live — cube_dial_peers (cube-tunnel-svc reads IOS-XE dial-peer config via RESTCONF over the LB tunnel), classified by leg (Toward CUCM / Toward LGW-PSTN). Editable.' },
      { key: 'calls',     label: 'Active calls',     columns: ['Call ID', 'Direction', 'Calling', 'Called', 'Codec', 'Duration', 'Trunk', 'Gateway'],
        api_note: 'Live — cube_active_calls (cube-svc spoke scrapes show call active voice brief)' },
      { key: 'backup',    label: 'Backup config',    columns: [],
        api_note: 'Live — IOS-XE running-config + D1 device-config snapshot (lgw-svc /api/lgw/gateways/{id}/running-config + /api/lgw/backup-config)' },
      { key: 'certs',     label: 'TLS Certificates', columns: ['CN', 'Gateway', 'Issuer', 'Expires', 'Status', 'Actions'],
        api_note: 'Live — on-box cert inventory (D1 lgw_certs, populated by the cert upload flow). cert-monitor-svc tracks expiry.' },
    ],
  },
};

interface IntegrationsResponse {
  tenant_id: string;
  categories: Record<string, { label: string; fields: Array<{ name: string; configured: boolean }> }>;
}

// Module reachability classification — drives which UI banner shows up.
//
//   'cloud'  : hub → Webex API directly. No spoke. WxC, WxCC.
//   'onprem' : hub can't reach this; spoke container on customer LAN required.
//              CUCM (AXL / RisPort70), UCCX (REST + Informix) — both LAN-only
//              protocols, no cloud equivalent.
//   'hybrid' : LGW boundary is reachable via Webex Calling APIs (premises-based
//              connection, gateway endpoints, locations). DEVICE-level CUBE
//              internals (dial peers, codecs, active calls, IOS XE logs)
//              still need a spoke OR direct device access. CUBE/LGW lives
//              here per Ahmed's architecture note 2026-05-18.
type Reachability = 'cloud' | 'onprem' | 'hybrid';
const REACHABILITY: Record<ModuleKey, Reachability> = {
  // Security Monitor: a cloud worker reading Cloudflare events — cloud-reachable.
  security: 'cloud',
  // SIP Tools: Upload SIP Logs needs no connectivity; active tests ride the
  // CUBE/LGW spoke + Webex APIs — hybrid like CUBE/LGW.
  sip:  'hybrid',
  wxc:  'cloud',
  wxcc: 'cloud',
  cucm: 'onprem',
  uccx: 'onprem',
  // CUBE/LGW split: Local Gateway is pure cloud (Webex API); CUBE is the
  // on-prem device side (spoke required). Both render the same 8 areas from
  // their own data plane.
  lgw:  'cloud',
  cube: 'onprem',
};

// For onprem & hybrid modules: spoke health detection.
type SpokeHealth =
  | { state: 'unknown' }                                          // still loading
  | { state: 'absent' }                                            // API returned 0 spokes for tenant
  | { state: 'error'; message: string }                            // API itself failed (4xx/5xx/network) — NOT same as absent
  | { state: 'stale'; spoke: SpokeRow; minsAgo: number }           // present but heartbeat is old
  | { state: 'healthy'; spoke: SpokeRow };                         // present and heartbeating

const STALE_HEARTBEAT_MS = 5 * 60 * 1000; // 5 min without heartbeat = stale

// Some tenants reach their on-prem Cisco devices over the Long Beach
// Cloudflare Tunnel (cucm-tunnel-svc — AXL/RisPort/RESTCONF over `cucm-lb`)
// instead of a registered spoke container. For those tenants the spoke is
// genuinely absent yet live data flows fine, so the "spoke required" banner is
// wrong. We read connector (tunnel) health from radar-svc — the same
// connector_status table the tunnel-health checks write — to decide whether to
// suppress the spoke banner and show a tunnel banner instead.
type TunnelHealth =
  | { state: 'unknown' }                                  // still loading / not applicable
  | { state: 'absent' }                                   // no tunnel connector registered for tenant
  | { state: 'error'; message: string }                   // radarConnectors API itself failed
  | { state: 'up'; connector: ConnectorHealth }           // tunnel reachable for this module
  | { state: 'down'; connector: ConnectorHealth };        // connector exists but not reachable/stale

// Is the on-prem LB tunnel actually carrying THIS module's protocol?
//   cucm -> AXL/RisPort reachability flag; uccx -> UCCX REST reachability flag;
//   cube/lgw -> ride the same tunnel via RESTCONF, so an operational connector
//   that isn't stale counts as reachable.
function moduleTunnelReachable(moduleKey: ModuleKey, c: ConnectorHealth): boolean {
  if (c.stale) return false;
  if (moduleKey === 'cucm') return c.cucm_reachable;
  if (moduleKey === 'uccx') return c.uccx_reachable;
  return c.tier === 'operational' || c.status === 'operational';
}

// Sidebar sub-nav source: each module's areas (key + label) for the nested left menu.
export const MODULE_AREAS: Record<string, { key: string; label: string }[]> =
  Object.fromEntries(Object.entries(MODULES).map(([k, s]) => [k, s.areas.map((a) => ({ key: a.key, label: a.label }))]));

export default function CoreModule({ moduleKey, me }: { moduleKey: ModuleKey; me: Me }) {
  const spec = MODULES[moduleKey];
  const [integ, setInteg] = useState<IntegrationsResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Post-split: lgw and cube are now SEPARATE sidebar modules — no more
  // intra-module toggle. Every module's areas render straight from spec.
  const visibleAreas = spec.areas;
  // Active sub-area is driven by the URL (?area=) so the global sidebar's nested
  // sub-menu and this page stay in sync.
  const [searchParams] = useSearchParams();
  const areaParam = searchParams.get('area') || '';
  const activeArea = visibleAreas.some((a) => a.key === areaParam) ? areaParam : (visibleAreas[0]?.key ?? '');
  // Real WxC live data — populated when moduleKey === 'wxc' and creds exist
  const [liveStats, setLiveStats] = useState<Record<string, number | string> | null>(null);
  const [liveError, setLiveError] = useState<string | null>(null);
  // Super-admin tenant picker (Webex Calling page). A GLOBAL super-admin has no
  // real tenant of their own, so wxc-svc returns configured:false until a real
  // tenant is supplied via ?tenant=. The picker lets them type one; the value
  // is persisted so it survives navigation. For non-super-admins this stays at
  // their own tenant and is never sent (the service scopes by session).
  const [adminTenant, setAdminTenant] = useState<string>(() => {
    const stored = localStorage.getItem('tnh_admin_tenant');
    if (stored != null) return stored;
    return me.tenant_id !== 'GLOBAL' ? me.tenant_id : '';
  });
  useEffect(() => { localStorage.setItem('tnh_admin_tenant', adminTenant); }, [adminTenant]);
  // The tenant string actually threaded into api.wxc* calls — only super-admins
  // override scope; everyone else passes undefined and is scoped by session.
  const wxcTenantArg = me.role === 'super_admin' ? adminTenant : undefined;
  // Spoke health — only meaningful for on-prem (Cisco) modules
  const [spokeHealth, setSpokeHealth] = useState<SpokeHealth>({ state: 'unknown' });
  // Tunnel (LB) health — alternative transport for on-prem modules
  const [tunnelHealth, setTunnelHealth] = useState<TunnelHealth>({ state: 'unknown' });

  // Reset active sub-tab when navigating between modules
  useEffect(() => {
    setLiveStats(null); setLiveError(null); setSpokeHealth({ state: 'unknown' }); setTunnelHealth({ state: 'unknown' });
  }, [moduleKey]);

  // For onprem (CUCM/UCCX) and hybrid (CUBE/LGW) modules: detect whether a
  // spoke container is registered and heartbeating for this tenant.
  //   - CUCM/UCCX: a spoke is REQUIRED — those devices are LAN-only.
  //   - CUBE/LGW: a spoke is OPTIONAL — LGW boundary is reachable via WxC,
  //     but device-level CUBE features need a spoke (or direct IOS XE access).
  // Distinguishing 'absent' (API said 0 spokes) from 'error' (API itself
  // failed) is important: if the user sees "no spoke registered" but a spoke
  // IS actually in D1, that's a different bug than a real missing spoke.
  // Call without tenant_id — backend forces tenant filtering via session.ten
  // for tenant_admin anyway; matching the pattern Spokes.tsx uses avoids
  // surprises like one path getting filtered differently than another.
  useEffect(() => {
    if (REACHABILITY[moduleKey] === 'cloud') return;
    api.spokesList()
      .then((r) => {
        const spokes = r.spokes ?? [];
        // eslint-disable-next-line no-console
        console.debug('[SpokeStatusBanner]', moduleKey, 'spokesList result:', spokes.length, 'spokes');
        if (spokes.length === 0) { setSpokeHealth({ state: 'absent' }); return; }
        // Pick the healthiest spoke (most recent heartbeat)
        const sorted = [...spokes].sort((a, b) => (b.last_heartbeat_at ?? 0) - (a.last_heartbeat_at ?? 0));
        const spoke = sorted[0]!;
        const hb = spoke.last_heartbeat_at;
        if (!hb || (Date.now() - hb) > STALE_HEARTBEAT_MS) {
          setSpokeHealth({ state: 'stale', spoke, minsAgo: hb ? Math.floor((Date.now() - hb) / 60000) : -1 });
        } else {
          setSpokeHealth({ state: 'healthy', spoke });
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('[SpokeStatusBanner] spokesList failed:', e);
        setSpokeHealth({ state: 'error', message: e?.message ?? 'spokesList API failed' });
      });
  }, [moduleKey, me.tenant_id]);

  // For onprem/hybrid modules: detect whether the tenant reaches its devices
  // over the Long Beach Cloudflare Tunnel (connector_status via radar-svc).
  // When the tunnel is up, the spoke-absent banner is suppressed in favour of a
  // tunnel-connected banner — tunnel tenants don't run a spoke at all.
  useEffect(() => {
    if (REACHABILITY[moduleKey] === 'cloud') return;
    api.radarConnectors()
      .then((r) => {
        const conns = r.connectors ?? [];
        if (conns.length === 0) { setTunnelHealth({ state: 'absent' }); return; }
        // Prefer a connector that actually carries this module's protocol;
        // otherwise fall back to the most recently checked one.
        const sorted = [...conns].sort((a, b) => (b.last_checked_at ?? 0) - (a.last_checked_at ?? 0));
        const reachableConn = sorted.find((c) => moduleTunnelReachable(moduleKey, c));
        const conn = reachableConn ?? sorted[0]!;
        setTunnelHealth(reachableConn ? { state: 'up', connector: conn } : { state: 'down', connector: conn });
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('[SpokeStatusBanner] radarConnectors failed:', e);
        setTunnelHealth({ state: 'error', message: e?.message ?? 'radarConnectors API failed' });
      });
  }, [moduleKey, me.tenant_id]);

  useEffect(() => {
    api.configList()
      .then((r) => setInteg(r as IntegrationsResponse))
      .catch((e) => setErr(e?.message ?? 'Could not load module configuration.'));
  }, []);

  // For WxC: call the real live endpoint to populate stat tiles.
  // IMPORTANT: must use the api.* client (absolute URL to api.trinethub.com).
  // A bare `fetch('/api/wxc/')` hits the SPA host, which falls back to
  // index.html — then r.json() chokes on `<!DOCTYPE` → "JSON.parse:
  // unexpected character at line 1 column 1".
  useEffect(() => {
    if (moduleKey !== 'wxc') return;
    setLiveStats(null); setLiveError(null);
    api.wxcStatus(wxcTenantArg)
      .then((d) => {
        if (d.configured === false) {
          setLiveError(d.message || 'Configure your Webex Service App credentials in Configuration Manager.');
          return;
        }
        if (d.stats) setLiveStats({
          'Users':         d.stats.users_total,
          'Phone numbers': d.stats.numbers_total,
          'Locations':     d.stats.locations_total,
          'Call queues':   d.stats.queues_total,
        });
      })
      .catch((e) => setLiveError(e?.message ?? 'WxC live fetch failed.'));
  }, [moduleKey, wxcTenantArg]);

  // For WxCC: same pattern as WxC. wxcc-svc was a stub before Phase 1; once it
  // ships (and tenant_integrations[category='wxcc'] is populated), the four
  // KPI tiles light up. Until then, the soft "Configure credentials" banner
  // appears — no error, no dead shell.
  //
  // Surface per-endpoint errors too. wxcc-svc wraps each parallel fetch in a
  // catch, so a scope/403 on the Webex Contact Center API ends up in d.errors
  // not as a throw. Without surfacing that, the tiles show "0 — Live from
  // Webex API" which is misleading. We show the first non-null upstream error
  // as the page-level banner instead.
  useEffect(() => {
    if (moduleKey !== 'wxcc') return;
    api.wxccStatus()
      .then((d) => {
        if (d.configured === false) {
          setLiveError(d.message || 'Configure your Webex Contact Center Service App credentials in Configuration Manager.');
          return;
        }
        if (d.errors) {
          const firstErr = Object.entries(d.errors).find(([, v]) => v != null);
          if (firstErr) {
            const [endpoint, msg] = firstErr;
            setLiveError(`Webex Contact Center API rejected the ${endpoint} call: ${msg}. ` +
              `If this says 'FAIL_VALIDATE_TOKEN', the WxCCServiceApp refresh token rotated/expired - store a fresh ` +
              `refresh_token in Config Mgr (category 'wxcc'); wxcc-svc now self-renews it afterwards. Do NOT add cjp:* ` +
              `scopes to the TriNetHub Integration - that breaks the Search/DAL token (see KB TNH-KB-0068).`);
          }
        }
        if (d.stats) setLiveStats({
          'Queues':        d.stats.queues_total,
          'Teams':         d.stats.teams_total,
          'Agents online': d.stats.agents_total,
          'Sites':         d.stats.sites_total,
        });
      })
      .catch((e) => setLiveError(e?.message ?? 'WxCC live fetch failed.'));
  }, [moduleKey]);

  // For CUCM: live KPI counts from cucm-tunnel-svc (AXL COUNT(*) over the LB
  // tunnel). Same honest pattern as WxC — soft "configure" banner when AXL
  // creds are absent, and an explicit error (never a fake 0) on an AXL fault.
  useEffect(() => {
    if (moduleKey !== 'cucm') return;
    setLiveStats(null); setLiveError(null);
    api.cucmStats(me.tenant_id !== 'GLOBAL' ? me.tenant_id : undefined)
      .then((d) => {
        if (d.configured === false) { setLiveError(d.message || 'Configure CUCM (AXL) credentials in Configuration Manager.'); return; }
        if (d.error) { setLiveError(`CUCM AXL error: ${d.error}. Check the Long Beach tunnel (cucm-lb) and AXL account.`); return; }
        if (d.stats) setLiveStats({
          'Phones':         d.stats.phones_total,
          'End users':      d.stats.end_users_total,
          'Route patterns': d.stats.route_patterns_total,
          'Device pools':   d.stats.device_pools_total,
        });
      })
      .catch((e) => setLiveError(e?.message ?? 'CUCM live fetch failed.'));
  }, [moduleKey]);

  // For CUBE/LGW: KPI tiles are live from lgw-svc — gateway count, dial-peer
  // count, registered SIP trunks, current active calls. All four queries are
  // cheap D1 reads; a failure leaves the tiles on their placeholder rather
  // than blocking the page.
  useEffect(() => {
    if (moduleKey !== 'cube') return;
    Promise.all([
      api.lgwGateways().catch(() => ({ gateways: [], total: 0 })),
      api.lgwDialPeers().catch(() => ({ dial_peers: [], total: 0 })),
      api.lgwSipTrunks().catch(() => ({ sip_trunks: [], total: 0 })),
      api.lgwActiveCalls().catch(() => ({ active_calls: [], total: 0 })),
      api.lgwCerts().catch(() => ({ certs: [], total: 0 })),
      api.wxcLocalGateways().catch(() => ({ local_gateways: [], total: 0 })),
    ]).then(([g, dp, st, ac, ct, hub]) => {
      const trunksUp = (st.sip_trunks ?? []).filter((t) => /registered|ok|up/i.test(t.registration_state)).length;
      const certs = ct.certs ?? [];
      const expiring = certs.filter((c) => (c.days_until_expiry ?? 999) > 0 && (c.days_until_expiry ?? 999) <= 30).length;
      const expired  = certs.filter((c) => (c.days_until_expiry ?? 1) <= 0).length;
      // liveStats holds keys for BOTH groups' tiles — the render picks the right subset.
      setLiveStats({
        'Gateways':       g.total ?? 0,
        'Local Gateways': hub.total ?? 0,
        'Dial peers':    dp.total ?? 0,
        'SIP trunks up': trunksUp,
        'Active calls':  ac.total ?? 0,
        'Certificates':  ct.total ?? 0,
        'Expiring ≤30d': expiring,
        'Expired':       expired,
      });
    }).catch((e) => setLiveError(e?.message ?? 'CUBE/LGW live fetch failed.'));
  }, [moduleKey]);

  // For LGW (Local Gateway cloud view): the four tiles are live counts from the
  // Webex Calling cloud (wxc-svc) — Trunks / Route lists / Dial plans / Locations.
  // LGW reuses the WxC Service App creds (no config category of its own), so its
  // connection state is derived from the `wxc` category (see altCat below).
  useEffect(() => {
    if (moduleKey !== 'lgw') return;
    setLiveStats(null); setLiveError(null);
    Promise.all([
      api.wxcLocalGateways(wxcTenantArg).catch(() => ({ local_gateways: [], total: 0 })),
      api.wxcRouteLists(wxcTenantArg).catch(() => ({ route_lists: [], total: 0 })),
      api.wxcDialPlans(wxcTenantArg).catch(() => ({ dial_plans: [], total: 0 })),
      api.wxcLocations(wxcTenantArg).catch(() => ({ locations: [], total: 0 })),
    ]).then(([gw, rl, dp, loc]) => {
      setLiveStats({
        'Trunks':      (gw as any).total ?? ((gw as any).local_gateways?.length ?? 0),
        'Route lists': (rl as any).total ?? ((rl as any).route_lists?.length ?? 0),
        'Dial plans':  (dp as any).total ?? ((dp as any).dial_plans?.length ?? 0),
        'Locations':   (loc as any).total ?? ((loc as any).locations?.length ?? 0),
      });
    }).catch((e) => setLiveError(e?.message ?? 'LGW live fetch failed.'));
  }, [moduleKey, wxcTenantArg]);

  // LGW has no credentials of its own — it reuses the WxC Service App creds, so
  // its connection state is the `wxc` category's (4/4). The vestigial `lgw` SSH
  // category (host/ssh_user/ssh_key_pem) is unused in cloud mode and would
  // otherwise drag the pill to a half-configured "Setup 4/7".
  const cat = spec.key === 'lgw' ? integ?.categories?.wxc : integ?.categories?.[spec.key];
  const altCat = spec.key === 'cube' ? integ?.categories?.lgw : null;
  const fields = [...(cat?.fields ?? []), ...(altCat?.fields ?? [])];
  const configuredCount = fields.filter((f) => f.configured).length;
  const totalFields = fields.length;
  const state: 'unknown' | 'not_configured' | 'partial' | 'connected' =
    integ === null ? 'unknown'
    : totalFields === 0 || configuredCount === 0 ? 'not_configured'
    : configuredCount < totalFields ? 'partial'
    : 'connected';

  const area = visibleAreas.find((a) => a.key === activeArea) ?? visibleAreas[0] ?? spec.areas[0];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
            <span>{spec.vendor}</span>
            <span>·</span>
            <span>{me.tenant_id}</span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">{spec.title}</h1>
          <p className="text-gray-500 text-sm mt-1 max-w-3xl">{spec.subtitle}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {moduleKey === 'wxc' && me.role === 'super_admin' && (
            <div className="flex gap-2 items-center text-sm">
              <span className="text-gray-500">Tenant:</span>
              <input value={adminTenant} onChange={(e) => setAdminTenant(e.target.value)}
                     placeholder="tenant id"
                     className="px-3 py-1.5 text-sm rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent w-40 font-mono" />
            </div>
          )}
          <ConnectionPill state={state} configured={configuredCount} total={totalFields} />
        </div>
      </div>

      {/* Stat tiles — hidden on full-page areas (e.g. Clean Up Tools) so the tool gets the whole page. */}
      {activeArea !== 'cleanup' && (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {spec.tiles.map((t) => {
          const liveValue = liveStats?.[t.label];
          const hasLive = liveValue !== undefined && liveValue !== null;
          const tileSource =
            moduleKey === 'cube' ? 'Live from cube-tunnel-svc (RESTCONF)'
            : moduleKey === 'cucm' ? 'Live from CUCM AXL (tunnel)'
            : moduleKey === 'lgw'  ? 'Live from Webex Calling cloud (wxc-svc)'
            : 'Live from Webex API';
          const tileEmpty =
            moduleKey === 'cube' ? 'No device data yet'
            : moduleKey === 'cucm' ? 'No CUCM data yet'
            : moduleKey === 'lgw'  ? 'No Webex Calling data yet'
            : (state === 'connected' ? 'Awaiting first sync' : 'Not connected');
          return (
            <div key={t.label} className="card p-4">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{t.label}</div>
              <div className={`text-3xl font-semibold tracking-tight mt-1 ${hasLive ? 'text-gray-900 dark:text-gray-100' : 'text-gray-300 dark:text-gray-600'}`}>
                {hasLive ? String(liveValue) : t.placeholder}
              </div>
              <div className="text-[11px] text-gray-400 mt-1">
                {hasLive ? tileSource : tileEmpty}
              </div>
            </div>
          );
        })}
      </div>
      )}
      {liveError && (
        <div className="card p-3 border-amber-300 bg-amber-50 dark:bg-amber-900/15 text-sm text-amber-800 dark:text-amber-200">
          {liveError}
        </div>
      )}

      {moduleKey === 'wxc' && <WebexInventoryCard />}

      {moduleKey === 'lgw' && <LgwMergedCard />}

      {/* Spoke readiness banner — only rendered for onprem/hybrid modules.
          Shows above the sub-nav so the user sees the readiness state before
          clicking into individual sub-tabs. */}
      <SpokeStatusBanner
        reachability={REACHABILITY[moduleKey]}
        health={spokeHealth}
        tunnel={tunnelHealth}
        moduleTitle={spec.title}
        tenantId={me.tenant_id}
      />

      {/* CTA when not connected — suppressed on the Local Gateway (lgw) module
          because it shares the Webex Calling Service App credentials (configured
          under the `wxc` category) — it has no integration category of its own,
          so the configuration-fields check is meaningless here. */}
      {state === 'not_configured' && moduleKey !== 'lgw' && (
        <div className="card p-4 border-amber-300 bg-amber-50 dark:bg-amber-900/15">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="font-medium text-amber-800 dark:text-amber-200">No {spec.title} credentials yet.</div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                Add hostname, admin credentials, and integration tokens in the Configuration Manager.
              </p>
            </div>
            <Link to="/configuration" className="btn-primary text-sm whitespace-nowrap">Configure connection →</Link>
          </div>
        </div>
      )}
      {state === 'partial' && moduleKey !== 'lgw' && (
        <div className="card p-3 border-amber-300 bg-amber-50/60 dark:bg-amber-900/10 text-sm">
          <span className="font-medium text-amber-800 dark:text-amber-200">{configuredCount} of {totalFields} fields filled.</span>{' '}
          <Link to="/configuration" className="text-tnh-accent hover:underline ml-1">Finish setup →</Link>
        </div>
      )}

      {/* Post-split data-plane caption — confirms which side the user is on. */}
      {(moduleKey === 'lgw' || moduleKey === 'cube') && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
            moduleKey === 'lgw'
              ? 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200'
              : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${moduleKey === 'lgw' ? 'bg-violet-500' : 'bg-amber-500'}`} />
            {moduleKey === 'lgw' ? 'CLOUD · Webex Calling' : 'DEVICE · cube-svc spoke (SSH)'}
          </span>
          <span className="text-xs text-gray-500">
            {moduleKey === 'lgw'
              ? 'Every tab here reads from the Webex Calling cloud API. The on-prem device view lives in the CUBE module.'
              : 'Every tab here reads from the on-prem CUBE via the cube-svc spoke. The Webex Calling cloud view lives in the Local Gateway module.'}
          </span>
        </div>
      )}

      {/* Active area content — full width (sub-nav now lives in the global sidebar) */}
      <div className="space-y-3">

      {/* Active feature area — wired endpoints render real data, everything
          else falls back to the descriptive skeleton table. */}
      {moduleKey === 'security' && ['monitor','offenders','sessions','posture','audit'].includes(area.key) ? (
        <SecurityMonitorPanel area={area.key} />
      ) : moduleKey === 'sip' && ['upload','single','signaling','bulk'].includes(area.key) ? (
        <SipToolsPanel areaKey={area.key} />
      ) : moduleKey === 'wxc' && state === 'connected' && ['numbers','users','locations','queues'].includes(area.key) ? (
        <WxcDataPanel spec={spec} area={area} state={state} areaKey={area.key as 'numbers'|'users'|'locations'|'queues'} tenant={wxcTenantArg} />
      ) : moduleKey === 'wxcc' && state === 'connected' && area.key === 'flows' ? (
        <WxccFlowsPanel />
      ) : moduleKey === 'wxcc' && state === 'connected' && ['queues','agents','skills','routing'].includes(area.key) ? (
        <WxccDataPanel spec={spec} area={area} state={state} areaKey={area.key as WxccAreaKey} />
      ) : moduleKey === 'wxcc' && area.key === 'emcl' ? (
        <WxccEmClFlowPanel />
      ) : moduleKey === 'wxcc' && area.key === 'audio' ? (
        <WxccAudioPanel />
      ) : moduleKey === 'wxcc' && area.key === 'newflow' ? (
        <WxccNewFlowPanel />
      ) : moduleKey === 'wxcc' && area.key === 'desktoplayout' ? (
        <WxccDesktopLayoutPanel />
      ) : moduleKey === 'wxcc' && area.key === 'cleanup' ? (
        <WxccCleanupPanel />
      ) : moduleKey === 'cucm' && ['phones','users','lines','routes','pools'].includes(area.key) ? (
        <CucmEntityPanel area={area} tenantId={me.tenant_id} areaKey={area.key as CucmAreaKey} />
      ) : moduleKey === 'cucm' && area.key === 'migration' ? (
        <CucmMigrationPanel area={area} tenantId={me.tenant_id} />
      ) : moduleKey === 'uccx' && area.key === 'migration' ? (
        <UccxMigrationPanel area={area} tenantId={me.tenant_id} />
      /* ── Local Gateway module (cloud / wxc-svc) ── */
      ) : moduleKey === 'lgw' && area.key === 'status' ? (
        <LgwHubPanel />
      ) : moduleKey === 'lgw' && area.key === 'routes' ? (
        <LgwWebexPanel areaKey="routes" />
      ) : moduleKey === 'lgw' && area.key === 'dialplans' ? (
        <LgwWebexPanel areaKey="dialplans" />
      ) : moduleKey === 'lgw' && area.key === 'locations' ? (
        <LgwWebexPanel areaKey="locations" />
      ) : moduleKey === 'lgw' && area.key === 'peers' ? (
        <LgwCloudDialPeersPanel />
      ) : moduleKey === 'lgw' && area.key === 'calls' ? (
        <LgwCloudActiveCallsPanel />
      ) : moduleKey === 'lgw' && area.key === 'backup' ? (
        <LgwCloudBackupPanel />
      ) : moduleKey === 'lgw' && area.key === 'certs' ? (
        <LgwCertPanel />
      /* ── CUBE module (device / spoke / lgw-svc) ── */
      ) : moduleKey === 'cube' && area.key === 'status' ? (
        <CubeStatusPanel />
      ) : moduleKey === 'cube' && (area.key === 'routes' || area.key === 'dialplans' || area.key === 'locations') ? (
        <CubeManagedInWebexPanel which={area.key as 'routes' | 'dialplans' | 'locations'} />
      ) : moduleKey === 'cube' && area.key === 'peers' ? (
        <LgwTelemetryPanel areaKey="peers" />
      ) : moduleKey === 'cube' && area.key === 'calls' ? (
        <LgwTelemetryPanel areaKey="calls" />
      ) : moduleKey === 'cube' && area.key === 'backup' ? (
        <CubeBackupPanel />
      ) : moduleKey === 'cube' && area.key === 'certs' ? (
        <CubeDeviceCertsPanel />
      ) : (
        <AreaPanel spec={spec} area={area} state={state} />
      )}

      {err && <div className="card p-3 text-sm text-tnh-err">{err}</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WxC live data panel — renders the four real endpoints in the wxc-svc:
//   numbers / users / locations / queues
// Each fetch happens on activeArea change; results are cached per-area in a
// component-local map so flipping back and forth between tabs is instant.
// ---------------------------------------------------------------------------
type WxcAreaKey = 'numbers' | 'users' | 'locations' | 'queues';
type WxcRow = WxcNumber | WxcUser | WxcLocation | WxcQueue;

function WxcDataPanel({ spec, area, state, areaKey, tenant }: { spec: ModuleSpec; area: FeatureArea; state: string; areaKey: WxcAreaKey; tenant?: string }) {
  const [rows, setRows] = useState<WxcRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const isConnected = state === 'connected';
  const primaryToneClass = area.primary?.tone === 'webex' ? 'bg-violet-600 hover:bg-violet-500' : 'bg-indigo-600 hover:bg-indigo-500';

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setRows(null);
    const fetcher = areaKey === 'numbers'   ? api.wxcNumbers(undefined, tenant)
                  : areaKey === 'users'     ? api.wxcUsers(undefined, tenant)
                  : areaKey === 'locations' ? api.wxcLocations(tenant)
                  :                            api.wxcQueues(tenant);
    fetcher
      .then((d) => {
        if (cancelled) return;
        const list: WxcRow[] = (areaKey === 'numbers' ? (d as { numbers: WxcNumber[] }).numbers
                              : areaKey === 'users'   ? (d as { users: WxcUser[] }).users
                              : areaKey === 'locations' ? (d as { locations: WxcLocation[] }).locations
                              : (d as { queues: WxcQueue[] }).queues) ?? [];
        setRows(list);
        setLoading(false);
      })
      .catch((e) => { if (!cancelled) { setError(e?.message ?? 'Fetch failed'); setLoading(false); }});
    return () => { cancelled = true; };
  }, [areaKey, tenant]);

  const filteredRows = (rows ?? []).filter((r) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return JSON.stringify(r).toLowerCase().includes(q);
  });

  return (
    <div className="space-y-3">
      {/* Action bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={`Search ${area.label.toLowerCase()}…`}
            className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm w-64"
          />
          <button
            onClick={() => exportCsv(area.columns, filteredRows, areaKey, spec.title)}
            disabled={!filteredRows.length}
            className="btn-ghost text-sm disabled:opacity-50"
            title="Export current filtered view to CSV"
          >Export CSV</button>
          <span className="text-xs text-gray-500">
            {loading ? 'Loading…' : `${filteredRows.length}${filter ? ` of ${rows?.length ?? 0}` : ''} ${area.label.toLowerCase()}`}
          </span>
        </div>
        {area.primary && (
          <button
            disabled={!isConnected}
            className={`text-white text-sm font-semibold px-4 py-1.5 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed ${primaryToneClass}`}
            title="Read-only — write operations land in the next iteration"
          >
            {area.primary.label}
          </button>
        )}
      </div>

      {/* Data table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
              <tr>
                {area.columns.map((c) => (
                  <th key={c} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={area.columns.length} className="px-4 py-12 text-center text-sm text-gray-500">Fetching {area.label.toLowerCase()} from Webex API…</td></tr>
              )}
              {!loading && error && (
                <tr><td colSpan={area.columns.length} className="px-4 py-8 text-center text-sm text-rose-600 dark:text-rose-400">{error}</td></tr>
              )}
              {!loading && !error && filteredRows.length === 0 && (
                <tr><td colSpan={area.columns.length} className="px-4 py-12 text-center text-sm text-gray-500">
                  No {area.label.toLowerCase()} {filter ? 'match the filter.' : `configured for this Webex org yet.`}
                </td></tr>
              )}
              {!loading && !error && filteredRows.map((row, i) => (
                <tr key={i} className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/30">
                  {renderWxcRow(areaKey, row).map((cell, j) => (
                    <td key={j} className="px-4 py-2.5 text-sm whitespace-nowrap">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {area.api_note && (
          <div className="px-4 py-2 border-t border-tnh-border dark:border-tnh-border-dark text-[11px] text-gray-500 bg-gray-50 dark:bg-gray-900/40">{area.api_note}</div>
        )}
      </div>
    </div>
  );
}

// Map one row from each WxC endpoint onto the column array defined in MODULES.wxc.areas.
function renderWxcRow(areaKey: WxcAreaKey, row: WxcRow): ReactNode[] {
  if (areaKey === 'numbers') {
    const n = row as WxcNumber;
    const owner = n.owner ? (n.owner.displayName || `${n.owner.firstName ?? ''} ${n.owner.lastName ?? ''}`.trim() || n.owner.type) : '—';
    return [
      n.phoneNumber || n.extension || '—',
      n.location?.name ?? '—',
      owner,
      n.phoneNumberType ?? (n.mainNumber ? 'Main' : '—'),
      <span key="s" className={`pill ${n.state === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'} text-[11px] px-2 py-0.5 rounded-full`}>{n.state ?? '—'}</span>,
      <button key="a" className="text-tnh-accent text-xs hover:underline" title="Detail view ships next">Details →</button>,
    ];
  }
  if (areaKey === 'users') {
    const u = row as WxcUser;
    const name = u.displayName || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || '—';
    const email = (u.emails && u.emails[0]) || u.userName || '—';
    const license = (u.licenses && u.licenses.length) ? `${u.licenses.length} license${u.licenses.length === 1 ? '' : 's'}` : '—';
    return [
      name,
      email,
      u.locationId ?? '—',
      license,
      u.extension ?? '—',
      '••••',  // VM PIN never exposed
      <button key="a" className="text-tnh-accent text-xs hover:underline" title="Detail view ships next">Details →</button>,
    ];
  }
  if (areaKey === 'locations') {
    const l = row as WxcLocation;
    const city = l.address?.city ?? '';
    const state = l.address?.state ?? '';
    const country = l.address?.country ?? '—';
    const addrSummary = l.address?.address1 ? `${l.address.address1}${city ? `, ${city}` : ''}${state ? `, ${state}` : ''}` : '—';
    return [
      l.name,
      country,
      l.timeZone ?? '—',
      addrSummary,
      '—',  // Numbers count not on Location row from Webex API
      '—',  // Users count not on Location row from Webex API
    ];
  }
  // queues
  const q = row as WxcQueue;
  return [
    q.name,
    q.extension ?? q.phoneNumber ?? '—',
    '—',  // Agents count needs separate /v1/telephony/config/callQueues/{id}/agents call
    '—',  // MOH detail ships Phase 2
    '—',  // Overflow detail ships Phase 2
    <button key="a" className="text-tnh-accent text-xs hover:underline" title="Detail view ships next">Details →</button>,
  ];
}

// Lightweight client-side CSV export — the rendered text per column.
function exportCsv(columns: string[], rows: WxcRow[], areaKey: WxcAreaKey, title: string) {
  const escape = (v: any) => {
    const s = v == null ? '' : String(typeof v === 'object' ? JSON.stringify(v) : v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const dataRows = rows.map((r) => renderWxcRow(areaKey, r).map((cell) => {
    if (typeof cell === 'object' && cell != null && (cell as any).props && (cell as any).props.children) {
      return (cell as any).props.children;
    }
    return cell;
  }));
  const csv = [columns.map(escape).join(','), ...dataRows.map((r) => r.map(escape).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title.replace(/\s+/g, '_')}_${areaKey}_${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// WxCC live data panel — Phase 1 wires queues + agents. Flows, AI Agent,
// routing, reports stay on the descriptive skeleton until their respective
// WxCC APIs are wired (Phase 2). Same operational shape as WxcDataPanel so
// the user experience is identical across modules.
// ---------------------------------------------------------------------------
type WxccAreaKey = 'queues' | 'agents' | 'skills' | 'routing';
type WxccRow = WxccQueue | WxccAgent | WxccSkill | WxccEntryPoint;

function WxccDataPanel({ spec, area, state, areaKey }: { spec: ModuleSpec; area: FeatureArea; state: string; areaKey: WxccAreaKey }) {
  const [rows, setRows] = useState<WxccRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const isConnected = state === 'connected';
  const primaryToneClass = area.primary?.tone === 'webex' ? 'bg-violet-600 hover:bg-violet-500' : 'bg-indigo-600 hover:bg-indigo-500';

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setRows(null);
    const fetcher =
      areaKey === 'queues'  ? api.wxccQueues()
    : areaKey === 'agents'  ? api.wxccAgents()
    : areaKey === 'skills'  ? api.wxccSkills()
    :                          api.wxccEntryPoints();  // 'routing' tab → entry-points
    fetcher
      .then((d) => {
        if (cancelled) return;
        const list: WxccRow[] =
          (areaKey === 'queues'  ? (d as { queues:       WxccQueue[]      }).queues
         : areaKey === 'agents'  ? (d as { agents:       WxccAgent[]      }).agents
         : areaKey === 'skills'  ? (d as { skills:       WxccSkill[]      }).skills
         :                          (d as { entry_points: WxccEntryPoint[] }).entry_points) ?? [];
        setRows(list);
        setLoading(false);
      })
      .catch((e) => { if (!cancelled) { setError(e?.message ?? 'Fetch failed'); setLoading(false); }});
    return () => { cancelled = true; };
  }, [areaKey]);

  const filteredRows = (rows ?? []).filter((r) => !filter || JSON.stringify(r).toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <input value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder={`Search ${area.label.toLowerCase()}…`}
            className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm w-64" />
          <span className="text-xs text-gray-500">
            {loading ? 'Loading…' : `${filteredRows.length}${filter ? ` of ${rows?.length ?? 0}` : ''} ${area.label.toLowerCase()}`}
          </span>
        </div>
        {area.primary && (
          <button disabled={!isConnected}
            className={`text-white text-sm font-semibold px-4 py-1.5 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed ${primaryToneClass}`}
            title="Read-only — write operations land in the next iteration">{area.primary.label}</button>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
              <tr>{area.columns.map((c) => (
                <th key={c} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{c}</th>
              ))}</tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={area.columns.length} className="px-4 py-12 text-center text-sm text-gray-500">Fetching {area.label.toLowerCase()} from Webex Contact Center API…</td></tr>)}
              {!loading && error && (<tr><td colSpan={area.columns.length} className="px-4 py-8 text-center text-sm text-rose-600 dark:text-rose-400">{error}</td></tr>)}
              {!loading && !error && filteredRows.length === 0 && (<tr><td colSpan={area.columns.length} className="px-4 py-12 text-center text-sm text-gray-500">No {area.label.toLowerCase()} {filter ? 'match the filter.' : 'in this WxCC tenant yet.'}</td></tr>)}
              {!loading && !error && filteredRows.map((row, i) => (
                <tr key={i} className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/30">
                  {renderWxccRow(areaKey, row).map((cell, j) => (
                    <td key={j} className="px-4 py-2.5 text-sm whitespace-nowrap">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {area.api_note && (
          <div className="px-4 py-2 border-t border-tnh-border dark:border-tnh-border-dark text-[11px] text-gray-500 bg-gray-50 dark:bg-gray-900/40">{area.api_note}</div>
        )}
      </div>
    </div>
  );
}

function renderWxccRow(areaKey: WxccAreaKey, row: WxccRow): ReactNode[] {
  if (areaKey === 'queues') {
    const q = row as WxccQueue;
    return [
      q.name ?? '—',
      '—',  // Service level % — needs analytics RT API
      '—',  // In queue (live) — needs RT API
      '—',  // Longest wait (live) — needs RT API
      q.channelType ?? '—',
      <button key="a" className="text-tnh-accent text-xs hover:underline" title="Detail view ships next">Details →</button>,
    ];
  }
  if (areaKey === 'agents') {
    const a = row as WxccAgent;
    const name = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim() || a.email || '—';
    return [
      name,
      (a.teamIds && a.teamIds[0]) || a.teamId || '—',
      a.contactCenterEnabled ? 'CC enabled' : (a.active ? 'Active' : 'Inactive'),
      a.skillProfileId ?? '—',
      '—',  // Active calls (live) — needs RT API
      <button key="a" className="text-tnh-accent text-xs hover:underline" title="Detail view ships next">Details →</button>,
    ];
  }
  if (areaKey === 'skills') {
    const s = row as WxccSkill;
    return [
      s.name ?? '—',
      s.type ?? '—',
      s.serviceLevelThreshold != null ? String(s.serviceLevelThreshold) : '—',
      '—',  // Agents count needs separate /skill/{id}/agents call
      <button key="a" className="text-tnh-accent text-xs hover:underline" title="Detail view ships next">Details →</button>,
    ];
  }
  // routing → entry points
  const ep = row as WxccEntryPoint;
  return [
    '—',  // DNIS not on entry-point row; needs separate dnis-mapping API
    ep.name ?? '—',
    '—',  // Flow name not on entry-point row; would need a /flow lookup by ID
    '—',  // Time-of-day routing
    '—',  // Last call (RT)
    <button key="a" className="text-tnh-accent text-xs hover:underline" title="Detail view ships next">Details →</button>,
  ];
}

// ---------------------------------------------------------------------------
// WxccFlowsPanel — live Flows tab.
//
// The Webex Service-App OAuth scope can't list flows directly (the Flow
// Designer is gated behind Control Hub admin auth at atlas-a.wbx2.com — see
// wxcc-svc /api/wxcc/flows for the architecture note). But each entry-point
// carries an inline flowId + flowTagId, and /dial-number rows expose the
// dialled DID + 5-digit extension per entryPointId. So we derive two live
// views and render them as one tab:
//
//   1. In-use flows — distinct flowIds referenced by ≥1 entry-point, with
//      the EP names that route to each. Plus an "Open in Control Hub" CTA
//      for the actual Flow Designer.
//   2. Entry Points & dial numbers — one card per EP showing its 10-digit
//      DID(s) and 5-digit extension(s).
// ---------------------------------------------------------------------------
function WxccFlowsPanel() {
  const [flows, setFlows] = useState<WxccInUseFlow[] | null>(null);
  const [eps, setEps] = useState<WxccEntryPointNumbers[] | null>(null);
  const [controlHubUrl, setControlHubUrl] = useState<string>('https://admin.webex.com/wxcc/customer-experience/routing-flows/flows');
  const [importedCount, setImportedCount] = useState<number>(0);
  const [derivedCount, setDerivedCount] = useState<number>(0);
  const [summary, setSummary] = useState<{ ten: number; five: number; dns: number; epsWith: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [importBody, setImportBody] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [importMsg, setImportMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  // BUILD-TASK-admin-oauth-unlocked-features Section A — auto-resolver state.
  const [resolveBusy, setResolveBusy] = useState(false);
  const [resolveMsg, setResolveMsg]   = useState<{ kind: 'ok' | 'warn' | 'err'; text: string } | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    Promise.all([api.wxccFlows(), api.wxccEntryPointNumbers()])
      .then(([flowResp, epResp]) => {
        if (cancelled) return;
        setFlows(flowResp.in_use_flows ?? []);
        setImportedCount(flowResp.imported_count ?? 0);
        setDerivedCount(flowResp.derived_count ?? 0);
        if (flowResp.control_hub_url) setControlHubUrl(flowResp.control_hub_url);
        setEps(epResp.entry_points ?? []);
        setSummary({
          ten: epResp.summary?.ten_digit_total ?? 0,
          five: epResp.summary?.five_digit_total ?? 0,
          dns: epResp.summary?.dial_numbers_total ?? 0,
          epsWith: epResp.summary?.entry_points_with_numbers ?? 0,
        });
        setLoading(false);
      })
      .catch((e) => { if (!cancelled) { setError(e?.message ?? 'Fetch failed'); setLoading(false); }});
    return () => { cancelled = true; };
  }, [refreshTick]);

  const doImport = async () => {
    const body = importBody.trim();
    if (!body) { setImportMsg({ kind: 'err', text: 'Paste a flowId→name map first.' }); return; }
    setImportBusy(true); setImportMsg(null);
    try {
      const r = await api.wxccFlowNamesImport(body);
      setImportMsg({ kind: 'ok', text: `Imported ${r.imported} flow name${r.imported === 1 ? '' : 's'}. Reloading…` });
      setImportBody('');
      setRefreshTick((t) => t + 1);
    } catch (e: any) {
      setImportMsg({ kind: 'err', text: e?.message ?? 'Import failed' });
    } finally {
      setImportBusy(false);
    }
  };

  const f = filter.toLowerCase();
  // Numbers carry punctuation in the source ("+1 (562)…") and the filter
  // input may be raw digits — compare digits-only for number matches.
  const fDigits = filter.replace(/\D/g, '');
  const flowsFiltered = (flows ?? []).filter((fl) =>
    !filter ||
    fl.flow_id.toLowerCase().includes(f) ||
    (fl.name && fl.name.toLowerCase().includes(f)) ||
    fl.entry_point_names.some((n) => n.toLowerCase().includes(f)) ||
    (!!fDigits && (fl.did_numbers ?? []).some((n) => n.replace(/\D/g, '').includes(fDigits))) ||
    (!!fDigits && (fl.ext_numbers ?? []).some((n) => n.replace(/\D/g, '').includes(fDigits))),
  );
  const epsFiltered = (eps ?? []).filter((e) =>
    !filter ||
    e.name.toLowerCase().includes(filter.toLowerCase()) ||
    e.ten_digit_numbers.some((n) => n.includes(filter)) ||
    e.five_digit_numbers.some((n) => n.includes(filter)),
  );
  // Render EPs that actually have ≥1 mapped DN first; show all so callers can
  // still see EPs with no DN (often internal-transfer-only entry points).
  const epsWithNumbers = epsFiltered.filter((e) => e.total > 0);
  const epsWithoutNumbers = epsFiltered.filter((e) => e.total === 0);

  return (
    <div className="space-y-4">
      {/* Action bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search flows by name, ID, entry point, or dial number…"
            className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm w-72"
          />
          <span className="text-xs text-gray-500">
            {loading
              ? 'Loading live data from Webex Contact Center…'
              : summary
                ? `${flowsFiltered.length} flows · ${epsFiltered.length} entry points · ${summary.dns} dial numbers (${summary.ten} ten-digit / ${summary.five} five-digit)`
                : ''}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={async () => {
              setResolveBusy(true); setResolveMsg(null);
              try {
                const r = await api.wxccFlowNamesResolve();
                const by = r.by_source ?? {};
                const breakdown = Object.entries(by)
                  .filter(([, v]) => (v as number) > 0)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(' · ');
                setResolveMsg({
                  kind: r.resolved > 0 ? 'ok' : 'warn',
                  text: r.resolved > 0
                    ? `Resolved ${r.resolved}/${r.total}${breakdown ? ` — ${breakdown}` : ''}. Reloading…`
                    : `No names resolved (${r.total} candidates). ${r.admin_oauth_used ? 'Admin token tried atlas/cms/org-flow, all 404s' : 'Admin OAuth not connected'} — fall back to manual import.`,
                });
                if (r.resolved > 0) setRefreshTick((t) => t + 1);
              } catch (e: any) {
                setResolveMsg({ kind: 'err', text: e?.message ?? 'Resolve failed' });
              } finally { setResolveBusy(false); }
            }}
            disabled={resolveBusy}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark text-sm hover:bg-gray-50 dark:hover:bg-gray-900/40 transition disabled:opacity-50"
            title="Resolve flow names from Webex Control Hub via the admin OAuth token (atlas / cms / org-flow with entry-point fallback)"
          >
            {resolveBusy ? 'Resolving…' : '↻ Resolve via admin OAuth'}
          </button>
          <button
            type="button"
            onClick={() => setImportOpen((v) => !v)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark text-sm hover:bg-gray-50 dark:hover:bg-gray-900/40 transition"
            title="Import a flowId → name map (manual paste)"
          >
            {importOpen ? '× Close importer' : '⤴ Import flow names'}
          </button>
          <a
            href={controlHubUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium transition"
            title="Flow Designer requires Webex Control Hub admin sign-in"
          >
            Open Flow Designer →
          </a>
        </div>
      </div>

      {resolveMsg && (
        <div className={`card p-2 text-xs ${resolveMsg.kind === 'ok' ? 'text-emerald-700 dark:text-emerald-300' : resolveMsg.kind === 'warn' ? 'text-amber-700 dark:text-amber-300' : 'text-rose-600 dark:text-rose-400'}`}>
          {resolveMsg.text}
        </div>
      )}

      {/* Import flow names panel (expandable) */}
      {importOpen && (
        <div className="card p-4 space-y-3">
          <div>
            <div className="text-sm font-semibold">Import flowId → name map</div>
            <div className="text-[11px] text-gray-500 mt-1">
              Paste either a JSON object like{' '}
              <code className="font-mono">{'{ "69e9a60b…": "Main Greeting" }'}</code> or CSV (<code className="font-mono">flowId,flowName</code> per line, header row is tolerated).
              Bulk-upserts into the tenant's <code className="font-mono">wxcc_flow_names</code> table.
            </div>
          </div>
          <textarea
            value={importBody}
            onChange={(e) => setImportBody(e.target.value)}
            rows={8}
            spellCheck={false}
            placeholder={`{ "69e9a60b28d1a4bb…": "Main IVR" }\n\n— or —\n\nflowId,flowName\n69e9a60b28d1a4bb…,Main IVR\nb2c0c3ea…,Sales Routing`}
            className="w-full px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs font-mono"
          />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={doImport}
              disabled={importBusy || !importBody.trim()}
              className="px-4 py-1.5 rounded-lg bg-tnh-accent hover:opacity-90 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {importBusy ? 'Importing…' : 'Import'}
            </button>
            {importMsg && (
              <span className={`text-xs ${importMsg.kind === 'ok' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                {importMsg.text}
              </span>
            )}
          </div>
        </div>
      )}

      {error && <div className="card p-3 text-sm text-rose-600 dark:text-rose-400">{error}</div>}

      {/* In-use flows table */}
      <div className="card overflow-hidden">
        <div className="px-4 py-2.5 border-b border-tnh-border dark:border-tnh-border-dark flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">Live flows in use</div>
            <div className="text-[11px] text-gray-500">Derived from each entry-point's <code className="font-mono">flowId</code> — names are admin-imported via the button above.</div>
          </div>
          {!loading && flows && (
            <span className="text-xs text-gray-500">
              {flows.length} unique flow IDs · {importedCount} imported / {derivedCount} derived
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Flow Name</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Flow ID</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Version tag</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Routed via entry points</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">DID (10-digit)</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">Extension (5-digit)</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">EPs (active / total)</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Fetching live flows…</td></tr>)}
              {!loading && !error && flowsFiltered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">
                  {filter ? 'No flows match the filter.' : 'No flows are currently routed via entry-points.'}
                </td></tr>
              )}
              {!loading && flowsFiltered.map((fl) => {
                const src = fl.name_source ?? 'none';
                const dids = fl.did_numbers ?? [];
                const exts = fl.ext_numbers ?? [];
                return (
                  <tr key={fl.flow_id} className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/30 align-top">
                    <td className="px-4 py-2.5 text-xs">
                      {src === 'imported' ? (
                        <span className="font-medium">{fl.name}</span>
                      ) : src === 'derived' ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span className="italic text-gray-700 dark:text-gray-300" title="Inferred from entry-point name — not the official Control Hub flow name. Import the flowId → name map to override.">{fl.name}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-mono uppercase tracking-wide" title="Derived from entry-point name">from EP</span>
                        </span>
                      ) : (
                        <span className="font-mono text-gray-500" title="No entry-point name was available to derive from">{fl.name}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap">{fl.flow_id}</td>
                    <td className="px-4 py-2.5 text-xs whitespace-nowrap">{fl.flow_tag_id || '—'}</td>
                    <td className="px-4 py-2.5 text-xs">
                      {fl.entry_point_names.length
                        ? fl.entry_point_names.join(', ')
                        : <span className="text-gray-400">{fl.entry_point_ids.length} entry point(s) without names</span>}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {dids.length === 0
                        ? <span className="text-gray-400">—</span>
                        : <ul className="space-y-0.5">
                            {dids.map((n) => (
                              <li key={n} className="font-mono whitespace-nowrap">{formatTenDigit(n)}</li>
                            ))}
                          </ul>}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {exts.length === 0
                        ? <span className="text-gray-400">—</span>
                        : <ul className="space-y-0.5">
                            {exts.map((n) => (
                              <li key={n} className="font-mono whitespace-nowrap">{n}</li>
                            ))}
                          </ul>}
                    </td>
                    <td className="px-4 py-2.5 text-xs whitespace-nowrap">{fl.active_ep_count} / {fl.total_ep_count}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-tnh-border dark:border-tnh-border-dark text-[11px] text-gray-500 bg-gray-50 dark:bg-gray-900/40">
          Flow Designer (JSON, publish) is served from Webex Control Hub — Service App OAuth can't list it. Flow names come from the admin-imported map (button above).
        </div>
      </div>

      {/* Entry points + dial numbers, classified */}
      <div className="card overflow-hidden">
        <div className="px-4 py-2.5 border-b border-tnh-border dark:border-tnh-border-dark flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">Entry Points — mapped dial numbers</div>
            <div className="text-[11px] text-gray-500">
              Each card shows an entry point's 10-digit DID (external) and 5-digit extension (internal). Numbers are joined live from <code className="font-mono">/dial-number</code>.
            </div>
          </div>
          {summary && (
            <span className="text-xs text-gray-500">
              {summary.epsWith} of {epsFiltered.length} EPs have numbers
            </span>
          )}
        </div>

        {loading && <div className="px-4 py-10 text-center text-sm text-gray-500">Fetching entry-point dial-number map…</div>}

        {!loading && !error && epsFiltered.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-gray-500">
            {filter ? 'No entry points match the filter.' : 'No entry points configured.'}
          </div>
        )}

        {!loading && !error && epsFiltered.length > 0 && (
          <div className="divide-y divide-tnh-border dark:divide-tnh-border-dark">
            {epsWithNumbers.map((ep) => <EntryPointCard key={ep.id} ep={ep} />)}
            {epsWithoutNumbers.length > 0 && (
              <details className="px-4 py-3">
                <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">
                  {epsWithoutNumbers.length} entry point{epsWithoutNumbers.length === 1 ? '' : 's'} with no mapped dial numbers (often internal-transfer-only)
                </summary>
                <ul className="mt-2 text-xs text-gray-500 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1 list-disc pl-5">
                  {epsWithoutNumbers.map((ep) => (
                    <li key={ep.id}><span className={ep.active ? '' : 'opacity-60'}>{ep.name || ep.id}</span>{!ep.active && ' (inactive)'}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Display E.164 as-is; for bare 10-digit, prettify to "+1 (xxx) xxx-xxxx".
// Shared by EntryPointCard and the in-use-flows table.
function formatTenDigit(n: string): string {
  const digits = n.replace(/\D/g, '');
  if (digits.length === 10) return `+1 (${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+1 (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  return n;
}

function EntryPointCard({ ep }: { ep: WxccEntryPointNumbers }) {
  return (
    <div className="px-4 py-3 flex flex-col gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold">{ep.name || ep.id}</span>
        {ep.channelType && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200">{ep.channelType}</span>}
        {!ep.active && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">Inactive</span>}
        {ep.flow_id && <span className="text-[11px] text-gray-500 font-mono ml-auto">flow {ep.flow_id}{ep.flow_tag_id ? ` · ${ep.flow_tag_id}` : ''}</span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">10-digit · DID{ep.ten_digit_numbers.length === 1 ? '' : 's'} ({ep.ten_digit_numbers.length})</div>
          {ep.ten_digit_numbers.length === 0
            ? <div className="text-xs text-gray-400">—</div>
            : <ul className="space-y-0.5">
                {ep.ten_digit_numbers.map((n) => (
                  <li key={n} className="font-mono text-sm">{formatTenDigit(n)}</li>
                ))}
              </ul>}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">5-digit · extension{ep.five_digit_numbers.length === 1 ? '' : 's'} ({ep.five_digit_numbers.length})</div>
          {ep.five_digit_numbers.length === 0
            ? <div className="text-xs text-gray-400">—</div>
            : <ul className="space-y-0.5">
                {ep.five_digit_numbers.map((n) => (
                  <li key={n} className="font-mono text-sm">{n}</li>
                ))}
              </ul>}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One feature area = action bar + skeleton table + API note.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// CucmEntityPanel — generic on-prem data view. Drives all 5 CUCM sub-tabs
// (phones, end users, directory numbers, route patterns, device pools) from a
// single per-entity config (CUCM_PANEL_CONFIGS below).
//
// Data flow (agent-less — the on-prem spoke is retired):
//   1. SPA fetches GET /api/cucm/<scope> (session-authed)
//   2. cucm-tunnel-svc builds the AXL SOAP (executeSQLQuery) and sends it
//      through the Long Beach Cloudflare Tunnel (cucm-lb) with the CF-Access
//      service token + AXL basic auth
//   3. Rows are parsed at the edge and returned live — nothing is cached in D1
//
// Like WxC/WxCC this is now pull-on-demand; the AXL creds live on the tunnel
// Worker, not in a customer-LAN container.
// ---------------------------------------------------------------------------
type CucmAreaKey = 'phones' | 'users' | 'lines' | 'routes' | 'pools';
type AnyCucmRow = CucmPhone | CucmEndUser | CucmLine | CucmRoutePattern | CucmDevicePool;

interface CucmPanelCfg {
  scope: string;                                                          // log + last-sync label
  searchPlaceholder: string;
  noun: string;                                                            // "phones" / "end users"
  fetch:    (q: string, tenantId: string) => Promise<{ rows: AnyCucmRow[]; lastSync: any }>;
  renderRow: (row: AnyCucmRow) => ReactNode[];
}

const CUCM_PANEL_CONFIGS: Record<CucmAreaKey, CucmPanelCfg> = {
  phones: {
    scope: 'phones', noun: 'phones',
    searchPlaceholder: 'Search by name / description / line / IP…',
    fetch: async (q, tenantId) => {
      const d = await api.cucmPhones({ tenant_id: tenantId, q, limit: 500 });
      return { rows: d.phones ?? [], lastSync: d.last_sync };
    },
    renderRow: (row) => {
      const p = row as CucmPhone;
      return [
        <span className="font-mono text-xs">{p.name}</span>,
        p.model || '—',
        p.description || '—',
        <span className={`pill text-[11px] px-2 py-0.5 rounded-full ${
          p.registration === 'Registered'   ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
        : p.registration === 'Unregistered' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
        :                                      'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
        }`}>{p.registration || 'Unknown'}</span>,
        <span className="font-mono text-xs">{p.line1_pattern || '—'}</span>,
        <span className="font-mono text-xs">{p.ip_address || '—'}</span>,
        <button className="text-tnh-accent text-xs hover:underline" title="Detail view ships next">Details →</button>,
      ];
    },
  },
  users: {
    scope: 'end_users', noun: 'end users',
    searchPlaceholder: 'Search by user ID / name / email / extension…',
    fetch: async (q, tenantId) => {
      const d = await api.cucmEndUsers({ tenant_id: tenantId, q, limit: 500 });
      return { rows: d.end_users ?? [], lastSync: d.last_sync };
    },
    renderRow: (row) => {
      const u = row as CucmEndUser;
      return [
        <span className="font-mono text-xs">{u.userid}</span>,
        u.first_name || '—',
        u.last_name || '—',
        u.mail_id || '—',
        u.primary_extension ? <span className="font-mono text-xs">{u.primary_extension}</span> : '—',
        <span className={`pill text-[11px] px-2 py-0.5 rounded-full ${
          u.status === 'active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                                : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
        }`}>{u.status || 'active'}</span>,
        <button className="text-tnh-accent text-xs hover:underline" title="Detail view ships next">Details →</button>,
      ];
    },
  },
  lines: {
    scope: 'lines', noun: 'directory numbers',
    searchPlaceholder: 'Search by pattern / description / partition…',
    fetch: async (q, tenantId) => {
      const d = await api.cucmLines({ tenant_id: tenantId, q, limit: 500 });
      return { rows: d.lines ?? [], lastSync: d.last_sync };
    },
    renderRow: (row) => {
      const l = row as CucmLine;
      return [
        <span className="font-mono text-xs">{l.pattern}</span>,
        l.partition || '—',
        l.description || '—',
        l.css || '—',
        l.active_devices > 0 ? `${l.active_devices}` : '—',
        <button className="text-tnh-accent text-xs hover:underline" title="Detail view ships next">Details →</button>,
      ];
    },
  },
  routes: {
    scope: 'route_patterns', noun: 'route patterns',
    searchPlaceholder: 'Search by pattern / target / partition…',
    fetch: async (q, tenantId) => {
      const d = await api.cucmRoutePatterns({ tenant_id: tenantId, q, limit: 500 });
      return { rows: d.route_patterns ?? [], lastSync: d.last_sync };
    },
    renderRow: (row) => {
      const r = row as CucmRoutePattern;
      return [
        <span className="font-mono text-xs">{r.pattern}</span>,
        r.partition || '—',
        r.route_target || '—',
        '—',  // CSS column placeholder — route_patterns table doesn't store CSS directly
        r.pre_dot ? 'Pre-dot' : '—',
        <button className="text-tnh-accent text-xs hover:underline" title="Detail view ships next">Details →</button>,
      ];
    },
  },
  pools: {
    scope: 'device_pools', noun: 'device pools',
    searchPlaceholder: 'Search by name / region / CSS…',
    fetch: async (q, tenantId) => {
      const d = await api.cucmDevicePools({ tenant_id: tenantId, q, limit: 500 });
      return { rows: d.device_pools ?? [], lastSync: d.last_sync };
    },
    renderRow: (row) => {
      const dp = row as CucmDevicePool;
      return [
        dp.name,
        dp.region || '—',
        dp.date_time_group || '—',
        dp.css || '—',
        dp.member_phones_count > 0 ? `${dp.member_phones_count}` : '—',
        <button className="text-tnh-accent text-xs hover:underline" title="Detail view ships next">Details →</button>,
      ];
    },
  },
};

function CucmEntityPanel({ area, tenantId, areaKey }: { area: FeatureArea; tenantId: string; areaKey: CucmAreaKey }) {
  const cfg = CUCM_PANEL_CONFIGS[areaKey];
  const [rows, setRows] = useState<AnyCucmRow[] | null>(null);
  const [lastSync, setLastSync] = useState<{ spoke_id: string; finished_at: number; rows_received: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    cfg.fetch(filter, tenantId)
      .then(({ rows: r, lastSync: ls }) => {
        if (cancelled) return;
        setRows(r); setLastSync(ls); setLoading(false);
      })
      .catch((e) => { if (!cancelled) { setError(e?.message ?? 'Fetch failed'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [areaKey, tenantId, filter]);

  // Reset filter when switching entities — search terms don't transfer
  useEffect(() => { setFilter(''); }, [areaKey]);

  const lastSyncLabel = lastSync
    ? `Last sync ${new Date(lastSync.finished_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })} (${lastSync.rows_received} rows)`
    : 'Live from CUCM AXL over the Long Beach tunnel.';

  return (
    <div className="space-y-3">
      {/* Action bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={cfg.searchPlaceholder}
            className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm w-72"
          />
          <span className="text-xs text-gray-500">
            {loading ? 'Loading…' : `${rows?.length ?? 0} ${cfg.noun}`}
          </span>
        </div>
        {area.primary && (
          <button disabled className="text-white text-sm font-semibold px-4 py-1.5 rounded-lg bg-indigo-600 opacity-50 cursor-not-allowed" title="Read-only — write operations land in the next iteration">
            {area.primary.label}
          </button>
        )}
      </div>

      {/* Data table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
              <tr>
                {area.columns.map((c) => (
                  <th key={c} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={area.columns.length} className="px-4 py-12 text-center text-sm text-gray-500">Querying {cfg.noun} from CUCM (AXL) over the tunnel…</td></tr>}
              {!loading && error && <tr><td colSpan={area.columns.length} className="px-4 py-8 text-center text-sm text-rose-600 dark:text-rose-400">{error}</td></tr>}
              {!loading && !error && (rows?.length ?? 0) === 0 && (
                <tr><td colSpan={area.columns.length} className="px-4 py-12 text-center text-sm text-gray-500">
                  No {cfg.noun} returned by CUCM. If this is unexpected, check the Long Beach tunnel (cucm-lb) and the AXL account.
                </td></tr>
              )}
              {!loading && !error && (rows ?? []).map((row, i) => (
                <tr key={(row as any).pkid ?? (row as any).userid ?? i} className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/30">
                  {cfg.renderRow(row).map((cell, j) => (
                    <td key={j} className="px-4 py-2.5 text-sm whitespace-nowrap">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-tnh-border dark:border-tnh-border-dark text-[11px] text-gray-500 bg-gray-50 dark:bg-gray-900/40 flex items-center justify-between gap-2 flex-wrap">
          <span>{lastSyncLabel}</span>
          <span>{area.api_note}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WxccNewFlowPanel — generate a brand-new WxCC flow from the master template
// + a filled config XLSX. End-to-end pipeline:
//   1. Download the master template (TSB-shape) + config XLSX scaffold
//   2. Fill the XLSX (DNIS, PIN, queue UUIDs, voicemail target, audio text)
//   3. Run build-flow-from-config.py locally — this substitutes everything,
//      runs the freshener (9-point readiness checklist), then the validator
//      (target: 0 errors per WXCC_FLOW_RULES.md)
//   4. Import the resulting JSON into Webex Control Hub → Publish
//
// Why the build is a local script rather than a SPA backend job: the
// freshener + validator are Python tools Ahmed has invested heavily in
// (Layer 1-6 validation contract, MongoDB ObjectId format, runtimeVariables
// generation, etc.). Re-implementing them in JS would create drift. The
// local-script pattern keeps a single source of truth.
// ---------------------------------------------------------------------------
function WxccNewFlowPanel() {
  return (
    <div className="space-y-4">
      {/* HERO */}
      <div className="card p-5">
        <h3 className="text-lg font-semibold">Build New Flow from Template</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-3xl">
          Generate a publish-ready WxCC call flow JSON from the master TSB template.
          Either run the build in-browser using the interactive form below, or download the full
          bundle (XLSX config + Python orchestrator + freshener + validator) and run it on your machine.
          Target outcome either way: <strong>0 validation errors</strong>, ready to import into Control Hub.
        </p>
      </div>

      {/* DOWNLOAD BAR — every bundle artifact, one-click download */}
      <div className="card p-4">
        <div className="text-sm font-semibold mb-2">📦  Bundle downloads</div>
        <p className="text-xs text-gray-500 mb-3">
          One-click downloads for the local CLI workflow. The browser-based build form below uses
          the same master template and writes a publish-ready JSON without needing any local Python.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <DownloadButton href="/WxCC_Flow_Build_Config.xlsx"     label="Config XLSX (template)"        emoji="📋" />
          <DownloadButton href="/wxcc-master-template.json"        label="Master Template JSON"          emoji="🧬" />
          <DownloadButton href="/build-flow-from-config.py"        label="build-flow-from-config.py"     emoji="⚙️" />
          <DownloadButton href="/wxcc_flow_freshener.py"           label="wxcc_flow_freshener.py"        emoji="🧪" />
          <DownloadButton href="/flow_validator.py"                label="flow_validator.py"             emoji="✅" />
          <DownloadButton href="/WXCC_FLOW_RULES.md"               label="WXCC_FLOW_RULES.md"            emoji="📜" />
        </div>
      </div>

      {/* INTERACTIVE BUILD FORM */}
      <NewFlowBuilderForm />

      {/* In-browser validator — same widget as EM/CL Flow sub-tab */}
      <FlowValidator />

      {/* Side-by-side info cards (kept) */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card p-4 border-amber-300 bg-amber-50/40 dark:bg-amber-900/10">
          <div className="font-semibold text-sm">🎙️  Audio Generation (separate pass)</div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            Audio file generation is a separate workflow because translations need your approval per the TTS rules. After you fill Sheet 3 with EN text:
          </p>
          <ol className="mt-2 text-[12px] list-decimal pl-5 space-y-1">
            <li>Upload the filled XLSX to Claude</li>
            <li>Claude proposes SP/KH/TA translations</li>
            <li>You approve</li>
            <li>Claude renders all WAVs in G.711 µ-law 8 kHz mono</li>
            <li>Upload WAVs in the Audio sub-tab (or Control Hub) — pre-flight collision check available there</li>
          </ol>
        </div>

        <div className="card p-4 border-rose-300 bg-rose-50/40 dark:bg-rose-900/10">
          <div className="font-semibold text-sm text-rose-900 dark:text-rose-200">⚠ Quality Gate — 0 errors required</div>
          <p className="text-xs text-gray-700 dark:text-gray-300 mt-1">
            Per <code className="font-mono text-[10px]">WXCC_FLOW_RULES.md</code>: <strong>"The standard for any WxCC flow build is 0 errors in Control Hub's Validation panel."</strong> The browser form runs the same substitution logic and assigns a fresh MongoDB ObjectId for the top-level flow id/flowId so it imports clean every time.
          </p>
          <p className="text-[11px] text-gray-600 dark:text-gray-400 mt-2">
            Validator layers: JSON parses · Import structure · Audio file refs · Queue destinations · Queue conditions · HTTP URL host allowlist.
          </p>
        </div>
      </div>
    </div>
  );
}

// Tiny styled <a download> button for the bundle download bar
function DownloadButton({ href, label, emoji }: { href: string; label: string; emoji: string }) {
  // The actual filename portion is preserved by the browser from the URL path
  return (
    <a href={href} download
       className="flex items-center gap-2 px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark hover:bg-violet-50 dark:hover:bg-violet-900/20 transition text-xs font-medium">
      <span className="text-base">{emoji}</span>
      <span className="truncate">{label}</span>
      <span className="ml-auto text-violet-600 dark:text-violet-400 text-[10px]">↓</span>
    </a>
  );
}

// ---------------------------------------------------------------------------
// WxccDesktopLayoutPanel — Desktop_layout studio.
//
// A WxCC Desktop Layout JSON has three near-duplicate role sections
// (agent / supervisor / supervisorAgent); hand edits routinely change the
// wrong copy. This panel performs the standard operations per-section in the
// browser and PROVES what changed (untouched sections must stay
// byte-identical) before offering the corrected JSON for download.
// Mirrors the WxCCDesktoplayout skill + wxcc_layout_tool.py (downloadable).
// ---------------------------------------------------------------------------
const DL_SECTIONS = ['agent', 'supervisor', 'supervisorAgent'] as const;
type DlSection = typeof DL_SECTIONS[number];

function dlNavList(sec: any): any[] {
  if (!sec || typeof sec !== 'object') return [];
  if (Array.isArray(sec?.area?.navigation)) return sec.area.navigation;
  if (Array.isArray(sec?.navigation)) return sec.navigation;
  return [];
}
function dlSetNavList(sec: any, nav: any[]) {
  if (sec?.area && Array.isArray(sec.area.navigation)) sec.area.navigation = nav;
  else sec.navigation = nav;
}
function dlRids(sec: any): string[] {
  const m = JSON.stringify(sec ?? {}).match(/rId=(\d+)/g) || [];
  return Array.from(new Set(m.map(x => x.replace('rId=', ''))));
}
function dlLabels(sec: any): string[] {
  return dlNavList(sec).map(i => i?.nav?.label ?? '?');
}

function WxccDesktopLayoutPanel() {
  const [orig, setOrig] = useState<any | null>(null);
  const [origName, setOrigName] = useState('layout.json');
  const [dflt, setDflt] = useState<any | null>(null);
  const [err, setErr] = useState('');
  const [report, setReport] = useState<string[]>([]);
  const [result, setResult] = useState<any | null>(null);

  const [scopeMatch, setScopeMatch] = useState('');
  const [keep, setKeep] = useState<Record<DlSection, boolean>>({ agent: false, supervisor: true, supervisorAgent: true });
  const [resetAgent, setResetAgent] = useState(false);
  const [oldRid, setOldRid] = useState('');
  const [newRid, setNewRid] = useState('');
  const [renFrom, setRenFrom] = useState('');
  const [renTo, setRenTo] = useState('');

  const readFile = (f: File, cb: (j: any) => void) => {
    const r = new FileReader();
    r.onload = () => {
      try { cb(JSON.parse(String(r.result))); setErr(''); }
      catch (e: any) { setErr(`${f.name}: not valid JSON — ${e?.message || e}`); }
    };
    r.readAsText(f);
  };

  function apply() {
    if (!orig) { setErr('Upload the current layout JSON first.'); return; }
    setErr('');
    const out = JSON.parse(JSON.stringify(orig));
    const log: string[] = [];
    const touched = new Set<DlSection>();

    if (scopeMatch.trim()) {
      for (const sec of DL_SECTIONS) {
        if (!out[sec] || keep[sec]) continue;
        const nav = dlNavList(out[sec]);
        const kept = nav.filter(i => !JSON.stringify(i).includes(scopeMatch.trim()));
        if (kept.length !== nav.length) {
          dlSetNavList(out[sec], kept);
          touched.add(sec);
          log.push(`scope: removed ${nav.length - kept.length} page(s) matching "${scopeMatch.trim()}" from [${sec}]`);
        }
      }
      if (!log.length) log.push(`scope: nothing matched "${scopeMatch.trim()}" outside kept sections — no change`);
    }
    if (resetAgent) {
      if (!dflt) { setErr('Reset requested but no Default layout uploaded.'); return; }
      out.agent = JSON.parse(JSON.stringify(dflt.agent));
      touched.add('agent');
      log.push('reset: [agent] replaced with the default export agent section (exact copy)');
    }
    if (oldRid.trim() && newRid.trim()) {
      const raw = JSON.stringify(out);
      const n = (raw.match(new RegExp(`rId=${oldRid.trim()}`, 'g')) || []).length;
      if (n === 0) log.push(`swap-link: rId=${oldRid.trim()} not found — no change`);
      else {
        const swapped = JSON.parse(raw.split(`rId=${oldRid.trim()}`).join(`rId=${newRid.trim()}`));
        for (const sec of DL_SECTIONS) {
          if (JSON.stringify(out[sec]) !== JSON.stringify(swapped[sec])) touched.add(sec);
        }
        Object.assign(out, swapped);
        log.push(`swap-link: retargeted ${n} link(s) rId=${oldRid.trim()} -> rId=${newRid.trim()}`);
      }
    }
    if (renFrom.trim() && renTo.trim()) {
      let n = 0;
      const walk = (o: any, sec?: DlSection) => {
        if (Array.isArray(o)) { o.forEach(v => walk(v, sec)); return; }
        if (o && typeof o === 'object') {
          for (const k of Object.keys(o)) {
            if ((k === 'label' || k === 'title' || k === 'appTitle') && typeof o[k] === 'string' && o[k].includes(renFrom.trim())) {
              o[k] = o[k].split(renFrom.trim()).join(renTo.trim());
              n++; if (sec) touched.add(sec);
            } else walk(o[k], sec);
          }
        }
      };
      for (const sec of DL_SECTIONS) walk(out[sec], sec);
      log.push(`rename: ${n} label/title field(s) "${renFrom.trim()}" -> "${renTo.trim()}"`);
    }

    let ok = true;
    for (const sec of DL_SECTIONS) {
      const same = JSON.stringify(orig[sec]) === JSON.stringify(out[sec]);
      if (touched.has(sec)) log.push(`[${sec}] expected to change — ${same ? 'WARNING identical to original (operation had no effect)' : 'changed OK'}`);
      else { log.push(`[${sec}] untouched: ${same ? 'byte-identical OK' : 'MODIFIED — unexpected!'}`); ok = ok && same; }
    }
    log.push(ok ? 'VALIDATION PASSED — safe to upload' : 'VALIDATION FAILED — a section changed that should not have');
    setReport(log);
    setResult(ok ? out : null);
  }

  const download = () => {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = origName.replace(/\.json$/i, '') + '_DesktopLayout.json';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-4">
      <div className="card p-5">
        <h3 className="text-lg font-semibold">Desktop_layout — WxCC Agent Desktop Layout Studio</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-3xl">
          Role-scope pages (supervisor-only), reset agents to the stock default, retarget Analyzer widgets
          and clone layouts for new teams — all in-browser, with proof that untouched role sections stayed
          byte-identical. Same engine as the <code className="font-mono text-[11px]">wxcc_layout_tool.py</code> bundle below.
        </p>
      </div>

      <div className="card p-4">
        <div className="text-sm font-semibold mb-2">📦  Bundle downloads</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <DownloadButton href="/wxcc_layout_tool.py" label="wxcc_layout_tool.py (CLI)" emoji="🛠️" />
        </div>
      </div>

      <div className="card p-4 space-y-3">
        <div className="text-sm font-semibold">1 · Load layout files</div>
        <div className="grid md:grid-cols-2 gap-3 text-xs">
          <label className="block">
            <span className="font-medium">Current layout JSON (required)</span>
            <input type="file" accept=".json" className="mt-1 block w-full text-xs"
                   onChange={e => { const f = e.target.files?.[0]; if (f) { setOrigName(f.name); readFile(f, setOrig); setResult(null); setReport([]); } }} />
          </label>
          <label className="block">
            <span className="font-medium">Default Desktop Layout JSON (only needed for reset)</span>
            <input type="file" accept=".json" className="mt-1 block w-full text-xs"
                   onChange={e => { const f = e.target.files?.[0]; if (f) readFile(f, setDflt); }} />
          </label>
        </div>
        {orig && (
          <div className="grid md:grid-cols-3 gap-2 text-[11px]">
            {DL_SECTIONS.map(sec => (
              <div key={sec} className="rounded-lg border border-tnh-border dark:border-tnh-border-dark p-2">
                <div className="font-semibold">{sec}</div>
                <div className="text-gray-500">pages: {dlLabels(orig[sec]).join(', ') || '(none)'}</div>
                <div className="text-gray-500">analyzer rIds: {dlRids(orig[sec]).join(', ') || '(none)'}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card p-4 space-y-4 text-xs">
        <div className="text-sm font-semibold">2 · Choose operations (any combination, applied in order)</div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <div className="font-medium">Role-scope a page (e.g. supervisor-only)</div>
            <input value={scopeMatch} onChange={e => setScopeMatch(e.target.value)}
                   placeholder='page label or rId, e.g. "Agent Statistics"'
                   className="w-full rounded border border-tnh-border dark:border-tnh-border-dark bg-transparent px-2 py-1.5" />
            <div className="flex gap-3 pt-1">
              {DL_SECTIONS.map(sec => (
                <label key={sec} className="flex items-center gap-1">
                  <input type="checkbox" checked={keep[sec]} onChange={e => setKeep({ ...keep, [sec]: e.target.checked })} />
                  keep in {sec}
                </label>
              ))}
            </div>
            <div className="text-[10px] text-gray-500">Sections NOT checked lose the page. supervisorAgent is a supervisor signed in as agent — it usually follows supervisor.</div>
          </div>

          <div className="space-y-1">
            <div className="font-medium">Reset agents to stock default</div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={resetAgent} onChange={e => setResetAgent(e.target.checked)} />
              Replace the whole <code className="font-mono">agent</code> section from the uploaded Default export
            </label>
            <div className="text-[10px] text-gray-500">Never hand-trim a default — stock layouts differ by version, partial trims leave drift.</div>
          </div>

          <div className="space-y-1">
            <div className="font-medium">Retarget Analyzer widget (rId swap)</div>
            <div className="flex gap-2">
              <input value={oldRid} onChange={e => setOldRid(e.target.value)} placeholder="old rId e.g. 309814"
                     className="w-1/2 rounded border border-tnh-border dark:border-tnh-border-dark bg-transparent px-2 py-1.5" />
              <input value={newRid} onChange={e => setNewRid(e.target.value)} placeholder="new rId e.g. 313028"
                     className="w-1/2 rounded border border-tnh-border dark:border-tnh-border-dark bg-transparent px-2 py-1.5" />
            </div>
          </div>

          <div className="space-y-1">
            <div className="font-medium">Rename team wording (labels/titles only)</div>
            <div className="flex gap-2">
              <input value={renFrom} onChange={e => setRenFrom(e.target.value)} placeholder="from e.g. TSB"
                     className="w-1/2 rounded border border-tnh-border dark:border-tnh-border-dark bg-transparent px-2 py-1.5" />
              <input value={renTo} onChange={e => setRenTo(e.target.value)} placeholder="to e.g. Parking"
                     className="w-1/2 rounded border border-tnh-border dark:border-tnh-border-dark bg-transparent px-2 py-1.5" />
            </div>
            <div className="text-[10px] text-gray-500">Touches label / title / appTitle fields only — URLs and ids are never modified.</div>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <button onClick={apply}
                  className="px-4 py-2 rounded-lg bg-tnh-accent text-white text-xs font-semibold hover:opacity-90 transition">
            Apply &amp; Validate
          </button>
          {result && (
            <button onClick={download}
                    className="px-4 py-2 rounded-lg border border-emerald-500 text-emerald-600 dark:text-emerald-400 text-xs font-semibold hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition">
              ⬇ Download corrected layout
            </button>
          )}
        </div>
        {err && <div className="text-tnh-err">{err}</div>}
        {report.length > 0 && (
          <pre className="rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-tnh-border dark:border-tnh-border-dark p-3 text-[11px] whitespace-pre-wrap">{report.join('\n')}</pre>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card p-4 border-amber-300 bg-amber-50/40 dark:bg-amber-900/10">
          <div className="font-semibold text-sm">🚦 One team = one layout</div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            A team can be attached to only ONE custom desktop layout. Assigning an already-assigned team throws
            <em> 400 — Teams assigned … already assigned to another desktop layout</em>. Fixes, in order:
          </p>
          <ol className="mt-2 text-[12px] list-decimal pl-5 space-y-1">
            <li><strong>Update in place</strong> — open the layout that already owns the team and replace its JSON. No move, no gap, no error.</li>
            <li><strong>Assign from the team side</strong> — Control Hub → Teams → team → Desktop Layout dropdown. Reassigns cleanly even when the layout side 400s (field-proven).</li>
            <li>Unassign from the old layout, save, then assign on the new one (brief default-layout window).</li>
          </ol>
        </div>
        <div className="card p-4 border-sky-300 bg-sky-50/40 dark:bg-sky-900/10">
          <div className="font-semibold text-sm">✅ Verify after deploying</div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            Upload via Control Hub → Desktop Experience → Desktop Layouts. Then have one normal agent and one
            supervisor sign out and back into Agent Desktop — the agent should see the expected (e.g. stock)
            desktop and the supervisor the custom page. Desktop only picks up layout changes on a fresh sign-in.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NewFlowBuilderForm — interactive in-browser builder.
//
// Loads /wxcc-master-template.json, takes form inputs for the values that
// change flow-to-flow (name, PIN, DNIS list, business hours, queue UUIDs,
// emergency dept, voicemail), runs the same substitution logic the Python
// orchestrator does, generates fresh top-level MongoDB ObjectIds (so each
// build is unique and Control Hub treats it as a new flow), and offers the
// built JSON as a download. The user can also upload their own master JSON
// (e.g. a previously-built flow they want to reskin).
// ---------------------------------------------------------------------------
function NewFlowBuilderForm() {
  // Template state
  const [template, setTemplate] = useState<any | null>(null);
  const [tplError, setTplError] = useState<string | null>(null);
  const [tplOverride, setTplOverride] = useState<File | null>(null);

  // Form state — defaults match the TSB master template
  const [flowName, setFlowName] = useState('TSB_New_Flow');
  const [pin, setPin] = useState('5678');
  const [oldPin] = useState('2876');  // value baked into master template
  const [dnis, setDnis] = useState('+15625559999, 89901');
  const [emergencyDept, setEmergencyDept] = useState('ESB');
  const [businessHoursUuid, setBusinessHoursUuid] = useState('');
  const [voicemail, setVoicemail] = useState('');
  const [queueEN, setQueueEN] = useState('');
  const [queueSP, setQueueSP] = useState('');
  const [queueKH, setQueueKH] = useState('');
  const [queueTA, setQueueTA] = useState('');
  const [workerUrl, setWorkerUrl] = useState('https://emergency-status-longbeach.ahmed-21d.workers.dev');
  const [apiSecret, setApiSecret] = useState('WxCC-EmergencyStatus-Secret-2026');

  // Build state
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [report, setReport] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load default template on mount
  useEffect(() => {
    fetch('/wxcc-master-template.json')
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((j) => {
        setTemplate(j);
        // Auto-populate defaults from the template so the user sees the existing values
        const vars = j?.variables ?? [];
        for (const v of vars) {
          if (v?.name === 'businessHours' && v?.value) setBusinessHoursUuid(v.value);
          if (v?.name === 'EmergencyDept' && v?.value) setEmergencyDept(v.value);
        }
        // Find the TSB fallback queue UUID and surface it
        const acts = j?.process?.activities ?? {};
        for (const a of Object.values<any>(acts)) {
          if (a?.name === 'Queue') {
            const fq = a?.properties?.fallbackQueue;
            if (fq) setQueueEN((cur) => cur || fq);
          }
        }
      })
      .catch((e) => setTplError(`Couldn't load master template: ${e.message}`));
  }, []);

  // If user uploads their own master, replace the template
  const onTplUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) { setTplOverride(null); return; }
    setTplOverride(f);
    try {
      const text = await f.text();
      const j = JSON.parse(text);
      setTemplate(j);
      setTplError(null);
    } catch (e: any) {
      setTplError(`Couldn't parse uploaded JSON: ${e.message}`);
    }
  };

  // Generate a 24-char hex MongoDB ObjectId (same format as the Python freshener uses)
  const mongoObjectId = (): string => {
    const now = Math.floor(Date.now() / 1000).toString(16).padStart(8, '0');
    let rand = '';
    for (let i = 0; i < 16; i++) rand += Math.floor(Math.random() * 16).toString(16);
    return now + rand;
  };

  const triggerDownload = (filename: string, content: string) => {
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Server-side build via wxcc-svc /api/wxcc/flow/build — preferred path.
  // The Worker is the canonical implementation; one source of truth for
  // freshener + validator + auto-fix. Returns transformed JSON + report.
  const onBuildOnServer = async () => {
    setBusy(true); setError(null); setResult(null); setReport(null);
    try {
      if (!template) throw new Error('Master template not loaded');
      if (!flowName.trim()) throw new Error('Flow name is required');
      if (!/^\d{2,8}$/.test(pin)) throw new Error('PIN must be 2-8 digits');

      const result = await api.wxccFlowBuild(
        {
          flowName: flowName.trim(), pin, dnis,
          emergencyDept, businessHoursUuid, voicemail,
          queueEN, queueSP, queueKH, queueTA,
          workerUrl, apiSecret,
          autoValidateAndFix: true,
        },
        template,
      );
      const outName = result.fileName ?? `${flowName.trim().replace(/[^A-Za-z0-9_-]/g, '_')}_BUILT.json`;
      triggerDownload(outName, JSON.stringify(result.flow, null, 2));
      const errCount = result.report?.counts?.errors ?? 0;
      const fixCount = result.report?.counts?.fixes_applied ?? 0;
      setResult(`Server OK — ${result.stats?.activities ?? '?'} activities, ${fixCount} auto-fixes applied, ${errCount} errors remain. Downloaded as ${outName}.`);
      // Build log → reuse the inline report rendering by stuffing the lines in
      setReport([
        ...(result.buildLog ?? []).map((l: string) => `✓ ${l}`),
        ...(result.report?.fixed ?? []).map((f: any) => `🛠️ [${f.check}] ${f.description} ×${f.count}`),
        ...((result.report?.issues ?? []).filter((i: any) => i.level === 'error').map((i: any) => `✗ [${i.check}] ${i.msg}`)),
      ]);
    } catch (e: any) {
      setError(`Server build failed: ${e?.message ?? e}`);
    } finally {
      setBusy(false);
    }
  };

  const onBuild = async () => {
    setBusy(true); setError(null); setResult(null); setReport(null);
    try {
      if (!template) throw new Error('Master template not loaded');
      if (!flowName.trim()) throw new Error('Flow name is required');
      if (!/^\d{2,8}$/.test(pin)) throw new Error('PIN must be 2-8 digits');

      const dnisValues = dnis.split(',').map(s => s.trim()).filter(Boolean);
      if (dnisValues.length === 0) throw new Error('At least one DNIS value is required');

      const built = JSON.parse(JSON.stringify(template));
      const log: string[] = [];

      // ── 1. Top-level metadata refresh ────────────────────────────────────
      const newId = mongoObjectId();
      const newFlowId = mongoObjectId();
      built.id = newId;
      built.flowId = newFlowId;
      built.name = flowName.trim();
      built.lastModifiedDate = new Date().toISOString();
      log.push(`✓ Fresh top-level id: ${newId}`);
      log.push(`✓ Fresh flowId: ${newFlowId}`);
      log.push(`✓ Flow name → ${flowName.trim()}`);

      // ── 2. Variable defaults: walk the variables array ───────────────────
      const oldEmergencyVar = `Emergency${oldPin}TTS`;
      const newEmergencyVar = `Emergency${pin}TTS`;
      const oldClosureVar   = `Closure${oldPin}TTS`;
      const newClosureVar   = `Closure${pin}TTS`;

      const vars = built.variables ?? [];
      for (const v of vars) {
        if (!v || typeof v !== 'object') continue;
        if (v.name === 'businessHours' && businessHoursUuid)  v.value = businessHoursUuid;
        if (v.name === 'EmergencyDept' && emergencyDept)      v.value = emergencyDept;
        if (v.name === 'This_Flow_PIN')                       v.value = pin;
        if (v.name === 'Emergency_Worker_URL' && workerUrl)   v.value = workerUrl;
        if (v.name === 'Emergency_API_Secret' && apiSecret)   v.value = apiSecret;
        if (v.name === oldEmergencyVar) v.name = newEmergencyVar;
        if (v.name === oldClosureVar)   v.name = newClosureVar;
      }
      log.push(`✓ Variables updated (PIN ${oldPin} → ${pin}, businessHours, EmergencyDept, worker URL, secret)`);

      // ── 3. Deep substitution pass on the entire JSON ─────────────────────
      // We serialize → string replace → re-parse. Safer than walking object refs
      // because tokens appear inside templated strings, URLs, conditions, etc.
      let s = JSON.stringify(built);
      // 3a. PIN → variable name rename + URL path /xxx/{PIN}
      s = s.split(oldEmergencyVar).join(newEmergencyVar);
      s = s.split(oldClosureVar).join(newClosureVar);
      s = s.split(`{{${oldEmergencyVar}}}`).join(`{{${newEmergencyVar}}}`);  // already covered but explicit
      s = s.split(`{{${oldClosureVar}}}`).join(`{{${newClosureVar}}}`);
      // PIN in URL path segments: /EmergencyTTS/2876, /ClosureOnOFF/2876
      s = s.split(`/${oldPin}`).join(`/${pin}`);
      // 3b. Business hours UUID replacement (template default → user value)
      const tplBusinessHoursDefault = 'b3329a54-b1c8-4e69-9fe1-3faf17d46d22';
      if (businessHoursUuid && businessHoursUuid !== tplBusinessHoursDefault) {
        s = s.split(tplBusinessHoursDefault).join(businessHoursUuid);
        log.push(`✓ businessHours UUID → ${businessHoursUuid.slice(0,8)}…`);
      }
      // 3c. Queue UUIDs (EN). Template fallback queue: 98ab5dd0-bc8f-4bf7-8a91-b0cbe38caa6c
      const tplQueueEN = '98ab5dd0-bc8f-4bf7-8a91-b0cbe38caa6c';
      if (queueEN && queueEN !== tplQueueEN) {
        s = s.split(tplQueueEN).join(queueEN);
        log.push(`✓ EN Queue UUID → ${queueEN.slice(0,8)}…`);
      }
      // 3d. DNIS substitution — replace each old DNIS with new one positionally
      const oldDnisList = ['+15625702000','+15625705700','+15625709993','89900'];
      dnisValues.forEach((newD, i) => {
        if (i < oldDnisList.length && newD !== oldDnisList[i]) {
          s = s.split(`"${oldDnisList[i]}"`).join(`"${newD}"`);
          log.push(`✓ DNIS ${oldDnisList[i]} → ${newD}`);
        }
      });

      const finalBuilt = JSON.parse(s);

      // ── 4. Voicemail (free-form note, since the template's vm wiring is custom per flow) ──
      if (voicemail) {
        finalBuilt.__voicemail_target = voicemail;
        log.push(`✓ Voicemail target stamped: ${voicemail}`);
      }

      // ── 5. Queue per-language stamps for traceability (Sheet 2 echo) ────
      finalBuilt.__build_metadata = {
        built_at: new Date().toISOString(),
        built_by: 'TriNetHub SPA NewFlowBuilderForm',
        source_template: tplOverride?.name ?? 'wxcc-master-template.json (default)',
        pin, flowName: flowName.trim(),
        dnisValues,
        queues: { EN: queueEN || null, SP: queueSP || null, KH: queueKH || null, TA: queueTA || null },
      };

      // ── 6. Download ──────────────────────────────────────────────────────
      const stamp = new Date().toISOString().replace(/[:.]/g,'').slice(0,15);
      const outName = `${flowName.trim().replace(/[^A-Za-z0-9_-]/g,'_')}_BUILT_${stamp}.json`;
      triggerDownload(outName, JSON.stringify(finalBuilt, null, 2));

      log.push(`✓ Output: ${outName}  (${(JSON.stringify(finalBuilt).length/1024).toFixed(1)} KB)`);
      setReport(log);
      setResult(`Built ${outName} — import into Webex Control Hub → Contact Center → Flows → Manage Flows → Import → Publish.`);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5 border-violet-300 bg-violet-50/30 dark:bg-violet-900/15">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h3 className="text-base font-semibold">🛠️  Interactive Builder (no CLI needed)</h3>
        <span className="text-xs text-gray-500">
          {template ? `Template loaded (${Object.keys(template.process?.activities ?? {}).length} activities, ${template.variables?.length ?? 0} variables)` :
           tplError ? `⚠ ${tplError}` : 'Loading master template…'}
        </span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
        Fill the fields, click <strong>Build flow</strong>, download the publish-ready JSON.
        The form pre-loads with the TSB master template's values — change only what differs for the new flow.
      </p>

      {/* Optional: upload a different master template */}
      <div className="mt-4">
        <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          Master template (optional — defaults to TSB master)
        </label>
        <input type="file" accept=".json,application/json" onChange={onTplUpload}
          className="block mt-1 text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-violet-600 file:text-white file:font-medium file:cursor-pointer" />
        {tplOverride && <div className="mt-1 text-xs text-gray-500">Using uploaded: {tplOverride.name} ({(tplOverride.size/1024).toFixed(1)} KB)</div>}
      </div>

      {/* Required fields */}
      <div className="mt-4 grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Flow name <span className="text-rose-500">*</span></label>
          <input type="text" value={flowName} onChange={(e) => setFlowName(e.target.value)} placeholder="TSB_NewDept_Flow"
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">This_Flow_PIN <span className="text-rose-500">*</span></label>
          <input type="text" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="5678"
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
          <div className="text-[11px] text-gray-500 mt-1">2–8 digits. Drives Emergency{pin || '<PIN>'}TTS, Closure{pin || '<PIN>'}TTS, and worker path /{pin || '<PIN>'}.</div>
        </div>

        <div className="md:col-span-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">DNIS values <span className="text-rose-500">*</span></label>
          <input type="text" value={dnis} onChange={(e) => setDnis(e.target.value)} placeholder="+15625551234, 89901"
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
          <div className="text-[11px] text-gray-500 mt-1">Comma-separated. Substitutes positionally for the TSB DNIS placeholders.</div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">EmergencyDept</label>
          <input type="text" value={emergencyDept} onChange={(e) => setEmergencyDept(e.target.value)} placeholder="ESB / TSB / etc."
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Business Hours UUID</label>
          <input type="text" value={businessHoursUuid} onChange={(e) => setBusinessHoursUuid(e.target.value)}
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm font-mono text-[11px]" />
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Voicemail target</label>
          <input type="text" value={voicemail} onChange={(e) => setVoicemail(e.target.value)} placeholder="+15625559876 or extension"
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Queue UUID (EN)</label>
          <input type="text" value={queueEN} onChange={(e) => setQueueEN(e.target.value)}
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm font-mono text-[11px]" />
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Queue UUID (SP)</label>
          <input type="text" value={queueSP} onChange={(e) => setQueueSP(e.target.value)}
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm font-mono text-[11px]" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Queue UUID (KH)</label>
          <input type="text" value={queueKH} onChange={(e) => setQueueKH(e.target.value)}
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm font-mono text-[11px]" />
        </div>

        <div className="md:col-span-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Queue UUID (TA)</label>
          <input type="text" value={queueTA} onChange={(e) => setQueueTA(e.target.value)}
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm font-mono text-[11px]" />
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Emergency Worker URL</label>
          <input type="text" value={workerUrl} onChange={(e) => setWorkerUrl(e.target.value)}
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm font-mono text-[11px]" />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">x-wxcc-secret</label>
          <input type="text" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)}
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm font-mono text-[11px]" />
        </div>
      </div>

      {/* Build button + result */}
      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <button onClick={onBuildOnServer} disabled={busy || !template}
          className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          title="Sends config + master template to wxcc-svc /api/wxcc/flow/build. Server runs build + auto-validate-and-fix. Recommended — canonical implementation.">
          {busy ? 'Building…' : '🛠️ Build + Auto-fix on server'}
        </button>
        <button onClick={onBuild} disabled={busy || !template}
          className="px-5 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          title="In-browser build (legacy). Use the server button above when available.">
          {busy ? 'Building…' : 'Build in browser only'}
        </button>
        {result && <span className="text-sm text-emerald-700 dark:text-emerald-300">{result}</span>}
        {error  && <span className="text-sm text-rose-600 dark:text-rose-400">⚠ {error}</span>}
      </div>

      {/* Build report */}
      {report && report.length > 0 && (
        <div className="mt-4 p-3 rounded-lg bg-gray-900/5 dark:bg-gray-100/5 border border-tnh-border dark:border-tnh-border-dark">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Build report</div>
          <ul className="text-[12px] font-mono text-gray-700 dark:text-gray-300 space-y-0.5">
            {report.map((line, i) => <li key={i}>{line}</li>)}
          </ul>
        </div>
      )}

      <div className="mt-3 text-[11px] text-gray-500">
        The output file is generated entirely in your browser — your config values never leave your machine.
        For the strictest validation, also download <code className="font-mono">flow_validator.py</code> above and run it locally on the BUILT.json.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WxccAudioPanel — live audio file inventory + bulk workflows.
//
// Lists every audio file in the WxCC tenant (via wxcc-svc's listing endpoint
// which proxies Cisco's /organization/{org}/audio-file with the OAuth token).
// Provides downloadable action buttons + documentation for the Python script
// workflows (bulk download, bulk upload with duplicate-name rejection,
// transcribe, export to spreadsheet, import from spreadsheet).
//
// The secret is gated client-side so casual page-loaders don't trigger 477
// metadata fetches. User pastes the x-wxcc-secret once per session.
// ---------------------------------------------------------------------------
function WxccAudioPanel() {
  const [files, setFiles] = useState<WxccAudioFile[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const items = await api.wxccAudioFiles();
      setFiles(items);
    } catch (e: any) {
      setError(e?.message ?? 'Listing failed');
    } finally {
      setLoading(false);
    }
  };

  const downloadOne = async (f: WxccAudioFile) => {
    if (!f.blobId) return;
    setDownloading((d) => ({ ...d, [f.id]: true }));
    try {
      const blob = await api.wxccAudioBlob(f.blobId);
      const safeName = (f.name?.trim() || `audio_${f.id.slice(0,8)}`).replace(/[<>:"/\\|?*\x00-\x1f]/g, '_');
      const fname = /\.wav$/i.test(safeName) ? safeName : `${safeName}.wav`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(`Download failed: ${e?.message ?? e}`);
    } finally {
      setDownloading((d) => { const c = { ...d }; delete c[f.id]; return c; });
    }
  };

  const downloadAllFiltered = async () => {
    if (!files) return;
    setBulkBusy(true); setBulkStatus(null);
    const list = filtered;
    let ok = 0; let fail = 0;
    for (let i = 0; i < list.length; i++) {
      const f = list[i]!;
      setBulkStatus(`Downloading ${i+1}/${list.length}: ${f.name?.trim() || f.id}`);
      try { await downloadOne(f); ok++; } catch { fail++; }
    }
    setBulkStatus(`Done — ${ok} ok, ${fail} failed (${list.length} total)`);
    setBulkBusy(false);
  };

  const exportManifestCsv = () => {
    if (!files) return;
    const rows = filtered;
    const escape = (v: any) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ['name', 'description', 'blobId', 'contentType', 'id'];
    const csv = [header.join(','), ...rows.map((f) => [f.name?.trim(), f.description, f.blobId, f.contentType, f.id].map(escape).join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `wxcc_audio_manifest_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const filtered = (files ?? []).filter((f) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (f.name || '').toLowerCase().includes(q) || (f.description || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4">
      {/* Action banner */}
      <div className="card p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-base font-semibold">Audio File Inventory</div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-3xl">
              Live view of every audio file in this WxCC tenant (Cisco's <code className="font-mono text-xs">/organization/{'{org}'}/audio-file</code>
              endpoint, proxied through wxcc-svc with the Service App OAuth token).
              Combine with the workflow scripts below to download, transcribe, enrich, and re-upload.
            </p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button onClick={load} disabled={loading}
            className="px-4 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium disabled:opacity-50">
            {loading ? 'Loading…' : 'Fetch list'}
          </button>
          <button onClick={downloadAllFiltered} disabled={bulkBusy || !files || filtered.length === 0}
            className="px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium disabled:opacity-50"
            title="Download every file currently shown (respects filter)">
            {bulkBusy ? 'Downloading…' : `📥 Download all${files && filter ? ` (${filtered.length} filtered)` : files ? ` (${files.length})` : ''}`}
          </button>
          <button onClick={exportManifestCsv} disabled={!files || filtered.length === 0}
            className="btn-ghost text-sm disabled:opacity-50"
            title="Export the metadata of the currently filtered files as CSV">
            📊 Export CSV
          </button>
          {files && !bulkBusy && (
            <span className="text-xs text-gray-500">{files.length} files in tenant</span>
          )}
          {bulkStatus && <span className="text-xs text-emerald-700 dark:text-emerald-300">{bulkStatus}</span>}
        </div>
      </div>

      {/* Workflow action grid */}
      <div className="grid md:grid-cols-2 gap-4">
        <WorkflowCard title="📥  Bulk Download" color="emerald"
          desc="Pull every audio file from this tenant into a local folder. Goes through wxcc-svc which uses the Service App OAuth token."
          script="download-wxcc-audio.py"
          cmd={`python3 /mnt/c/Users/ahhamad/Downloads/download-wxcc-audio.py \\
    --out /mnt/c/Users/ahhamad/Downloads/wxcc-audio`}
          notes={['Output: ~/Downloads/wxcc-audio/longbeach/', 'Also writes _manifest.json with the Cisco metadata', '--filter "ESB_" to narrow', '--limit 10 to test small first']} />

        <WorkflowCard title="📤  Bulk Upload (pre-flight + UI drag-drop)" color="sky"
          desc="Cisco audio upload is Control Hub admin-only (Service App tokens return 403 — same as Flow Designer). The pre-flight script tells you up-front which local files would collide with existing WxCC names, optionally stages the safe ones into a clean folder for one-shot drag-drop."
          script="preflight-wxcc-audio-upload.py"
          cmd={`# 1. Run the diff
python3 /mnt/c/Users/ahhamad/Downloads/preflight-wxcc-audio-upload.py \\
    /mnt/c/Users/ahhamad/Downloads/new-prompts \\
    --stage-safe-to /mnt/c/Users/ahhamad/Downloads/safe-to-upload \\
    --csv /mnt/c/Users/ahhamad/Downloads/preflight-report.csv

# 2. Drag the staged folder into Control Hub
https://admin.webex.com/wxcc/customer-experience/audio-files`}
          notes={[
            'Output buckets: SAFE (no collision) / COLLISION (name exists in WxCC) / WXCC_ONLY (informational)',
            'Case-insensitive + whitespace-trimmed match by default — same as Control Hub rejection logic',
            '--stage-safe-to copies SAFE files into a clean folder for one-shot drag-drop',
            '--csv produces a full audit trail for the upload session',
          ]} />

        <WorkflowCard title="🎙️  Transcribe All" color="indigo"
          desc="Run Whisper (via voice-stt-longbeach worker) over every downloaded WAV and write transcripts into WxCC_Prompt_Template.xlsx."
          script="transcribe-wxcc-prompts.py"
          cmd={`python3 /mnt/c/Users/ahhamad/Downloads/transcribe-wxcc-prompts.py \\
    /mnt/c/Users/ahhamad/Downloads/wxcc-audio/longbeach \\
    --template /mnt/c/Users/ahhamad/Downloads/WxCC_Prompt_Template.xlsx`}
          notes={['Language auto-detected from filename (_EN, _SP, _KH, _TA)', 'Per Ahmed\'s TTS rules: transcripts are DRAFT — review before using as canonical text', 'Whisper accuracy: EN ~95%, SP ~90%, KH/TA ~70-80% (spot-check)']} />

        <WorkflowCard title={'📊  Enrich with "Used In Flow"'} color="amber"
          desc="Scan one or more flow JSON files and add a column to the spreadsheet showing every flow + activity that references each audio file."
          script="enrich-prompts-spreadsheet.py"
          cmd={`python3 /mnt/c/Users/ahhamad/Downloads/enrich-prompts-spreadsheet.py \\
    /mnt/c/Users/ahhamad/Downloads/TSB_Main_Flow.json \\
    --template /mnt/c/Users/ahhamad/Downloads/WxCC_Prompt_Template.xlsx`}
          notes={[
            'Handles all 3 reference styles: variable interpolation, _LANG suffix, LANG_ prefix',
            'Reports patterns found in flows but missing from spreadsheet (suggests rows you should add)',
            'Output: _ENRICHED.xlsx',
          ]} />
      </div>

      {/* Live inventory table */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-tnh-border dark:border-tnh-border-dark flex items-center gap-3 flex-wrap">
          <div className="font-semibold">Audio Inventory</div>
          <input value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by name / description…"
            className="ml-auto px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm w-80"
          />
          <span className="text-xs text-gray-500">
            {files ? `${filtered.length}${filter ? ` of ${files.length}` : ''} shown` : 'Click "Fetch list" above to load'}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Name</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Description</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Blob ID</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Type</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!files && !loading && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500">
                  Click <strong>Fetch list</strong> to load the tenant's audio file inventory.
                </td></tr>
              )}
              {loading && <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500">Fetching audio file metadata from WxCC…</td></tr>}
              {error && <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-rose-600">{error}</td></tr>}
              {!loading && !error && filtered.length === 0 && files && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500">No audio files match the filter.</td></tr>
              )}
              {filtered.map((f) => (
                <tr key={f.id} className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/30">
                  <td className="px-4 py-2 font-mono text-xs">{f.name?.trim() || '(no name)'}</td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{f.description || '—'}</td>
                  <td className="px-4 py-2 font-mono text-[11px] text-gray-500">{f.blobId?.slice(0, 30)}…</td>
                  <td className="px-4 py-2 text-xs">{f.contentType}</td>
                  <td className="px-4 py-2 text-xs">
                    <button onClick={() => downloadOne(f)} disabled={!!downloading[f.id] || !f.blobId}
                      className="px-2.5 py-1 rounded-md bg-violet-600 hover:bg-violet-500 text-white text-[11px] font-medium disabled:opacity-50">
                      {downloading[f.id] ? '…' : '📥 Download'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Helper card component for the workflow action grid
function WorkflowCard({ title, color, desc, script, cmd, notes }: { title: string; color: 'emerald'|'sky'|'indigo'|'amber'; desc: string; script: string; cmd: string; notes: string[] }) {
  const borderClass = {
    emerald: 'border-emerald-300 bg-emerald-50/40 dark:bg-emerald-900/10',
    sky:     'border-sky-300 bg-sky-50/40 dark:bg-sky-900/10',
    indigo:  'border-indigo-300 bg-indigo-50/40 dark:bg-indigo-900/10',
    amber:   'border-amber-300 bg-amber-50/40 dark:bg-amber-900/10',
  }[color];
  return (
    <div className={`card p-4 ${borderClass}`}>
      <div className="font-semibold text-sm">{title}</div>
      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{desc}</p>
      <div className="mt-2 text-[11px] text-gray-500 font-mono">{script}</div>
      <pre className="mt-2 text-[11px] font-mono whitespace-pre-wrap bg-gray-900/5 dark:bg-gray-100/5 p-2 rounded text-gray-800 dark:text-gray-200 overflow-x-auto">{cmd}</pre>
      <ul className="mt-2 text-[11px] text-gray-600 dark:text-gray-400 list-disc pl-5 space-y-0.5">
        {notes.map((n) => <li key={n}>{n}</li>)}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CucmMigrationPanel — read-only "CUCM → WxC" gap analysis.
//
// Fetches CUCM end_users (from D1 via /api/spokes/cucm/end-users) and WxC
// users (from webexapis.com/v1/people via wxc-svc), then joins by email
// (case-insensitive). Each row reports one of three migration states:
//
//   matched   — same email exists in both. User is "migrated" (or migration
//               candidate is identified by identity match).
//   cucm_only — CUCM has this user but WxC doesn't. Migration target.
//   wxc_only  — WxC has this user but CUCM doesn't. Already cloud-native;
//               possibly a new hire who never had a desk phone.
//
// Progress % = matched / (matched + cucm_only). Bigger is better.
// This is Phase 1 — read-only. Write actions (create WxC user from CUCM
// attributes, deactivate CUCM user post-migration) ship later when we have
// WxC write scopes on the Service App.
// ---------------------------------------------------------------------------
type MigrationStatus = 'matched' | 'cucm_only' | 'wxc_only';
interface CucmMigrationRow {
  cucm?: CucmEndUser;
  wxc?: WxcUser;
  email: string;
  status: MigrationStatus;
}

function CucmMigrationPanel({ area, tenantId }: { area: FeatureArea; tenantId: string }) {
  const [rows, setRows] = useState<CucmMigrationRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | MigrationStatus>('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    Promise.allSettled([
      api.cucmEndUsers({ tenant_id: tenantId, limit: 2000 }),
      api.wxcUsers(500),
    ]).then(([cucmResult, wxcResult]) => {
      if (cancelled) return;
      const cucmUsers = cucmResult.status === 'fulfilled' ? (cucmResult.value.end_users ?? []) : [];
      const wxcUsers  = wxcResult.status  === 'fulfilled' ? (wxcResult.value.users     ?? []) : [];
      if (cucmResult.status === 'rejected' && wxcResult.status === 'rejected') {
        setError(`Both data sources failed. CUCM: ${(cucmResult.reason as any)?.message}. WxC: ${(wxcResult.reason as any)?.message}`);
        setLoading(false); return;
      }

      // Index by lowercase email for case-insensitive join
      const byEmail = new Map<string, CucmMigrationRow>();
      for (const u of cucmUsers) {
        const email = (u.mail_id ?? '').trim().toLowerCase();
        if (!email) continue;  // CUCM users with no email aren't comparable; skip
        byEmail.set(email, { email, cucm: u, status: 'cucm_only' });
      }
      for (const u of wxcUsers) {
        const primary = (u.emails && u.emails[0]) || u.userName || '';
        const email = primary.trim().toLowerCase();
        if (!email) continue;
        const existing = byEmail.get(email);
        if (existing) {
          existing.wxc = u;
          existing.status = 'matched';
        } else {
          byEmail.set(email, { email, wxc: u, status: 'wxc_only' });
        }
      }
      setRows([...byEmail.values()].sort((a, b) => a.email.localeCompare(b.email)));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [tenantId]);

  const filtered = (rows ?? []).filter((r) => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (!filter) return true;
    const q = filter.toLowerCase();
    return r.email.includes(q)
        || (r.cucm?.first_name ?? '').toLowerCase().includes(q)
        || (r.cucm?.last_name ?? '').toLowerCase().includes(q)
        || (r.cucm?.userid ?? '').toLowerCase().includes(q)
        || (r.wxc?.displayName ?? '').toLowerCase().includes(q);
  });

  const counts = {
    total:     rows?.length ?? 0,
    matched:   (rows ?? []).filter((r) => r.status === 'matched').length,
    cucm_only: (rows ?? []).filter((r) => r.status === 'cucm_only').length,
    wxc_only:  (rows ?? []).filter((r) => r.status === 'wxc_only').length,
  };
  const denom = counts.matched + counts.cucm_only;
  const progressPct = denom > 0 ? Math.round((counts.matched / denom) * 100) : 0;

  return (
    <div className="space-y-3">
      {/* Progress summary */}
      <div className="card p-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">CUCM → WxC migration progress</div>
            <div className="mt-1 flex items-baseline gap-3">
              <span className="text-3xl font-semibold">{progressPct}%</span>
              <span className="text-sm text-gray-500">
                {counts.matched} of {denom} CUCM users matched in WxC by email
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm flex-wrap">
            <button onClick={() => setStatusFilter('all')}       className={`flex items-center gap-2 ${statusFilter === 'all' ? 'font-semibold underline' : ''}`}>
              <span className="w-2 h-2 rounded-full bg-gray-400" /> All <span className="text-gray-500">({counts.total})</span>
            </button>
            <button onClick={() => setStatusFilter('matched')}   className={`flex items-center gap-2 ${statusFilter === 'matched' ? 'font-semibold underline' : ''}`}>
              <span className="w-2 h-2 rounded-full bg-emerald-500" /> Matched <span className="text-gray-500">({counts.matched})</span>
            </button>
            <button onClick={() => setStatusFilter('cucm_only')} className={`flex items-center gap-2 ${statusFilter === 'cucm_only' ? 'font-semibold underline' : ''}`}>
              <span className="w-2 h-2 rounded-full bg-amber-500" /> CUCM only <span className="text-gray-500">({counts.cucm_only})</span>
            </button>
            <button onClick={() => setStatusFilter('wxc_only')}  className={`flex items-center gap-2 ${statusFilter === 'wxc_only' ? 'font-semibold underline' : ''}`}>
              <span className="w-2 h-2 rounded-full bg-sky-500" /> WxC only <span className="text-gray-500">({counts.wxc_only})</span>
            </button>
          </div>
        </div>
        <div className="mt-3 w-full h-2 rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 flex-wrap">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by user ID / name / email…"
          className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm w-80"
        />
        <span className="text-xs text-gray-500">{loading ? 'Loading…' : `${filtered.length} shown`}</span>
      </div>

      {/* Data table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
              <tr>
                {area.columns.map((c) => (
                  <th key={c} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={area.columns.length} className="px-4 py-12 text-center text-sm text-gray-500">Joining CUCM end users with WxC users by email…</td></tr>}
              {!loading && error && <tr><td colSpan={area.columns.length} className="px-4 py-8 text-center text-sm text-rose-600 dark:text-rose-400">{error}</td></tr>}
              {!loading && !error && filtered.length === 0 && (
                <tr><td colSpan={area.columns.length} className="px-4 py-12 text-center text-sm text-gray-500">
                  No users match the current filter. {(rows?.length ?? 0) === 0 ? 'No CUCM end users returned over the tunnel yet.' : ''}
                </td></tr>
              )}
              {!loading && !error && filtered.map((r) => {
                const cucmName = r.cucm ? `${r.cucm.first_name} ${r.cucm.last_name}`.trim() : '—';
                const wxcName  = r.wxc ? (r.wxc.displayName || `${r.wxc.firstName ?? ''} ${r.wxc.lastName ?? ''}`.trim()) : '—';
                const statusPill =
                  r.status === 'matched'   ? <span className="pill text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">Matched</span>
                : r.status === 'cucm_only' ? <span className="pill text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">CUCM only — needs WxC user</span>
                :                            <span className="pill text-[11px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">WxC only — no CUCM source</span>;
                return (
                  <tr key={r.email} className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/30">
                    <td className="px-4 py-2.5 font-mono text-xs">{r.cucm?.userid || '—'}</td>
                    <td className="px-4 py-2.5">{cucmName}</td>
                    <td className="px-4 py-2.5">{r.email}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{r.cucm?.primary_extension || '—'}</td>
                    <td className="px-4 py-2.5">{wxcName}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{r.wxc?.extension || '—'}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{statusPill}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <button className="text-tnh-accent text-xs hover:underline" title="Per-user migration actions ship in a later iteration">
                        {r.status === 'matched' ? 'View' : r.status === 'cucm_only' ? 'Migrate →' : 'Link CUCM →'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-tnh-border dark:border-tnh-border-dark text-[11px] text-gray-500 bg-gray-50 dark:bg-gray-900/40">
          {area.api_note} · Phase 1: read-only gap analysis · Phase 2: per-user migrate actions (requires WxC write scopes)
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UccxMigrationPanel — same shape as CUCM, joins UCCX agents with WxCC agents
// by email. Currently a PLACEHOLDER because UCCX inventory sync isn't wired
// yet (we only wired CUCM in this session). Component structure is ready —
// once you implement uccx-inventory-pusher.py + spoke-svc/api/spokes/uccx/
// endpoints + cucm_agents D1 table, just swap api.uccxAgents() in for the
// stubbed empty array.
// ---------------------------------------------------------------------------
function UccxMigrationPanel({ area, tenantId }: { area: FeatureArea; tenantId: string }) {
  const [wxccAgents, setWxccAgents] = useState<WxccAgent[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    api.wxccAgents()
      .then((d) => { if (!cancelled) { setWxccAgents(d.agents ?? []); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e?.message ?? 'WxCC fetch failed'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [tenantId]);

  return (
    <div className="space-y-3">
      {/* Pattern parity with CUCM migration: show what we know now, be explicit
          about what's not wired yet. */}
      <div className="card p-5 border-amber-300 bg-amber-50/40 dark:bg-amber-900/15">
        <div className="font-medium text-amber-900 dark:text-amber-200">
          UCCX inventory sync is not wired yet
        </div>
        <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 max-w-3xl">
          The UCCX→WxCC migration view uses the same shape as CUCM→WxC: it
          joins UCCX agents with WxCC agents by email and surfaces the gap.
          But UCCX agent inventory isn't being pushed by the spoke yet — only
          CUCM is. To enable this view, replicate the CUCM pattern for UCCX:
        </p>
        <ol className="text-sm text-gray-700 dark:text-gray-300 mt-2 list-decimal list-inside space-y-1">
          <li>D1 migration: <code className="font-mono text-xs">uccx_agents</code>, <code className="font-mono text-xs">uccx_csqs</code>, <code className="font-mono text-xs">uccx_skills</code> tables</li>
          <li>spoke-svc: extend the sync endpoint to accept <code className="font-mono text-xs">scope='uccx_agents'</code></li>
          <li>spoke-side pusher: <code className="font-mono text-xs">uccx-inventory-pusher.py</code> (mirror of <code className="font-mono text-xs">cucm-inventory-pusher.py</code>)</li>
          <li>SPA api helpers: <code className="font-mono text-xs">api.uccxAgents()</code>, etc.</li>
        </ol>
        <p className="text-xs text-gray-500 mt-3">
          Until then, here's what we can show from the WxCC side — the target tenant has{' '}
          {loading ? '…' : error ? `(error: ${error})` : <span className="font-semibold">{wxccAgents?.length ?? 0}</span>}{' '}
          contact-center agents already provisioned. When UCCX data lands, we'll join on email and surface the gap.
        </p>
      </div>

      {/* Skeleton table preserved so the layout doesn't jump when UCCX data lands */}
      <div className="card overflow-hidden opacity-50">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
              <tr>{area.columns.map((c) => (
                <th key={c} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{c}</th>
              ))}</tr>
            </thead>
            <tbody>
              <tr><td colSpan={area.columns.length} className="px-4 py-12 text-center text-sm text-gray-500">
                Awaiting UCCX inventory push from the spoke.
              </td></tr>
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-tnh-border dark:border-tnh-border-dark text-[11px] text-gray-500 bg-gray-50 dark:bg-gray-900/40">
          {area.api_note} · WxCC side: {loading ? '…' : `${wxccAgents?.length ?? 0} agents ready to receive matches`}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WxccEmClFlowPanel — Emergency + Closure subgraph injector.
//
// This panel doesn't render live data; it documents and provides download
// links for the inject-em-cl-flow.py script that splices the EM/CL chain
// (closure check → emergency check → BusinessHours) into any WxCC flow.
//
// The script is the actual functional piece — it generates fresh UUIDs,
// rewrites variable references for the per-flow PIN (e.g. Emergency5678TTS),
// patches both process.activities and diagram.widgets, re-wires existing
// links to BusinessHours through the new chain, and outputs an importable
// JSON ready to upload into Webex Control Hub.
//
// Why ship as a script rather than a server-side transformer: this is a
// per-tenant administrative operation done a few times per year. A local
// script gives the admin direct control over the source/output files and
// avoids hub-side state for what is essentially a one-shot JSON transform.
// A server-side endpoint can be added in Phase 2 if multi-tenant operators
// need it via the UI.
// ---------------------------------------------------------------------------
function WxccEmClFlowPanel() {
  return (
    <div className="space-y-4">
      <div className="card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[20rem]">
            <h3 className="text-lg font-semibold">EM/CL Flow Injector</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-3xl">
              Splice the standard Emergency + Closure subgraph into any WxCC call flow JSON.
              The injector takes your existing flow, drops the closure→emergency check chain
              in front of the activity you choose (typically <code className="font-mono text-xs">BusinessHours</code>),
              and outputs an importable JSON ready to upload into Webex Control Hub.
            </p>
          </div>
        </div>

        <div className="mt-5 grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">What it injects (8 activities)</div>
            <ul className="mt-2 text-sm text-gray-700 dark:text-gray-300 space-y-1 list-disc pl-5">
              <li><code className="font-mono text-xs">DNISEntry</code> — DNIS router. Per-flow configurable: matched DNIS values trigger EM/CL; everything else skips it</li>
              <li><code className="font-mono text-xs">HTTPRequest_ohf_hr3_u33</code> — GET <code>/ClosureOnOFF/{'{PIN}'}</code></li>
              <li><code className="font-mono text-xs">ClosureStatus</code> — case on <code>{'{{ClosureOnOFF}}'}</code></li>
              <li><code className="font-mono text-xs">ClosurePlay</code> — TTS of <code>Closure{'<PIN>'}TTS</code></li>
              <li><code className="font-mono text-xs">EndFlow_z4c</code> — terminates after closure</li>
              <li><code className="font-mono text-xs">HTTPRequest_ohf_tyn</code> — GET <code>/EmergencyTTS/{'{PIN}'}</code></li>
              <li><code className="font-mono text-xs">EmergencyStatus_Condition</code> — case on <code>{'{{EmergencyEN_DS}}'}</code></li>
              <li><code className="font-mono text-xs">EmergencyPlay</code> — TTS of <code>Emergency{'<PIN>'}TTS</code> → continues to target</li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Questions the script will ask</div>
            <ul className="mt-2 text-sm text-gray-700 dark:text-gray-300 space-y-1 list-disc pl-5">
              <li><strong>PIN</strong> (e.g. <code>5678</code>) — drives <code>Emergency5678TTS</code> + <code>Closure5678TTS</code> variable names</li>
              <li><strong>Downstream activity</strong> — name of the activity the chain should flow into (default <code>BusinessHours</code>)</li>
              <li><strong>DNIS values</strong> (NEW) — comma-separated phone numbers or extensions that should trigger the EM/CL chain (e.g. <code>"+15625709993,89900"</code>). Any DNIS NOT in this list falls through to the downstream activity, skipping EM/CL.</li>
              <li><strong>Emergency Worker URL</strong> — defaults to the LB worker</li>
              <li><strong>x-wxcc-secret</strong> — defaults to the LB secret</li>
            </ul>
          </div>
        </div>

        <div className="mt-5 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-tnh-border dark:border-tnh-border-dark">
          <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">Usage</div>
          <pre className="text-xs font-mono whitespace-pre-wrap text-gray-800 dark:text-gray-200">{`# Interactive (recommended — it lists your flow's activities + prompts for each input)
python3 ~/Downloads/inject-em-cl-flow.py YourFlow.json

# Non-interactive (for automation / scripting)
python3 ~/Downloads/inject-em-cl-flow.py YourFlow.json \\
    --pin 5678 \\
    --downstream BusinessHours \\
    --dnis "+15625551234,89900,+13105556789" \\
    --output YourFlow_withEMCL.json`}</pre>
        </div>

        <div className="mt-5 p-3 rounded-lg border border-sky-300 bg-sky-50/40 dark:bg-sky-900/15 text-sm text-gray-700 dark:text-gray-300">
          <strong className="text-sky-900 dark:text-sky-200">After injection:</strong>{' '}
          Webex Control Hub → Contact Center → Flows → Manage Flows → Import → upload the
          <code className="font-mono text-xs mx-1">_withEMCL.json</code> file → Publish.
          Then make sure your emergency-status Worker has entries for the new PIN.
        </div>

        <div className="mt-4 text-xs text-gray-500">
          Files needed (both must live next to each other when running the script):{' '}
          <code className="font-mono">inject-em-cl-flow.py</code> · <code className="font-mono">em-cl-template.json</code>.
          They're already in your <code className="font-mono">~/Downloads/</code> folder if you used the local install path
          from the previous TSB fix. If not, ask Claude to regenerate them.
        </div>
      </div>

      <div className="card p-4 border-amber-300 bg-amber-50/30 dark:bg-amber-900/10">
        <div className="font-medium text-amber-900 dark:text-amber-200">Important — variable name uniqueness per flow</div>
        <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
          Each call flow needs its own PIN so the variables don't collide across flows.
          Long Beach's TSB flow uses PIN <code className="font-mono">2876</code> with{' '}
          <code className="font-mono">Emergency2876TTS</code> and <code className="font-mono">Closure2876TTS</code>.
          When you run the injector on a new flow, pick a different PIN (e.g. <code className="font-mono">5678</code>) — the script
          will generate <code className="font-mono">Emergency5678TTS</code> and <code className="font-mono">Closure5678TTS</code>
          automatically. The PIN also becomes the path segment in the worker URL{' '}
          (<code className="font-mono text-xs">/EmergencyTTS/5678</code>), so make sure your admin UI knows about it.
        </p>
      </div>

      {/* Interactive injector — does it all in-browser, no CLI needed */}
      <EmClInjectorForm />

      {/* Flow Validator — upload any flow JSON, get the 6-check report inline */}
      <FlowValidator />
    </div>
  );
}

// ---------------------------------------------------------------------------
// FlowValidator — JS port of flow_validator.py
//
// Runs the same 6 checks the Python validator runs, so a flow that just got
// rejected by Control Hub (or an EM/CL-injected build) can be uploaded right
// in the SPA and the operator sees exactly which rule failed. No CLI round-trip.
//
// Checks ported:
//   1. HTTP Request URLs target allowed Cloudflare Worker hosts (blocks
//      mockapi.io / localhost / non-allowlisted hosts) AND activity/widget URL parity
//   2. Link integrity — orphans, dead-ends, reachability from NewContact entry
//   3. Audio file references (warn on tenant-specific TSB_/Test_/Demo_ files)
//   4. Queue routing conditions (queue-lookup, queue-contact, CallsQueuedNow / EWT)
//   5. Queue destination validity — placeholder UUIDs + missing fallback on variableQueue
//   6. End-to-end routing trace (informational summary of terminal nodes)
//
// Output: structured ValidationReport rendered with ✓/⚠/✗ markers, copyable
// to the user. Result encoded the same way the Python tool encodes pass/fail:
// "0 errors" = ready to publish.
// ---------------------------------------------------------------------------
const ALLOWED_HOST_PATTERNS = [
  /emergency-status-\w+\.ahmed-21d\.workers\.dev/,
  /directory-lookup-\w+\.ahmed-21d\.workers\.dev/,
  /voice-stt-\w+\.ahmed-21d\.workers\.dev/,
];
const BLOCKED_HOST_PATTERNS = [/mockapi\.io/, /localhost/, /127\.0\.0\.1/];
const SYSTEM_CONDS = new Set(['error', 'timeout', 'invalid', 'exception', 'undefined error']);
const PLACEHOLDER_UUID = /^0+-0+-0+-0+-\d+$/;
const RISKY_AUDIO_PREFIX = /^(TSB|Test|Demo|Custom|Team)_/;

type HostStatus = 'ok' | 'variable' | 'blocked' | 'unknown' | 'empty';
function hostStatus(url: string): HostStatus {
  if (!url) return 'empty';
  if (url.startsWith('{{')) return 'variable';
  for (const p of BLOCKED_HOST_PATTERNS) if (p.test(url)) return 'blocked';
  for (const p of ALLOWED_HOST_PATTERNS) if (p.test(url)) return 'ok';
  return 'unknown';
}

interface Issue { level: 'error' | 'warn' | 'info'; check: string; msg: string }
interface ValidatorReport {
  flowName: string; flowId: string;
  counts: { activities: number; links: number; reachable: number; orphans: number; deadEnds: number; httpActs: number; queueActs: number };
  http: Array<{ name: string; url: string; status: HostStatus; sync: boolean; method: string }>;
  orphans: string[]; deadEnds: string[]; missingLinkWidgets: number;
  audioRefs: Array<{ file: string; users: number; risky: boolean }>;
  queueDest: Array<{ name: string; mode: 'variable' | 'static'; ok: boolean; reason: string }>;
  threshConds: Array<{ name: string; expr: string }>;
  callbacks: string[]; terminals: string[];
  issues: Issue[];
}

function validateFlow(sf: any): ValidatorReport {
  const acts: Record<string, any> = sf?.process?.activities ?? {};
  const links: any[] = sf?.process?.links ?? [];
  const widgets: Record<string, any> = sf?.diagram?.widgets ?? {};
  const issues: Issue[] = [];

  // CHECK 1 — HTTP URLs
  const httpFindings: ValidatorReport['http'] = [];
  for (const [aid, a] of Object.entries(acts)) {
    if (a?.properties?.activityName !== 'http-request-v2') continue;
    const actUrl = a?.properties?.httpRequestUrl ?? '';
    const widUrl = widgets[aid]?.properties?.httpRequestUrl ?? '';
    const same = actUrl === widUrl;
    const status = hostStatus(actUrl);
    httpFindings.push({ name: a?.name ?? aid, url: actUrl, status, sync: same, method: a?.properties?.httpRequestMethod ?? '?' });
    if (status === 'blocked')   issues.push({ level: 'error', check: 'HTTP', msg: `${a?.name}: URL points at blocked host (${actUrl})` });
    if (status === 'unknown')   issues.push({ level: 'warn',  check: 'HTTP', msg: `${a?.name}: URL host not in allowlist (${actUrl})` });
    if (status === 'empty')     issues.push({ level: 'error', check: 'HTTP', msg: `${a?.name}: empty httpRequestUrl` });
    if (!same)                  issues.push({ level: 'error', check: 'HTTP', msg: `${a?.name}: activity URL ≠ widget URL (${actUrl} vs ${widUrl})` });
  }

  // CHECK 2 — Link integrity
  const incoming: Record<string, any[]> = {};
  const outgoing: Record<string, any[]> = {};
  for (const l of links) {
    (outgoing[l.sourceActivityId] ||= []).push(l);
    (incoming[l.targetActivityId] ||= []).push(l);
  }
  let entry: string | null = null;
  for (const [aid, a] of Object.entries(acts)) if (a?.properties?.activityName === 'NewContact') { entry = aid; break; }

  const reachable = new Set<string>();
  if (entry) {
    const stack = [entry];
    while (stack.length) {
      const cur = stack.pop()!;
      if (reachable.has(cur)) continue;
      reachable.add(cur);
      for (const l of (outgoing[cur] ?? [])) stack.push(l.targetActivityId);
    }
  } else {
    issues.push({ level: 'error', check: 'Links', msg: 'No NewContact entry activity found' });
  }

  const orphans: string[] = [];
  const deadEnds: string[] = [];
  const terminals: string[] = [];
  for (const [aid, a] of Object.entries(acts)) {
    if (aid !== entry && !(incoming[aid]?.length)) orphans.push(a?.name ?? aid);
    const outs = outgoing[aid] ?? [];
    if (!outs.length) terminals.push(a?.name ?? aid);
    else if (outs.every((l) => SYSTEM_CONDS.has((l.conditionExpr ?? '').toLowerCase()))) deadEnds.push(a?.name ?? aid);
  }
  let missingLinkWidgets = 0;
  for (const l of links) if (!widgets[l.id]) missingLinkWidgets++;
  if (orphans.length)   issues.push({ level: 'warn',  check: 'Links', msg: `${orphans.length} orphan activities (no inbound): ${orphans.slice(0,3).join(', ')}${orphans.length>3?'…':''}` });
  if (deadEnds.length)  issues.push({ level: 'warn',  check: 'Links', msg: `${deadEnds.length} dead-end activities (only error outs): ${deadEnds.slice(0,3).join(', ')}${deadEnds.length>3?'…':''}` });
  if (missingLinkWidgets) issues.push({ level: 'error', check: 'Links', msg: `${missingLinkWidgets} links missing widget mirror — Control Hub will fail to render the diagram` });
  const unreachable = Object.keys(acts).length - reachable.size;
  if (entry && unreachable > 0) issues.push({ level: 'warn', check: 'Links', msg: `${unreachable} activities unreachable from entry` });

  // CHECK 3 — Audio refs
  const audioRefs: Record<string, number> = {};
  for (const a of Object.values<any>(acts)) {
    const prompts = a?.properties?.prompts ?? [];
    for (const p of prompts) {
      const v = (p && typeof p === 'object') ? (p.value ?? '') : '';
      if (typeof v === 'string' && /\.wav$/i.test(v)) audioRefs[v] = (audioRefs[v] ?? 0) + 1;
    }
  }
  const audioList: ValidatorReport['audioRefs'] = Object.entries(audioRefs).map(([file, users]) => ({ file, users, risky: RISKY_AUDIO_PREFIX.test(file) }));

  // CHECK 5 — Queue destinations
  const queueDest: ValidatorReport['queueDest'] = [];
  for (const a of Object.values<any>(acts)) {
    if (a?.properties?.activityName !== 'queue-contact') continue;
    const p = a.properties; const radio = p.queueRadioGroup ?? '';
    if (radio === 'variableQueue') {
      const fb = p.fallbackQueue ?? '';
      if (!fb || PLACEHOLDER_UUID.test(fb)) {
        queueDest.push({ name: a.name, mode: 'variable', ok: false, reason: 'variableQueue is missing required fallback queue' });
        issues.push({ level: 'error', check: 'Queue', msg: `${a.name}: variableQueue without fallback (Cisco validator rejects)` });
      } else {
        queueDest.push({ name: a.name, mode: 'variable', ok: true, reason: `fallback=${p.fallbackQueue_name ?? fb.slice(0,8)+'…'}` });
      }
    } else {
      const dest = p.destination ?? '';
      if (PLACEHOLDER_UUID.test(dest)) {
        queueDest.push({ name: a.name, mode: 'static', ok: false, reason: `placeholder UUID: ${dest}` });
        issues.push({ level: 'error', check: 'Queue', msg: `${a.name}: queue destination is placeholder UUID — replace or switch to variableQueue with fallback` });
      } else {
        queueDest.push({ name: a.name, mode: 'static', ok: true, reason: p.destination_name ?? dest });
      }
    }
  }

  // CHECK 4 — Queue conditions / capacity logic / callbacks
  const threshConds: ValidatorReport['threshConds'] = [];
  const callbacks: string[] = [];
  let queueActs = 0;
  for (const a of Object.values<any>(acts)) {
    const at = a?.properties?.activityName ?? '';
    const p = a?.properties ?? {};
    if (at === 'queue-contact' || at === 'queue-lookup') queueActs++;
    if (at === 'condition-activity') {
      const expr = p.expression ?? '';
      if (/CallsQueuedNow|EWT|AvailableAgents|PIQ/.test(expr)) threshConds.push({ name: a.name, expr });
    }
    if (at === 'callback') callbacks.push(`${a.name} (ANI=${p.callbackAni ?? '?'}, Queue=${p.callbackQueue ?? '?'})`);
  }

  let httpActs = 0;
  for (const a of Object.values<any>(acts)) if (a?.properties?.activityName === 'http-request-v2') httpActs++;

  return {
    flowName: sf?.name ?? '?', flowId: sf?.id ?? '?',
    counts: { activities: Object.keys(acts).length, links: links.length, reachable: reachable.size, orphans: orphans.length, deadEnds: deadEnds.length, httpActs, queueActs },
    http: httpFindings, orphans, deadEnds, missingLinkWidgets, audioRefs: audioList,
    queueDest, threshConds, callbacks, terminals, issues,
  };
}

function FlowValidator() {
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<ValidatorReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverReport, setServerReport] = useState<any | null>(null);

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null); setReport(null); setServerReport(null);
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    setFile(f);
  };

  const onValidate = async () => {
    if (!file) return;
    setBusy(true); setError(null); setReport(null); setServerReport(null);
    try {
      const txt = await file.text();
      const sf = JSON.parse(txt);
      const r = validateFlow(sf);
      setReport(r);
    } catch (e: any) {
      setError(`Couldn't parse / validate JSON: ${e?.message ?? e}`);
    } finally {
      setBusy(false);
    }
  };

  // Server-side validate-and-fix: POSTs flow JSON to wxcc-svc, gets back a
  // FIXED flow + structured report, and offers the fixed JSON as a download.
  const onValidateAndFix = async () => {
    if (!file) return;
    setBusy(true); setError(null); setReport(null); setServerReport(null);
    try {
      const txt = await file.text();
      const sf = JSON.parse(txt);
      const result = await api.wxccFlowValidateAndFix(sf);
      setServerReport(result);
      // Download the fixed flow automatically — the whole point of this button
      const outName = (sf.name ?? 'flow').replace(/[^A-Za-z0-9_-]/g,'_') + '_FIXED.json';
      const blob = new Blob([JSON.stringify(result.flow, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = outName;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(`Server validate-and-fix failed: ${e?.message ?? e}`);
    } finally {
      setBusy(false);
    }
  };

  const downloadReport = () => {
    if (!report) return;
    const text = formatReportText(report);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${(report.flowName || 'flow').replace(/[^A-Za-z0-9_-]/g,'_')}_validator_report.txt`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const errors = (report?.issues ?? []).filter((i) => i.level === 'error');
  const warns  = (report?.issues ?? []).filter((i) => i.level === 'warn');
  const passed = report && errors.length === 0;

  return (
    <div className="card p-5 border-sky-300 bg-sky-50/30 dark:bg-sky-900/15">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h3 className="text-base font-semibold">🔍  Flow Validator — upload any flow JSON</h3>
        <span className="text-xs text-gray-500">Same 6-check audit as <code className="font-mono">flow_validator.py</code></span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
        Upload a flow JSON (a build that failed, a Control Hub export, an EM/CL-injected output, anything) and the SPA runs the same validation rules the Python CLI does. <strong>0 errors = ready to publish.</strong>
      </p>

      <div className="mt-4 flex items-end gap-3 flex-wrap">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Flow JSON</label>
          <input type="file" accept=".json,application/json" onChange={onUpload}
            className="block mt-1 text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-sky-600 file:text-white file:font-medium file:cursor-pointer" />
          {file && <div className="mt-1 text-xs text-gray-500">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>}
        </div>
        <button onClick={onValidate} disabled={busy || !file}
          className="px-5 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
          {busy ? 'Validating…' : '🔍 Validate flow'}
        </button>
        <button onClick={onValidateAndFix} disabled={busy || !file}
          className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          title="Sends the flow to wxcc-svc, applies safe auto-fixes (URL sync, missing widget mirrors, runtimeVariables reconcile, etc.), and downloads the fixed flow.">
          {busy ? 'Fixing…' : '🛠️ Validate & Auto-fix on server'}
        </button>
        {report && (
          <button onClick={downloadReport}
            className="btn-ghost text-sm">📄 Download report</button>
        )}
        {error && <span className="text-sm text-rose-600 dark:text-rose-400">⚠ {error}</span>}
      </div>

      {/* Server-side report renders below the in-browser one (separate panel) */}
      {serverReport && (
        <div className="mt-5 space-y-2">
          <div className={`p-3 rounded-lg border ${serverReport.report?.passed ? 'border-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/20' : 'border-rose-300 bg-rose-50/60 dark:bg-rose-900/20'}`}>
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div className={`font-semibold text-sm ${serverReport.report?.passed ? 'text-emerald-800 dark:text-emerald-200' : 'text-rose-800 dark:text-rose-200'}`}>
                {serverReport.report?.passed
                  ? `✓ Server: 0 errors, ${serverReport.report?.counts?.warnings ?? 0} warnings — fixed flow downloaded`
                  : `✗ Server: ${serverReport.report?.counts?.errors ?? 0} errors remain (${serverReport.report?.counts?.fixes_applied ?? 0} auto-fixes applied) — fixed-as-far-as-possible flow downloaded`}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">via <code className="font-mono">/api/wxcc/flow/validate-and-fix</code></div>
            </div>
          </div>
          {Array.isArray(serverReport.report?.fixed) && serverReport.report.fixed.length > 0 && (
            <div className="card p-3 border-emerald-300 bg-emerald-50/30 dark:bg-emerald-900/10">
              <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-2">{serverReport.report.fixed.length} auto-fix(es) applied</div>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                {serverReport.report.fixed.map((f: any, i: number) => (
                  <li key={i}>🛠️ <span className="text-emerald-700 dark:text-emerald-300 font-mono text-xs">[{f.check}]</span> {f.description} <span className="text-gray-500">×{f.count}</span></li>
                ))}
              </ul>
            </div>
          )}
          {Array.isArray(serverReport.report?.issues) && serverReport.report.issues.filter((i: any) => i.level === 'error').length > 0 && (
            <div className="card p-3 border-rose-300 bg-rose-50/30 dark:bg-rose-900/10">
              <div className="text-xs font-semibold uppercase tracking-wider text-rose-700 dark:text-rose-300 mb-2">Errors that need human review (server can't safely auto-fix)</div>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                {serverReport.report.issues.filter((i: any) => i.level === 'error').map((i: any, idx: number) => (
                  <li key={idx}>✗ <span className="text-rose-700 dark:text-rose-300 font-mono text-xs">[{i.check}]</span> {i.msg}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Report */}
      {report && (
        <div className="mt-5 space-y-3">
          {/* Pass/fail banner */}
          <div className={`p-3 rounded-lg border ${passed ? 'border-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/20' : 'border-rose-300 bg-rose-50/60 dark:bg-rose-900/20'}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className={`font-semibold text-sm ${passed ? 'text-emerald-800 dark:text-emerald-200' : 'text-rose-800 dark:text-rose-200'}`}>
                {passed ? `✓  ${errors.length} errors, ${warns.length} warnings — flow is ready to publish` : `✗  ${errors.length} errors, ${warns.length} warnings — flow is NOT ready to publish`}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Flow: <strong>{report.flowName}</strong> · {report.counts.activities} activities · {report.counts.links} links · {report.counts.httpActs} HTTP · {report.counts.queueActs} queue
              </div>
            </div>
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div className="card p-3 border-rose-300 bg-rose-50/30 dark:bg-rose-900/10">
              <div className="text-xs font-semibold uppercase tracking-wider text-rose-700 dark:text-rose-300 mb-2">{errors.length} ERROR(s)</div>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                {errors.map((i, idx) => <li key={idx}>✗ <span className="text-rose-700 dark:text-rose-300 font-mono text-xs">[{i.check}]</span> {i.msg}</li>)}
              </ul>
            </div>
          )}

          {/* Warnings */}
          {warns.length > 0 && (
            <div className="card p-3 border-amber-300 bg-amber-50/30 dark:bg-amber-900/10">
              <div className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300 mb-2">{warns.length} WARNING(s)</div>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                {warns.map((i, idx) => <li key={idx}>⚠ <span className="text-amber-700 dark:text-amber-300 font-mono text-xs">[{i.check}]</span> {i.msg}</li>)}
              </ul>
            </div>
          )}

          {/* Detail tables */}
          <details className="card p-3">
            <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-gray-500">HTTP Request URLs ({report.http.length})</summary>
            <table className="w-full text-xs mt-2">
              <thead className="text-gray-500"><tr><th className="text-left py-1">Activity</th><th className="text-left">Method</th><th className="text-left">URL</th><th className="text-left">Host</th><th className="text-left">Sync</th></tr></thead>
              <tbody>
                {report.http.map((h, i) => (
                  <tr key={i} className="border-t border-tnh-border dark:border-tnh-border-dark">
                    <td className="py-1 pr-2 font-mono">{h.name}</td>
                    <td className="pr-2">{h.method}</td>
                    <td className="pr-2 font-mono text-[10px] break-all">{h.url || '(empty)'}</td>
                    <td className="pr-2">{h.status === 'ok' ? '✓' : h.status === 'variable' ? '○' : h.status === 'blocked' ? '✗' : h.status === 'empty' ? '!' : '?'} {h.status}</td>
                    <td>{h.sync ? '✓' : '✗'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>

          <details className="card p-3">
            <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-gray-500">Queue destinations ({report.queueDest.length})</summary>
            <table className="w-full text-xs mt-2">
              <thead className="text-gray-500"><tr><th className="text-left py-1">Activity</th><th className="text-left">Mode</th><th className="text-left">Status</th><th className="text-left">Detail</th></tr></thead>
              <tbody>
                {report.queueDest.map((q, i) => (
                  <tr key={i} className="border-t border-tnh-border dark:border-tnh-border-dark">
                    <td className="py-1 pr-2 font-mono">{q.name}</td>
                    <td className="pr-2">{q.mode}</td>
                    <td className="pr-2">{q.ok ? '✓' : '✗'}</td>
                    <td className="pr-2 font-mono text-[10px]">{q.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>

          <details className="card p-3">
            <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-gray-500">Audio references ({report.audioRefs.length})</summary>
            <ul className="text-xs mt-2 space-y-0.5">
              {report.audioRefs.map((a, i) => (
                <li key={i} className="font-mono text-[11px]">{a.risky ? '⚠' : '○'} {a.file} <span className="text-gray-500">— used by {a.users} activities</span></li>
              ))}
              {report.audioRefs.length === 0 && <li className="text-gray-500">No .wav references found.</li>}
            </ul>
          </details>

          <details className="card p-3">
            <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-gray-500">Capacity / threshold conditions ({report.threshConds.length})</summary>
            <ul className="text-xs mt-2 space-y-0.5">
              {report.threshConds.map((c, i) => <li key={i} className="font-mono text-[11px]">{c.name}: {c.expr}</li>)}
              {report.threshConds.length === 0 && <li className="text-gray-500">No CallsQueuedNow / EWT / AvailableAgents / PIQ conditions found.</li>}
            </ul>
          </details>

          <details className="card p-3">
            <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-gray-500">Orphans · Dead-ends · Terminals</summary>
            <div className="text-xs mt-2 grid md:grid-cols-3 gap-3">
              <div>
                <div className="font-semibold mb-1">Orphans ({report.orphans.length})</div>
                <ul className="font-mono text-[11px] space-y-0.5">{report.orphans.length === 0 ? <li className="text-gray-500">none</li> : report.orphans.map((n,i) => <li key={i}>· {n}</li>)}</ul>
              </div>
              <div>
                <div className="font-semibold mb-1">Dead-ends ({report.deadEnds.length})</div>
                <ul className="font-mono text-[11px] space-y-0.5">{report.deadEnds.length === 0 ? <li className="text-gray-500">none</li> : report.deadEnds.map((n,i) => <li key={i}>· {n}</li>)}</ul>
              </div>
              <div>
                <div className="font-semibold mb-1">Terminals ({report.terminals.length})</div>
                <ul className="font-mono text-[11px] space-y-0.5">{report.terminals.map((n,i) => <li key={i}>· {n}</li>)}</ul>
              </div>
            </div>
          </details>
        </div>
      )}
    </div>
  );
}

function formatReportText(r: ValidatorReport): string {
  const lines: string[] = [];
  lines.push('═══════════════════════════════════════════════════════════════');
  lines.push(`  Flow Validator Report — ${r.flowName}`);
  lines.push(`  id:          ${r.flowId}`);
  lines.push(`  Activities:  ${r.counts.activities}`);
  lines.push(`  Links:       ${r.counts.links}`);
  lines.push(`  Reachable:   ${r.counts.reachable}`);
  lines.push(`  HTTP acts:   ${r.counts.httpActs}`);
  lines.push(`  Queue acts:  ${r.counts.queueActs}`);
  lines.push('═══════════════════════════════════════════════════════════════');
  const errors = r.issues.filter(i => i.level === 'error');
  const warns  = r.issues.filter(i => i.level === 'warn');
  lines.push('');
  lines.push(`SUMMARY: ${errors.length} errors, ${warns.length} warnings`);
  lines.push(errors.length === 0 ? '✓ Ready to publish (no errors)' : '✗ NOT ready to publish');
  lines.push('');
  if (errors.length) { lines.push('ERRORS:'); for (const e of errors) lines.push(`  ✗ [${e.check}] ${e.msg}`); lines.push(''); }
  if (warns.length)  { lines.push('WARNINGS:'); for (const w of warns)  lines.push(`  ⚠ [${w.check}] ${w.msg}`); lines.push(''); }
  lines.push('HTTP activities:');
  for (const h of r.http) lines.push(`  [${h.status}] ${h.method} ${h.name} ${h.url}${h.sync ? '' : '  (widget URL desync)'}`);
  lines.push('');
  lines.push('Queue destinations:');
  for (const q of r.queueDest) lines.push(`  [${q.ok ? 'ok' : 'FAIL'}] ${q.name} (${q.mode}) — ${q.reason}`);
  lines.push('');
  lines.push('Audio references:');
  for (const a of r.audioRefs) lines.push(`  ${a.risky ? '⚠' : '○'} ${a.file} (used ${a.users}x)`);
  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// EmClInjectorForm — interactive in-browser injector for EM/CL chain.
// Mirrors the inject-em-cl-flow.py CLI: user uploads a flow JSON, picks PIN /
// downstream activity / DNIS values, clicks Inject, and downloads the
// modified flow ready to import into Webex Control Hub.
// Template loaded from /em-cl-template.json (public asset).
// ---------------------------------------------------------------------------
function EmClInjectorForm() {
  const [template, setTemplate] = useState<any | null>(null);
  const [tplError, setTplError] = useState<string | null>(null);
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [sourceFlow, setSourceFlow] = useState<any | null>(null);
  const [activityNames, setActivityNames] = useState<string[]>([]);
  const [pin, setPin] = useState('5678');
  const [downstream, setDownstream] = useState('BusinessHours');
  const [dnis, setDnis] = useState('');
  const [workerUrl, setWorkerUrl] = useState('https://emergency-status-longbeach.ahmed-21d.workers.dev');
  const [apiSecret, setApiSecret] = useState('WxCC-EmergencyStatus-Secret-2026');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch template on mount
  useEffect(() => {
    fetch('/em-cl-template.json')
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(setTemplate)
      .catch((e) => setTplError(`Couldn't load /em-cl-template.json: ${e.message}`));
  }, []);

  // Parse the uploaded flow + list its activities so user can pick downstream
  const onSourceChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null); setResult(null);
    const f = e.target.files?.[0];
    if (!f) { setSourceFile(null); setSourceFlow(null); setActivityNames([]); return; }
    setSourceFile(f);
    try {
      const text = await f.text();
      const flow = JSON.parse(text);
      setSourceFlow(flow);
      const acts = flow?.process?.activities ?? {};
      const names = Object.values(acts)
        .filter((a: any) => a && typeof a === 'object' && a.name)
        .map((a: any) => a.name as string);
      const unique = Array.from(new Set(names)).sort();
      setActivityNames(unique);
      // Auto-suggest BusinessHours if present
      if (unique.includes('BusinessHours') && downstream === 'BusinessHours') {/* keep default */}
      else if (unique.length > 0 && !unique.includes(downstream)) setDownstream(unique[0]!);
    } catch (e: any) {
      setError(`Couldn't parse JSON: ${e.message}`);
      setSourceFlow(null); setActivityNames([]);
    }
  };

  const triggerDownload = (filename: string, content: string) => {
    const blob = new Blob([content], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const onInject = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      if (!template) throw new Error('Template not loaded yet');
      if (!sourceFlow) throw new Error('Upload a flow JSON first');
      if (!pin || !/^\d{2,8}$/.test(pin)) throw new Error('PIN must be 2-8 digits');
      if (!downstream || !activityNames.includes(downstream)) throw new Error(`Downstream activity "${downstream}" not in this flow`);
      const dnisValues = dnis.split(',').map(s => s.trim()).filter(Boolean);
      if (dnisValues.length === 0) throw new Error('Need at least one DNIS value');

      // Deep-copy the source flow so we don't mutate the parsed state
      const src = JSON.parse(JSON.stringify(sourceFlow));
      const stats = injectEmCl(src, template, { pin, downstream, dnisValues, workerUrl, apiSecret });

      const outName = (sourceFile?.name ?? 'flow.json').replace(/\.json$/i, '') + '_withEMCL.json';
      triggerDownload(outName, JSON.stringify(src, null, 2));
      setResult(`OK — inserted ${stats.insertedActivities} activities + ${stats.insertedLinks} links, rerouted ${stats.reroutedLinks} existing links, added ${stats.addedVariables.length} variables. Downloaded as ${outName}.`);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  // Server-side inject — POSTs to wxcc-svc, which runs the canonical injector
  // + auto-validate-and-fix and returns a publish-ready JSON. Same UI, but the
  // transformation now has a single source of truth.
  const onInjectOnServer = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      if (!template) throw new Error('Template not loaded yet');
      if (!sourceFlow) throw new Error('Upload a flow JSON first');
      if (!pin || !/^\d{2,8}$/.test(pin)) throw new Error('PIN must be 2-8 digits');
      if (!downstream || !activityNames.includes(downstream)) throw new Error(`Downstream activity "${downstream}" not in this flow`);
      const dnisValues = dnis.split(',').map(s => s.trim()).filter(Boolean);
      if (dnisValues.length === 0) throw new Error('Need at least one DNIS value');

      const baseName = (sourceFile?.name ?? 'flow.json').replace(/\.json$/i, '');
      const result = await api.wxccFlowInjectEmCl({
        sourceFlow, template, pin, downstream, dnisValues, workerUrl, apiSecret,
        outputBaseName: baseName, autoValidateAndFix: true,
      });
      const outName = result.fileName ?? `${baseName}_withEMCL.json`;
      triggerDownload(outName, JSON.stringify(result.flow, null, 2));
      const errCount = result.report?.counts?.errors ?? 0;
      const fixCount = result.report?.counts?.fixes_applied ?? 0;
      setResult(`Server OK — inserted ${result.stats.insertedActivities} activities, rerouted ${result.stats.reroutedLinks} links, ${fixCount} auto-fixes, ${errCount} errors remain. Downloaded as ${outName}.`);
    } catch (e: any) {
      setError(`Server inject failed: ${e?.message ?? e}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5 border-violet-300 bg-violet-50/30 dark:bg-violet-900/15">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h3 className="text-base font-semibold">Interactive Injector</h3>
        <span className="text-xs text-gray-500">
          {template ? `Template loaded (${Object.keys(template.activities ?? {}).length} activities)` : tplError ? `⚠ ${tplError}` : 'Loading template…'}
        </span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
        Upload a flow JSON, pick the parameters below, and download the augmented flow — no CLI needed.
      </p>

      {/* File picker */}
      <div className="mt-4">
        <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Source flow JSON</label>
        <input type="file" accept=".json,application/json" onChange={onSourceChange}
          className="block mt-1 text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-violet-600 file:text-white file:font-medium file:cursor-pointer" />
        {sourceFile && (
          <div className="mt-1 text-xs text-gray-500">
            Loaded: {sourceFile.name} · {(sourceFile.size / 1024).toFixed(1)} KB · {activityNames.length} activities
          </div>
        )}
      </div>

      {/* Parameter inputs */}
      <div className="mt-4 grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">PIN (2–8 digits)</label>
          <input type="text" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="5678"
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
          <div className="text-[11px] text-gray-500 mt-1">Drives variable names Emergency{pin || '<PIN>'}TTS and Closure{pin || '<PIN>'}TTS</div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Downstream activity</label>
          <select value={downstream} onChange={(e) => setDownstream(e.target.value)} disabled={activityNames.length === 0}
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm disabled:opacity-50">
            {activityNames.length === 0 ? <option value="">(upload a flow first)</option>
              : activityNames.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <div className="text-[11px] text-gray-500 mt-1">Where the EM/CL chain flows into when neither closure nor emergency is active.</div>
        </div>

        <div className="md:col-span-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">DNIS values (comma-separated)</label>
          <input type="text" value={dnis} onChange={(e) => setDnis(e.target.value)} placeholder="+15625551234, 89900, +13105556789"
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
          <div className="text-[11px] text-gray-500 mt-1">Phone numbers / extensions that should trigger the EM/CL chain. Other DNIS values fall through to the downstream activity.</div>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Emergency Worker URL</label>
          <input type="text" value={workerUrl} onChange={(e) => setWorkerUrl(e.target.value)}
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm font-mono text-xs" />
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">x-wxcc-secret</label>
          <input type="text" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)}
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm font-mono text-xs" />
        </div>
      </div>

      {/* Submit + result */}
      <div className="mt-5 flex items-center gap-3 flex-wrap">
        <button onClick={onInjectOnServer} disabled={busy || !template || !sourceFile}
          className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          title="Sends flow + params to wxcc-svc /api/wxcc/flow/inject-em-cl. Runs the canonical injector + auto-validate-and-fix on the server (single source of truth). Recommended.">
          {busy ? 'Injecting…' : '🛠️ Inject + Auto-fix on server'}
        </button>
        <button onClick={onInject} disabled={busy || !template || !sourceFile}
          className="px-5 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          title="In-browser injector (legacy). Use the server button above when available.">
          {busy ? 'Injecting…' : 'Inject in browser only'}
        </button>
        {result && <span className="text-sm text-emerald-700 dark:text-emerald-300">{result}</span>}
        {error  && <span className="text-sm text-rose-600 dark:text-rose-400">{error}</span>}
      </div>

      <div className="mt-3 text-[11px] text-gray-500">
        The output file is generated and downloaded entirely in your browser — your flow JSON never leaves your machine.
        Next step after download: Webex Control Hub → Contact Center → Flows → Manage Flows → Import → upload the file → Publish.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// injectEmCl — pure JS port of inject-em-cl-flow.py's inject() function.
// Mutates src; returns stats.
// ---------------------------------------------------------------------------
function injectEmCl(src: any, template: any, opts: { pin: string; downstream: string; dnisValues: string[]; workerUrl: string; apiSecret: string }) {
  const { pin, downstream, dnisValues, workerUrl, apiSecret } = opts;
  const newUuid = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

  // Locate downstream in source
  const srcActivities = src.process?.activities ?? (src.process = src.process || {}, src.process.activities = src.process.activities || {});
  const srcWidgets = src.diagram?.widgets ?? (src.diagram = src.diagram || {}, src.diagram.widgets = src.diagram.widgets || {});
  let srcLinks = src.process.links ?? [];
  if (!Array.isArray(srcLinks)) srcLinks = Object.values(srcLinks);
  src.process.links = srcLinks;

  const downstreamEntry = Object.entries(srcActivities).find(([, a]: any) => a?.name === downstream);
  if (!downstreamEntry) throw new Error(`Downstream activity '${downstream}' not in source`);
  const downstreamId = downstreamEntry[0] as string;

  // Variable renames (template's PIN 2876 → user PIN)
  const varRenames: Record<string, string> = {
    Emergency2876TTS: `Emergency${pin}TTS`,
    Closure2876TTS:   `Closure${pin}TTS`,
  };

  // New UUIDs for every template activity
  const idRenames: Record<string, string> = {};
  for (const oldId of Object.keys(template.activities)) idRenames[oldId] = newUuid();

  // Find DNIS router in template
  const dnisRouterName = template.dnis_router_name || 'DNISEntry';
  const dnisOldId = Object.entries(template.activities).find(([, a]: any) => a?.name === dnisRouterName)?.[0];

  // Layout offset — anchor 800px left of downstream widget
  const targetWidget: any = srcWidgets[downstreamId];
  const offsetX = (targetWidget?.point?.x ?? 500) - 800;
  const offsetY = targetWidget?.point?.y ?? 500;
  const tplFirstWidget: any = Object.values(template.widgets ?? {})[0];
  const tplOriginX = tplFirstWidget?.point?.x ?? 0;

  const rewriteRefs = (node: any): any => {
    if (Array.isArray(node)) return node.map(rewriteRefs);
    if (node && typeof node === 'object') {
      if ('outputVariable' in node && typeof node.outputVariable === 'string' && varRenames[node.outputVariable]) {
        node.outputVariable = varRenames[node.outputVariable];
      }
      for (const k of Object.keys(node)) node[k] = rewriteRefs(node[k]);
      return node;
    }
    if (typeof node === 'string') {
      let s = node;
      for (const [oldV, newV] of Object.entries(varRenames)) s = s.split(`{{${oldV}}}`).join(`{{${newV}}}`);
      for (const [oldId, newId] of Object.entries(idRenames)) s = s.split(oldId).join(newId);
      return s;
    }
    return node;
  };

  // Clone template activities + widgets, swap IDs/vars, populate DNIS router
  const newActivities: any = {};
  const newWidgets: any = {};
  for (const [oldId, newId] of Object.entries(idRenames)) {
    const newAct = JSON.parse(JSON.stringify(template.activities[oldId]));
    newAct.id = newId;
    rewriteRefs(newAct);
    if (oldId === dnisOldId) {
      newAct.properties = newAct.properties || {};
      newAct.properties.menuLinks = ['0'];
      newAct.properties.menuLinks_input = [...dnisValues];
      newAct.properties['menuLinks:input'] = [...dnisValues];
    }
    newActivities[newId] = newAct;
    const oldWidget = template.widgets?.[oldId];
    if (oldWidget) {
      const newW = JSON.parse(JSON.stringify(oldWidget));
      newW.id = newId;
      if (newW.point && typeof newW.point === 'object') {
        const origX = newW.point.x ?? 0;
        newW.point.x = offsetX + (origX - tplOriginX);
        newW.point.y = offsetY + ((newW.point.y ?? 500) - 500);
      }
      rewriteRefs(newW);
      newWidgets[newId] = newW;
    }
  }

  // Clone template's internal links
  const newLinks: any[] = [];
  for (const l of (template.links ?? [])) {
    const nl = JSON.parse(JSON.stringify(l));
    nl.id = newUuid();
    nl.sourceActivityId = idRenames[nl.sourceActivityId];
    nl.targetActivityId = idRenames[nl.targetActivityId];
    newLinks.push(nl);
  }

  // Find chain entry + exit IDs in renamed space
  const findTplIdByName = (name: string) => Object.entries(template.activities).find(([, a]: any) => a?.name === name)?.[0];
  const entryNewId       = idRenames[findTplIdByName(template.chain_entry_name)!];
  const dnisMatchNewId   = idRenames[findTplIdByName(template.dnis_match_target || 'HTTPRequest_ohf_hr3_u33')!];
  const emergPlayNewId   = idRenames[findTplIdByName('EmergencyPlay')!];
  const emergCondNewId   = idRenames[findTplIdByName('EmergencyStatus_Condition')!];

  // DNIS router exits
  newLinks.push({ id: newUuid(), sourceActivityId: entryNewId, targetActivityId: dnisMatchNewId, conditionExpr: '0', properties: { value: 'matched' } });
  newLinks.push({ id: newUuid(), sourceActivityId: entryNewId, targetActivityId: downstreamId,   conditionExpr: 'default', properties: { value: 'default' } });
  newLinks.push({ id: newUuid(), sourceActivityId: entryNewId, targetActivityId: downstreamId,   conditionExpr: 'error',   properties: { value: 'error' } });
  // Chain exits → downstream
  newLinks.push({ id: newUuid(), sourceActivityId: emergPlayNewId, targetActivityId: downstreamId, conditionExpr: 'default', properties: { value: 'default' } });
  newLinks.push({ id: newUuid(), sourceActivityId: emergCondNewId, targetActivityId: downstreamId, conditionExpr: '1',        properties: { value: 'false' } });
  newLinks.push({ id: newUuid(), sourceActivityId: emergCondNewId, targetActivityId: downstreamId, conditionExpr: 'default',  properties: { value: 'default' } });

  // Reroute existing source links: anything going TO downstream now goes to entry
  let rerouted = 0;
  for (const l of srcLinks) {
    if (l.targetActivityId === downstreamId && !Object.values(idRenames).includes(l.sourceActivityId)) {
      l.targetActivityId = entryNewId;
      rerouted++;
    }
  }

  // Merge into src
  Object.assign(srcActivities, newActivities);
  Object.assign(srcWidgets, newWidgets);
  srcLinks.push(...newLinks);

  // Variables — add if missing
  src.variables = src.variables || [];
  const addVar = (name: string, type: string, value: any, description = '') => {
    if (src.variables.some((v: any) => v?.name === name)) return false;
    src.variables.push({
      name, type, value, description,
      inputType: type === 'STRING' ? 'Plain Text' : 'Boolean',
      isSecure: false, isExternal: false, isCallVariable: true,
      callScope: 'GLOBAL', isAgentViewable: false, isAgentEditable: false,
    });
    return true;
  };
  const added: string[] = [];
  if (addVar(`Emergency${pin}TTS`, 'STRING', null, 'Emergency message TTS — populated by HTTPRequest')) added.push(`Emergency${pin}TTS`);
  if (addVar(`Closure${pin}TTS`,   'STRING', null, 'Closure message TTS — populated by HTTPRequest')) added.push(`Closure${pin}TTS`);
  for (const v of (template.shared_vars ?? [])) {
    let val = v.value;
    if (v.name === 'This_Flow_PIN') val = pin;
    else if (v.name === 'Emergency_Worker_URL') val = workerUrl;
    else if (v.name === 'Emergency_API_Secret') val = apiSecret;
    if (addVar(v.name, v.type, val, v.description ?? '')) added.push(v.name);
  }

  return {
    insertedActivities: Object.keys(newActivities).length,
    insertedLinks: newLinks.length,
    reroutedLinks: rerouted,
    addedVariables: added,
  };
}

// ---------------------------------------------------------------------------
// SpokeStatusBanner — renders above the sub-nav on on-prem / hybrid modules
// to tell the user what's needed for live data to flow.
//
//   onprem  (CUCM, UCCX): spoke is REQUIRED. Show prominent CTA if absent.
//   hybrid  (CUBE/LGW)  : LGW boundary is reachable via WxC. Spoke is only
//                         needed for device-level features (peers/codecs).
// ---------------------------------------------------------------------------
function SpokeStatusBanner({ reachability, health, tunnel, moduleTitle, tenantId }: {
  reachability: Reachability;
  health: SpokeHealth;
  tunnel: TunnelHealth;
  moduleTitle: string;
  tenantId: string;
}) {
  if (reachability === 'cloud' || health.state === 'unknown') return null;

  // Tunnel-first tenants: when the device data is flowing over the Long Beach
  // Cloudflare Tunnel, a missing spoke is EXPECTED, not an error. Show a
  // tunnel-connected banner instead of the misleading "spoke required" CTA.
  // (Only overrides the absent/stale spoke states; a registered+healthy spoke
  // still gets its own banner below.)
  if (tunnel.state === 'up' && (health.state === 'absent' || health.state === 'stale' || health.state === 'error')) {
    const c = tunnel.connector;
    return (
      <div className="card p-3 border-emerald-300 bg-emerald-50/40 dark:bg-emerald-900/10 text-sm flex items-center justify-between gap-2 flex-wrap">
        <span className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          {moduleTitle} is reachable over the Long Beach tunnel
          {c.connector ? ` (${c.connector})` : ''}.
          Live data is read on-demand via this tunnel — no spoke container required for this tenant.
        </span>
        <Link to="/troubleshooting" className="text-tnh-accent text-xs hover:underline whitespace-nowrap">Tunnel health →</Link>
      </div>
    );
  }

  // API-call failure — surface this distinctly so we don't blame the user
  // for a missing spoke when actually the spokesList API itself errored.
  if (health.state === 'error') {
    return (
      <div className="card p-4 border-amber-300 bg-amber-50/60 dark:bg-amber-900/15">
        <div className="font-medium text-amber-900 dark:text-amber-200">
          Couldn't check spoke status for {moduleTitle}
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
          The /api/spokes call failed: <code className="font-mono text-xs">{health.message}</code>.
          Open F12 → Console for full details. If you have a spoke registered, it may still be
          pushing data even though this banner can't confirm.
        </p>
      </div>
    );
  }

  // CUBE/LGW: hybrid. LGW config flows via WxC; CUBE internals need spoke.
  if (reachability === 'hybrid') {
    if (health.state === 'absent') {
      return (
        <div className="card p-4 border-sky-300 bg-sky-50/60 dark:bg-sky-900/15">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[20rem]">
              <div className="font-medium text-sky-900 dark:text-sky-200">
                {moduleTitle}: Local Gateway is managed through Webex Calling
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                LGW boundary configuration (premises connection, gateway endpoints, location
                associations) is reachable through Webex Calling cloud APIs — no spoke required for
                that data. Device-level CUBE features (dial peers, codec profiles, active calls,
                IOS XE logs) need a TriNetHub Spoke on your CUBE/LGW network OR direct device access.
              </p>
            </div>
            <Link to="/spokes" className="btn-primary text-sm whitespace-nowrap">Install a Spoke →</Link>
          </div>
        </div>
      );
    }
    if (health.state === 'stale') {
      return (
        <div className="card p-3 border-amber-300 bg-amber-50/60 dark:bg-amber-900/15 text-sm flex items-center justify-between gap-3 flex-wrap">
          <span className="text-amber-900 dark:text-amber-200">
            Spoke <code className="font-mono text-xs">{health.spoke.spoke_id}</code> is registered but hasn't heartbeated in {health.minsAgo} min.
            Device-level CUBE features may be stale. LGW boundary data via Webex Calling is unaffected.
          </span>
          <Link to="/spokes" className="text-tnh-accent text-xs hover:underline whitespace-nowrap">Open Spokes manager →</Link>
        </div>
      );
    }
    // healthy
    return (
      <div className="card p-3 border-emerald-300 bg-emerald-50/40 dark:bg-emerald-900/10 text-sm flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        <span className="text-gray-700 dark:text-gray-300">
          Spoke <code className="font-mono text-xs">{health.spoke.spoke_id}</code> connected. CUBE/LGW data sync is active.
        </span>
      </div>
    );
  }

  // CUCM / UCCX: truly on-prem. Without a spoke, nothing works.
  if (health.state === 'absent') {
    return (
      <div className="card p-5 border-rose-300 bg-rose-50/60 dark:bg-rose-900/15">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[20rem]">
            <div className="font-semibold text-rose-900 dark:text-rose-200">
              A TriNetHub Spoke is required for {moduleTitle}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {moduleTitle} uses LAN-only protocols (AXL / RisPort70 / REST / Informix) that aren't reachable from the
              cloud. The TriNetHub Spoke is a small container you run on your customer network — it authenticates back
              to TriNetHub via mTLS and proxies these device APIs into the hub.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Tenant: <code className="font-mono">{tenantId}</code> · Status: <span className="text-rose-700 dark:text-rose-300 font-medium">no spoke registered</span>
            </p>
          </div>
          <Link to="/spokes" className="btn-primary text-sm whitespace-nowrap">Generate install link →</Link>
        </div>
      </div>
    );
  }
  if (health.state === 'stale') {
    return (
      <div className="card p-4 border-amber-300 bg-amber-50/60 dark:bg-amber-900/15">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[20rem]">
            <div className="font-medium text-amber-900 dark:text-amber-200">
              Spoke registered but unreachable
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Spoke <code className="font-mono text-xs">{health.spoke.spoke_id}</code> hasn't heartbeated in {health.minsAgo} min.
              No fresh {moduleTitle} data will arrive until the spoke container reconnects.
            </p>
            <p className="text-xs text-gray-500 mt-2">
              Check the spoke container logs on your CUCM admin host. Common causes: container stopped, mTLS cert
              expired, outbound 443 blocked.
            </p>
          </div>
          <Link to="/spokes" className="btn-primary text-sm whitespace-nowrap">Open Spokes manager →</Link>
        </div>
      </div>
    );
  }
  // healthy
  return (
    <div className="card p-3 border-emerald-300 bg-emerald-50/40 dark:bg-emerald-900/10 text-sm flex items-center justify-between gap-2 flex-wrap">
      <span className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        Spoke <code className="font-mono text-xs">{health.spoke.spoke_id}</code> connected and heartbeating.
        Awaiting first sync of {moduleTitle} data from the spoke.
      </span>
      <Link to="/spokes" className="text-tnh-accent text-xs hover:underline whitespace-nowrap">Spokes manager →</Link>
    </div>
  );
}

// ===========================================================================
// LgwCertPanel — CUBE/LGW → TLS Certificates sub-tab.
//
// Phase 1 of the CUBE/LGW production module: certificate monitor + manual
// renewal. Backed by lgw-svc (/api/lgw/*) and cert-monitor-svc (6h expiry
// scan). Everything here is a real action — no decorative skeletons:
//   • Live cert inventory with color-coded expiry countdown
//   • Add Gateway (you need a gateway before you can attach a cert)
//   • Upload Certificate (paste PEM → server parses → preview → stored)
//   • Per-row Renew (paste replacement PEM → stage → preview diff → install)
//   • Per-row Download (cert + chain PEM)
//   • Renewal history
// ===========================================================================
function LgwCertPanel() {
  const [gateways, setGateways] = useState<LgwGateway[] | null>(null);
  const [certs, setCerts] = useState<LgwCert[] | null>(null);
  const [renewals, setRenewals] = useState<LgwRenewal[] | null>(null);
  // Webex Calling premise-PSTN trunks — pairs with lgw_gateways by hostname so
  // the per-LGW panel can show the cloud-side outboundProxy that the LGW must
  // present in its TLS handshake.
  const [wxcTrunks, setWxcTrunks] = useState<WxcLocalGateway[] | null>(null);
  // Webex trunks with their on-box cert expiry (cert_state.cert_expires_at) so the
  // KPI tiles count on-box device certs, not just manually-uploaded ones.
  const [trunkCerts, setTrunkCerts] = useState<LgwTrunk[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  // collapsible forms
  const [showAddGw, setShowAddGw] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [renewCertId, setRenewCertId] = useState<string | null>(null);
  // Per-LGW selection — drives the per-LGW section above the inventory.
  const [selectedGwId, setSelectedGwId] = useState<string>('');

  const load = async () => {
    setLoading(true); setError(null);
    try {
      // wxcLocalGateways is best-effort (a tenant may have no Webex creds yet)
      const [g, c, r, w, tk] = await Promise.all([
        api.lgwGateways(),
        api.lgwCerts(),
        api.lgwRenewals(),
        api.wxcLocalGateways().catch(() => ({ local_gateways: [], total: 0 } as any)),
        api.lgwTrunks().catch(() => ({ items: [] } as any)),
      ]);
      setGateways(g.gateways); setCerts(c.certs); setRenewals(r.renewals);
      setWxcTrunks(w.local_gateways ?? []);
      setTrunkCerts(tk.items ?? []);
      // pre-select the first gateway if nothing chosen yet
      if (!selectedGwId && g.gateways?.[0]) setSelectedGwId(g.gateways[0].gateway_id);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load LGW certificate data');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = (certs ?? []).filter((c) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return [c.cn, c.issuer, c.gateway_name, c.purpose, c.status_live].some((v) => (v ?? '').toLowerCase().includes(q));
  });

  // expiry colour bands: >30d green · 7-30d amber · 1-7d red · <=0/missing rose
  const expiryClass = (days: number | null, status: string): string => {
    if (status === 'expired' || status === 'missing' || (days != null && days <= 0)) return 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200';
    if (days == null) return 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
    if (days <= 7)  return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
    if (days <= 30) return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
  };
  const fmtDate = (epochSec: number) => epochSec ? new Date(epochSec * 1000).toISOString().slice(0, 10) : '—';

  // KPI tiles count BOTH manually-uploaded gateway certs (lgw_certs, with
  // days_until_expiry) AND on-box trunk certs (cert_state.cert_expires_at, epoch
  // ms, from the Netmiko ingest joined by FQDN). Derive a single list of
  // days-until-expiry across both sources.
  const _nowMs = Date.now();
  const manualDays = (certs ?? [])
    .map((c) => c.days_until_expiry)
    .filter((d): d is number => d != null);
  const trunkDays = (trunkCerts ?? [])
    .map((t) => t.cert_state?.cert_expires_at)
    .filter((ms): ms is number => ms != null)
    .map((ms) => Math.floor((ms - _nowMs) / 86400000));
  const allDays = [...manualDays, ...trunkDays];
  const counts = {
    total: (certs ?? []).length + trunkDays.length,
    healthy: allDays.filter((d) => d > 30).length,
    expiring: allDays.filter((d) => d > 7 && d <= 30).length,
    critical: allDays.filter((d) => d > 0 && d <= 7).length,
    expired: allDays.filter((d) => d <= 0).length,
  };

  return (
    <div className="space-y-4">
      {/* BUILD-TASK-admin-oauth-unlocked-features Section C — Webex Calling
          trunk cert lifecycle, driven by the admin OAuth token. Lives above
          the legacy per-LGW upload tracker so admins see the integrated
          CSR-fetch path first. */}
      <AdminTrunkCertsPanel />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          { label: 'Total certs', val: counts.total, cls: 'text-gray-700 dark:text-gray-200' },
          { label: 'Healthy (>30d)', val: counts.healthy, cls: 'text-emerald-600 dark:text-emerald-300' },
          { label: 'Expiring (7-30d)', val: counts.expiring, cls: 'text-amber-600 dark:text-amber-300' },
          { label: 'Critical (≤7d)', val: counts.critical, cls: 'text-red-600 dark:text-red-300' },
          { label: 'Expired', val: counts.expired, cls: 'text-rose-600 dark:text-rose-300' },
        ].map((k) => (
          <div key={k.label} className="card p-3">
            <div className={`text-2xl font-semibold ${k.cls}`}>{loading ? '—' : k.val}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Honest-state banner — Webex cannot auto-discover trunk certs, so this
          tab is a MANUAL UPLOAD TRACKER. Probe-verified: 0/12 candidate Webex
          per-trunk certificate endpoints exist (see BUILD-TASK-lgw-certs).
          For the LIVE on-box certs (real notAfter pulled from each IOS-XE
          device), point the user at the CUBE module's TLS Certificates tab. */}
      <div className="rounded-lg border border-amber-300 bg-amber-50/70 dark:bg-amber-900/15 p-3 text-xs">
        <div className="font-semibold text-amber-800 dark:text-amber-200 mb-0.5">
          Manual-upload tracker (cloud API can't list trunk certs)
        </div>
        <div className="text-gray-700 dark:text-gray-200">
          The Webex Calling Service App API does NOT expose per-trunk certificate data (probe-verified —
          every candidate /v1/telephony/config/premisePstn/trunks/&#123;id&#125;/certificate variant 404s). This
          tab stays a manual tracker: upload a PEM here to record its expiry in the hub. For{' '}
          <strong>real on-box certificates</strong> read live from each IOS-XE device (notAfter parsed off
          the device by the cube-svc spoke), open the <strong>CUBE</strong> module → <em>TLS Certificates</em>.
        </div>
      </div>

      {/* Action bar */}
      <div className="card p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-base font-semibold">TLS Certificate Monitor — manual upload</div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-3xl">
              Tracks every SIP-TLS / mTLS certificate you upload here for your CUBE and Local Gateway devices.
              <code className="font-mono text-xs mx-1">cert-monitor-svc</code> re-scans expiry every 6 hours and
              raises notifications at 30-day, 7-day, and expiry thresholds. Renewals are manual upload
              (paste the replacement PEM — the server parses, previews the diff, then promotes it).
            </p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button onClick={() => { setShowAddGw(v => !v); setShowUpload(false); }}
            className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-900/30">
            {showAddGw ? '× Cancel' : '+ Add Gateway'}
          </button>
          <button onClick={() => { setShowUpload(v => !v); setShowAddGw(false); }}
            disabled={!gateways || gateways.length === 0}
            className="px-3 py-1.5 rounded-lg bg-cisco-600 hover:bg-cisco-500 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium disabled:opacity-50"
            title={gateways && gateways.length === 0 ? 'Add a gateway first' : 'Upload a new certificate'}>
            {showUpload ? '× Cancel' : '⬆ Upload Certificate'}
          </button>
          <button onClick={load} disabled={loading}
            className="btn-ghost text-sm disabled:opacity-50">↻ Refresh</button>
          <input value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by CN / issuer / gateway…"
            className="ml-auto px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm w-72" />
        </div>

        {showAddGw && <AddGatewayForm gateways={gateways ?? []} onDone={() => { setShowAddGw(false); load(); }} />}
        {showUpload && <UploadCertForm gateways={gateways ?? []} wxcTrunks={wxcTrunks ?? []} onDone={() => { setShowUpload(false); load(); }} />}
      </div>

      {/* Per-LGW certificate management — gateway picker + Webex limitation
          banner + scoped upload-with-validate-match. This is the BUILD-TASK-
          lgw-certs §2 deliverable in its "honest fallback" form (probe
          confirmed: Webex Calling does NOT expose per-trunk CSR/cert
          management to the Service App scope). */}
      <PerLgwCertSection
        gateways={gateways ?? []}
        wxcTrunks={wxcTrunks ?? []}
        selectedGwId={selectedGwId}
        onSelectGw={setSelectedGwId}
        onUploaded={load}
      />

      {/* Cert inventory */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-tnh-border dark:border-tnh-border-dark flex items-center gap-3 flex-wrap">
          <div className="font-semibold">Certificate Inventory</div>
          <span className="text-xs text-gray-500">
            {loading ? 'Loading…' : `${filtered.length}${filter ? ` of ${certs?.length ?? 0}` : ''} certificates`}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
              <tr>
                {['CN', 'Gateway', 'Issuer', 'Expires', 'Status', 'Actions'].map((c) => (
                  <th key={c} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500">Loading certificate inventory…</td></tr>}
              {error && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-rose-600">{error}</td></tr>}
              {!loading && !error && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500">
                  {certs && certs.length === 0
                    ? 'No certificates tracked yet. Add a gateway, then upload its TLS certificate.'
                    : 'No certificates match the filter.'}
                </td></tr>
              )}
              {!loading && !error && filtered.map((c) => (
                <Fragment key={c.cert_id}>
                  <tr className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/30">
                    <td className="px-4 py-2.5">
                      <div className="font-mono text-xs">{c.cn || '(no CN)'}</div>
                      <div className="text-[10px] text-gray-500">{c.purpose} · {c.key_alg}</div>
                    </td>
                    <td className="px-4 py-2.5 text-xs">{c.gateway_name || (c.gateway_id ?? '').slice(0, 8) || '—'}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-gray-300">{(c.issuer || '').replace(/,.*/, '') || '—'}</td>
                    <td className="px-4 py-2.5 text-xs whitespace-nowrap">{fmtDate(c.not_after)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${expiryClass(c.days_until_expiry, c.status_live)}`}>
                        {c.days_until_expiry == null ? c.status_live
                          : c.days_until_expiry <= 0 ? `expired ${-c.days_until_expiry}d ago`
                          : `${c.days_until_expiry}d left`}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs whitespace-nowrap">
                      <button onClick={() => setRenewCertId(renewCertId === c.cert_id ? null : c.cert_id)}
                        className="px-2.5 py-1 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-medium mr-1.5">
                        {renewCertId === c.cert_id ? '× Close' : '↻ Renew'}
                      </button>
                      <a href={api.lgwCertDownloadUrl(c.cert_id)}
                        className="px-2.5 py-1 rounded-md border border-tnh-border dark:border-tnh-border-dark text-[11px] font-medium hover:bg-gray-100 dark:hover:bg-gray-800">
                        ⬇ PEM
                      </a>
                    </td>
                  </tr>
                  {renewCertId === c.cert_id && (
                    <tr>
                      <td colSpan={6} className="px-4 py-0">
                        <RenewCertForm cert={c} onDone={() => { setRenewCertId(null); load(); }} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Renewal history */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-tnh-border dark:border-tnh-border-dark font-semibold">
          Renewal History
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
              <tr>
                {['When', 'Method', 'By', 'Status', 'New expiry', 'Diff'].map((c) => (
                  <th key={c} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(!renewals || renewals.length === 0) && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">No renewals recorded yet.</td></tr>
              )}
              {(renewals ?? []).map((r) => (
                <tr key={r.renewal_id} className="border-t border-tnh-border dark:border-tnh-border-dark">
                  <td className="px-4 py-2 text-xs">{new Date(r.initiated_at).toISOString().replace('T', ' ').slice(0, 16)}</td>
                  <td className="px-4 py-2 text-xs">{r.method}</td>
                  <td className="px-4 py-2 text-xs">{r.actor_email}</td>
                  <td className="px-4 py-2 text-xs">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] ${
                      r.status === 'succeeded' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                      : r.status === 'failed' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
                      : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-2 text-xs">{r.new_not_after ? new Date(r.new_not_after * 1000).toISOString().slice(0, 10) : '—'}</td>
                  <td className="px-4 py-2 text-[11px] text-gray-600 dark:text-gray-400">{r.diff_summary || r.error_detail || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Add Gateway — minimal form (name + host required, rest defaulted)
function AddGatewayForm({ gateways, onDone }: { gateways: LgwGateway[]; onDone: () => void }) {
  const [name, setName] = useState('');
  const [host, setHost] = useState('');
  const [role, setRole] = useState('primary');
  const [mode, setMode] = useState('cube');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      if (!name.trim() || !host.trim()) throw new Error('Name and host are required');
      if (gateways.some((g) => g.name === name.trim())) throw new Error(`A gateway named "${name.trim()}" already exists`);
      await api.lgwCreateGateway({ name: name.trim(), host: host.trim(), role: role as any, lgw_mode: mode as any });
      onDone();
    } catch (e: any) {
      setError(e?.message ?? 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 p-3 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-gray-50 dark:bg-gray-900/30">
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">New Gateway</div>
      <div className="grid md:grid-cols-4 gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (e.g. cube-lb-pri-01)"
          className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        <input value={host} onChange={(e) => setHost(e.target.value)} placeholder="Host / FQDN / IP"
          className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        <select value={role} onChange={(e) => setRole(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
          <option value="primary">primary</option><option value="secondary">secondary</option>
          <option value="dr">dr</option><option value="test">test</option>
        </select>
        <select value={mode} onChange={(e) => setMode(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
          <option value="cube">cube</option><option value="wxc_calling">wxc_calling</option><option value="hybrid">hybrid</option>
        </select>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <button onClick={submit} disabled={busy}
          className="px-4 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium disabled:opacity-50">
          {busy ? 'Creating…' : 'Create Gateway'}
        </button>
        {error && <span className="text-sm text-rose-600 dark:text-rose-400">⚠ {error}</span>}
      </div>
    </div>
  );
}

// PerLgwCertSection — BUILD-TASK-lgw-certs §2 honest-fallback delivery.
// Probe-confirmed: Webex Calling does NOT expose per-trunk certificate /
// CSR management to the Service App scope (every candidate /v1/telephony/
// config/premisePstn/trunks/{id}/csr|certificate|...|tlsCertificate|identity
// returned 404 "No static resource hydra/..."). So this section gives the
// admin per-LGW manual cert upload with hostname validate-match against the
// Webex outbound proxy, makes the limitation explicit, and points at the
// IOS-XE PKI commands that DO work (on the LGW itself).
function PerLgwCertSection({ gateways, wxcTrunks, selectedGwId, onSelectGw, onUploaded }:
  { gateways: LgwGateway[]; wxcTrunks: WxcLocalGateway[]; selectedGwId: string;
    onSelectGw: (id: string) => void; onUploaded: () => void }) {
  const [showWhy, setShowWhy] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const selectedGw = gateways.find((g) => g.gateway_id === selectedGwId) || null;
  // Pair the selected LGW with a Webex trunk by host match (substring) or
  // name match — same heuristic the UploadCertForm uses. Best-effort.
  const matchedTrunk = selectedGw
    ? (wxcTrunks ?? []).find((t) =>
        (t.outbound_proxy ?? '').toLowerCase().includes((selectedGw.host ?? '').toLowerCase()) ||
        (t.name ?? '').toLowerCase() === (selectedGw.name ?? '').toLowerCase())
    : null;

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-base font-semibold">Per-LGW certificate management</div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-3xl">
            Pick a Local Gateway, then upload the CA-signed certificate that the LGW will present in its TLS handshake
            to Webex Calling. TriNetHub <strong>validates</strong> the cert's CN/SAN matches the expected hostname
            before storing, then <code className="font-mono text-xs">cert-monitor-svc</code> tracks it for expiry.
          </p>
        </div>
      </div>

      {/* Honest-fallback banner — Webex Calling cert API not exposed */}
      <div className="rounded-lg border border-amber-300 bg-amber-50/70 dark:bg-amber-900/15 p-3 text-xs">
        <div className="flex items-start gap-2">
          <span className="text-amber-700 dark:text-amber-300 font-semibold whitespace-nowrap">⚠ Webex CSR/upload — not exposed</span>
          <span className="text-gray-700 dark:text-gray-200">
            Pulling a CSR from Webex Calling and pushing a signed cert back through the Webex API would be the ideal
            cloud-driven flow, but the Service App scope does not expose those endpoints — the cert lives on the LGW
            device itself. Generate the CSR on the LGW (IOS-XE PKI), get it signed, install it on the LGW, then
            upload a copy here for inventory + expiry tracking.
          </span>
        </div>
        <button onClick={() => setShowWhy((v) => !v)} className="mt-1.5 text-amber-700 dark:text-amber-300 hover:underline text-[11px]">
          {showWhy ? 'Hide probe evidence' : 'Show probe evidence (12 Webex endpoints probed)'}
        </button>
        {showWhy && (
          <pre className="mt-2 p-2 rounded bg-gray-900/5 dark:bg-gray-900/40 text-[10px] font-mono whitespace-pre-wrap leading-snug">
{`Probe (live, against the LongBeach Webex Calling org):
  GET  /v1/telephony/config/premisePstn/trunks/{id}/csr                  → 404
  POST /v1/telephony/config/premisePstn/trunks/{id}/csr/generate         → 404
  POST /v1/telephony/config/premisePstn/trunks/{id}/csr                  → 404
  GET  /v1/telephony/config/premisePstn/trunks/{id}/certificate          → 404
  GET  /v1/telephony/config/premisePstn/trunks/{id}/certificates         → 404
  GET  /v1/telephony/config/premisePstn/trunks/{id}/tlsCertificate       → 404
  GET  /v1/telephony/config/premisePstn/trunks/{id}/tls                  → 404
  GET  /v1/telephony/config/premisePstn/trunks/{id}/identity             → 404
  GET  /v1/telephony/config/premisePstn/certificates                     → 404
  GET  /v1/telephony/config/certificates                                 → 404
  GET  /v1/telephony/config/premisePstn/certificateAuthorities           → 404
All 12 returned: "No static resource hydra/v1/telephony/config/..." — Webex
Calling does not surface a per-trunk certificate API to Service App scopes.
TriNetHub therefore tracks the cert (CN, SAN, expiry, fingerprint) only — the
cert itself lives on the LGW and is renewed via IOS-XE PKI commands.`}
          </pre>
        )}
      </div>

      {/* Gateway picker */}
      {gateways.length === 0 ? (
        <div className="text-sm text-gray-500">
          No gateways yet. Use <strong>+ Add Gateway</strong> above to register your first Local Gateway, then return here.
        </div>
      ) : (
        <>
          <div className="grid gap-3 md:grid-cols-[1fr_auto] items-end">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Select Local Gateway</span>
              <select value={selectedGwId} onChange={(e) => onSelectGw(e.target.value)}
                className="block w-full px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
                {gateways.map((g) => (
                  <option key={g.gateway_id} value={g.gateway_id}>
                    {g.name} — {g.host}{g.role ? ` (${g.role})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-2">
              <button disabled
                title="Not available — Webex Calling does not expose CSR generation to Service Apps (probe-verified). Generate the CSR on the LGW via 'crypto pki enroll' on IOS-XE."
                className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-400 text-sm font-medium cursor-not-allowed">
                Pull CSR from Webex (unavailable)
              </button>
              <button onClick={() => setShowUpload((v) => !v)}
                disabled={!selectedGw}
                className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium disabled:opacity-50">
                {showUpload ? '× Close' : '⬆ Upload signed cert for this LGW'}
              </button>
            </div>
          </div>

          {/* Context strip — host + matched Webex trunk's outboundProxy */}
          {selectedGw && (
            <div className="text-[11px] text-gray-600 dark:text-gray-300 grid sm:grid-cols-3 gap-x-4 gap-y-1">
              <div><span className="text-gray-500">LGW host:</span> <code className="font-mono">{selectedGw.host || '—'}</code></div>
              <div><span className="text-gray-500">LGW mode:</span> {selectedGw.lgw_mode || '—'}</div>
              <div>
                <span className="text-gray-500">Webex outbound proxy:</span>{' '}
                {matchedTrunk
                  ? <code className="font-mono text-emerald-700 dark:text-emerald-300">{matchedTrunk.outbound_proxy || '(none configured)'}</code>
                  : <span className="text-gray-400">no Webex trunk matched by host/name</span>}
              </div>
            </div>
          )}

          {showUpload && selectedGw && (
            <UploadCertForm
              gateways={gateways}
              wxcTrunks={wxcTrunks}
              prefilledGatewayId={selectedGw.gateway_id}
              onDone={() => { setShowUpload(false); onUploaded(); }}
            />
          )}
        </>
      )}
    </div>
  );
}

// Upload Certificate — pick gateway, paste PEM, validate-match (cert CN/SAN
// vs gateway hostname / Webex outbound proxy) BEFORE storing. The validate
// step is the honest substitute for the BUILD-TASK §2 "validate matches the
// CSR" check — Webex does not expose a CSR pull to the Service App (probe-
// verified), so hostname-match is the most checkable invariant we have.
function UploadCertForm({ gateways, wxcTrunks, onDone, prefilledGatewayId }:
  { gateways: LgwGateway[]; wxcTrunks: WxcLocalGateway[]; onDone: () => void; prefilledGatewayId?: string }) {
  const [gatewayId, setGatewayId] = useState(prefilledGatewayId || gateways[0]?.gateway_id || '');
  const [pem, setPem] = useState('');
  const [purpose, setPurpose] = useState('sip-tls');
  const [notes, setNotes] = useState('');
  // expected hostname defaults to the LGW's host (or Webex outboundProxy if
  // we can pair the LGW with a Webex trunk). Editable in the form.
  const [expectedHostname, setExpectedHostname] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any | null>(null);
  const [validateResult, setValidateResult] = useState<LgwCertValidateResponse | null>(null);
  const [forceMismatch, setForceMismatch] = useState(false);

  // When the gateway changes, pre-fill expectedHostname from the LGW host, then
  // override with the Webex trunk's outboundProxy if a match is found.
  useEffect(() => {
    const gw = gateways.find((g) => g.gateway_id === gatewayId);
    if (!gw) { setExpectedHostname(''); return; }
    const trunkMatch = (wxcTrunks ?? []).find((t) =>
      (t.outbound_proxy ?? '').toLowerCase().includes((gw.host ?? '').toLowerCase()) ||
      (t.name ?? '').toLowerCase() === (gw.name ?? '').toLowerCase()
    );
    setExpectedHostname(trunkMatch?.outbound_proxy || gw.host || '');
    setValidateResult(null); setForceMismatch(false); setResult(null); setError(null);
  }, [gatewayId, gateways, wxcTrunks]);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) { setPem(await f.text()); setValidateResult(null); setForceMismatch(false); }
  };

  const validate = async () => {
    setBusy(true); setError(null); setValidateResult(null); setResult(null);
    try {
      if (!gatewayId) throw new Error('Select a gateway');
      if (!pem.trim()) throw new Error('Paste or upload a PEM certificate');
      const r = await api.lgwCertValidate(gatewayId, pem, expectedHostname || undefined);
      setValidateResult(r);
    } catch (e: any) {
      setError(e?.message ?? 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      if (!gatewayId) throw new Error('Select a gateway');
      if (!pem.trim()) throw new Error('Paste or upload a PEM certificate');
      const r = await api.lgwCertUpload(gatewayId, pem, purpose, notes);
      setResult(r.parsed);
      setTimeout(onDone, 1500);
    } catch (e: any) {
      setError(e?.message ?? 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 p-3 rounded-lg border border-indigo-300 bg-indigo-50/40 dark:bg-indigo-900/15">
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Upload Certificate</div>
      <div className="grid md:grid-cols-2 gap-2">
        <select value={gatewayId} onChange={(e) => setGatewayId(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
          {gateways.map((g) => <option key={g.gateway_id} value={g.gateway_id}>{g.name} ({g.host})</option>)}
        </select>
        <select value={purpose} onChange={(e) => setPurpose(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
          <option value="sip-tls">sip-tls</option><option value="mtls-trunk">mtls-trunk</option>
          <option value="webex-trunk">webex-trunk</option><option value="admin-mgmt">admin-mgmt</option><option value="other">other</option>
        </select>
      </div>
      <input type="file" accept=".pem,.crt,.cer,.txt" onChange={onFile}
        className="block mt-2 text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-indigo-600 file:text-white file:font-medium file:cursor-pointer" />
      <textarea value={pem} onChange={(e) => setPem(e.target.value)} rows={5}
        placeholder="-----BEGIN CERTIFICATE-----&#10;… or use the file picker above"
        className="block w-full mt-2 px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs font-mono" />
      <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)"
        className="block w-full mt-2 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
      {/* Expected-hostname validate-match input */}
      <div className="mt-2">
        <label className="block text-[11px] text-gray-500 mb-1">
          Expected hostname (LGW outbound proxy / FQDN the cert's CN or SAN must match):
        </label>
        <input value={expectedHostname} onChange={(e) => { setExpectedHostname(e.target.value); setValidateResult(null); setForceMismatch(false); }}
          placeholder="e.g. cube-lb-pri-01.example.com"
          className="block w-full px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm font-mono" />
      </div>

      {/* Validate-match result panel */}
      {validateResult && (
        <div className={`mt-2 p-2.5 rounded-lg border text-xs ${
          validateResult.match.matches
            ? 'border-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/15'
            : 'border-rose-300 bg-rose-50/60 dark:bg-rose-900/15'
        }`}>
          <div className="flex items-start gap-2">
            <span className={`font-semibold text-sm ${validateResult.match.matches ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
              {validateResult.match.matches ? '✓ Matches' : '✗ Mismatch'}
            </span>
            <span className="text-gray-700 dark:text-gray-200">{validateResult.match.reason}</span>
          </div>
          <div className="mt-1.5 text-[11px] text-gray-600 dark:text-gray-300">
            <div><strong>CN:</strong> <code className="font-mono">{validateResult.parsed.cn || '—'}</code></div>
            <div><strong>SAN:</strong> <code className="font-mono">{(validateResult.parsed.san ?? []).join(', ') || '—'}</code></div>
            <div><strong>Issuer:</strong> {validateResult.parsed.issuer || '—'}</div>
            <div><strong>Expires:</strong> {new Date(validateResult.parsed.not_after * 1000).toISOString().slice(0,10)} ({validateResult.parsed.days_until_expiry}d) · {validateResult.parsed.key_alg}</div>
            <div className="break-all"><strong>Fingerprint:</strong> <code className="font-mono">{validateResult.parsed.fingerprint_sha256}</code></div>
          </div>
          {!validateResult.match.matches && (
            <label className="mt-2 flex items-center gap-2 text-[11px] text-rose-700 dark:text-rose-300">
              <input type="checkbox" checked={forceMismatch} onChange={(e) => setForceMismatch(e.target.checked)} />
              I understand the cert CN/SAN does not match the expected hostname — upload anyway (e.g. intentional SAN swap).
            </label>
          )}
        </div>
      )}

      <div className="mt-2 flex items-center gap-3 flex-wrap">
        <button onClick={validate} disabled={busy || !pem.trim()}
          className="px-3 py-1.5 rounded-lg border border-indigo-300 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 text-sm font-medium disabled:opacity-50">
          {busy && !validateResult ? 'Validating…' : '1. Validate match'}
        </button>
        <button onClick={submit} disabled={busy || !validateResult || (!validateResult.match.matches && !forceMismatch)}
          className="px-4 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium disabled:opacity-50"
          title={!validateResult ? 'Run "Validate match" first' : (!validateResult.match.matches && !forceMismatch) ? 'Acknowledge the mismatch first' : 'Parse + store the cert; cert-monitor-svc will track it'}>
          {busy && validateResult ? 'Storing…' : '2. Parse & Store Certificate'}
        </button>
        {error && <span className="text-sm text-rose-600 dark:text-rose-400">⚠ {error}</span>}
        {result && (
          <span className="text-sm text-emerald-700 dark:text-emerald-300">
            ✓ {result.cn} · expires {new Date(result.not_after * 1000).toISOString().slice(0,10)} ({result.days_until_expiry}d) · {result.key_alg}
          </span>
        )}
      </div>
      <div className="mt-2 text-[11px] text-gray-500">
        TriNetHub tracks the cert (expiry, fingerprint, status) — the cert itself must be applied on the LGW device
        via IOS-XE PKI (<code className="font-mono">crypto pki trustpoint</code> + <code className="font-mono">crypto pki import</code>).
        Webex Calling does not expose CSR / cert upload for premise-PSTN trunks to Service Apps (probe-verified).
      </div>
    </div>
  );
}

// Renew Certificate — paste replacement PEM, stage → preview diff → install
function RenewCertForm({ cert, onDone }: { cert: LgwCert; onDone: () => void }) {
  const [pem, setPem] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [staged, setStaged] = useState<{ renewal_id: string; preview: any } | null>(null);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) setPem(await f.text());
  };

  const stage = async () => {
    setBusy(true); setError(null);
    try {
      if (!pem.trim()) throw new Error('Paste or upload the replacement PEM');
      const r = await api.lgwCertStageRenewal(cert.cert_id, pem);
      setStaged({ renewal_id: r.renewal_id, preview: r.preview });
    } catch (e: any) {
      setError(e?.message ?? 'Staging failed');
    } finally {
      setBusy(false);
    }
  };

  const install = async () => {
    if (!staged) return;
    setBusy(true); setError(null);
    try {
      await api.lgwCertInstallRenewal(cert.cert_id, staged.renewal_id);
      onDone();
    } catch (e: any) {
      setError(e?.message ?? 'Install failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="my-2 p-3 rounded-lg border border-indigo-300 bg-indigo-50/40 dark:bg-indigo-900/15">
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
        Renew certificate — {cert.cn}
      </div>
      <p className="text-[11px] text-gray-500 mb-2">
        Paste the replacement certificate PEM. The server parses it, shows the diff vs the
        live cert, then you click Install to promote it. The live cert is untouched until you install.
      </p>
      {!staged ? (
        <>
          <input type="file" accept=".pem,.crt,.cer,.txt" onChange={onFile}
            className="block text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-indigo-600 file:text-white file:font-medium file:cursor-pointer" />
          <textarea value={pem} onChange={(e) => setPem(e.target.value)} rows={4}
            placeholder="-----BEGIN CERTIFICATE-----"
            className="block w-full mt-2 px-3 py-2 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs font-mono" />
          <div className="mt-2 flex items-center gap-3">
            <button onClick={stage} disabled={busy}
              className="px-4 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium disabled:opacity-50">
              {busy ? 'Staging…' : 'Stage & Preview'}
            </button>
            {error && <span className="text-sm text-rose-600 dark:text-rose-400">⚠ {error}</span>}
          </div>
        </>
      ) : (
        <div>
          <div className="p-2 rounded-lg bg-white dark:bg-gray-900/40 border border-tnh-border dark:border-tnh-border-dark text-xs space-y-0.5">
            <div><strong>New CN:</strong> {staged.preview.cn}</div>
            <div><strong>New issuer:</strong> {(staged.preview.issuer || '').replace(/,.*/, '')}</div>
            <div><strong>Old expiry:</strong> {new Date(staged.preview.old_not_after * 1000).toISOString().slice(0,10)}
              {' → '}<strong>New expiry:</strong> {new Date(staged.preview.new_not_after * 1000).toISOString().slice(0,10)}</div>
            <div><strong>Diff:</strong> {staged.preview.diff_summary}</div>
          </div>
          <div className="mt-2 flex items-center gap-3">
            <button onClick={install} disabled={busy}
              className="px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium disabled:opacity-50">
              {busy ? 'Installing…' : '✓ Install this certificate'}
            </button>
            <button onClick={() => setStaged(null)} disabled={busy} className="btn-ghost text-sm">Back</button>
            {error && <span className="text-sm text-rose-600 dark:text-rose-400">⚠ {error}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// AdminTrunkCertsPanel — BUILD-TASK-admin-oauth-unlocked-features Section C.
// Webex Calling premise-PSTN trunks listed via the admin OAuth token, with
// per-row Fetch CSR + Upload Cert actions. Honest 401/403 surfacing — if
// Cisco gates the CSR endpoint, the row shows the tracking ID + hint and we
// do NOT pretend the operation succeeded.
// ===========================================================================
// Emit the exact IOS-XE commands to generate a fresh key pair + CSR on a
// Local Gateway, with CN/SAN pre-filled from the trunk's FQDN. Webex Calling
// cert-based trunks require the FQDN in the cert CN/SAN and a 2048-bit (or
// larger) key signed by a supported public CA (GoDaddy/DigiCert/etc.). The CSR
// is generated ON the device — Webex does not issue one — so this gives the
// operator the precise rekey commands to paste into each LGW.
function buildLgwCsrScript(fqdn: string): string {
  const year = new Date().getFullYear();
  const tp = `WxC-LGW-${year}`;
  return [
    `! ============================================================`,
    `!  Generate key + CSR on Local Gateway  —  ${fqdn}`,
    `!  Paste in IOS-XE config mode (conf t). Requires IOS-XE 17.x`,
    `!  for 'subject-alt-name'. Key + CSR are created on the device.`,
    `! ============================================================`,
    `crypto key generate rsa label ${tp} modulus 2048`,
    `!`,
    `crypto pki trustpoint ${tp}`,
    ` enrollment terminal pem`,
    ` fqdn ${fqdn}`,
    ` subject-name CN=${fqdn},O=City of Long Beach,L=Long Beach,ST=CA,C=US`,
    ` subject-alt-name ${fqdn}`,
    ` rsakeypair ${tp}`,
    ` hash sha256`,
    ` revocation-check none`,
    `!`,
    `! Produce the CSR (copy the BEGIN CERTIFICATE REQUEST block → submit to GoDaddy):`,
    `crypto pki enroll ${tp}`,
    `!`,
    `! After GoDaddy issues the cert, install the CA chain then the signed cert:`,
    `crypto pki authenticate ${tp}    ! paste root/intermediate PEM`,
    `crypto pki import ${tp} certificate    ! paste the issued device cert PEM`,
    `!`,
    `! Then point the cert-based trunk's tls profile / sip-ua at trustpoint ${tp},`,
    `! and run 'Upload & verify renewal' in TriNetHub to record the new expiry.`,
    `! ============================================================`,
  ].join('\n');
}

function AdminTrunkCertsPanel() {
  const [trunks, setTrunks] = useState<LgwTrunk[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [boundary, setBoundary] = useState<{ status: number; tracking: string | null; error: string | null } | null>(null);
  const [busyTrunkId, setBusyTrunkId] = useState<string | null>(null);
  const [opMsg, setOpMsg] = useState<{ kind: 'ok' | 'warn' | 'err'; text: string; trunkId?: string } | null>(null);
  const [csrPreview, setCsrPreview] = useState<{ trunkId: string; csr_pem: string; bytes: number } | null>(null);
  const [uploadFor, setUploadFor] = useState<{ trunkId: string } | null>(null);
  const [certPem, setCertPem] = useState('');
  const [chainPem, setChainPem] = useState('');
  // Real per-trunk live status keyed by trunk id, sourced from the Service-App
  // path (/api/wxc/local-gateways). The admin-OAuth token this panel uses returns
  // status "unknown"; the Service App returns the true "online"/etc. Same trunk
  // ids, so we merge by id for an accurate status pill.
  const [liveStatusById, setLiveStatusById] = useState<Record<string, { status: string | null; errors: string[] }>>({});
  // Upload-renewed-cert + verify flow.
  const [verifyFor, setVerifyFor] = useState<{ trunkId: string; fqdn: string } | null>(null);
  const [verifyPem, setVerifyPem] = useState('');
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyResult, setVerifyResult] = useState<{ renewed: boolean; ct_match: boolean; new_not_after_iso: string; message: string } | null>(null);
  // Top-of-panel "Renew certificate" flow — select an LGW, upload/paste the new PEM.
  const [renewFqdn, setRenewFqdn] = useState('');
  const [renewPem, setRenewPem] = useState('');
  const [renewFileName, setRenewFileName] = useState<string | null>(null);
  const [renewBusy, setRenewBusy] = useState(false);
  const [renewResult, setRenewResult] = useState<
    { ok: boolean; renewed: boolean; ct_match: boolean; ct_reached: boolean; old_not_after_iso: string | null; new_not_after_iso: string; cn: string; issuer: string; message: string } | null
  >(null);
  // "Generate key + CSR" device-command helper (IOS-XE), per Local Gateway.
  const [csrCmdFor, setCsrCmdFor] = useState<{ trunkId: string; fqdn: string } | null>(null);
  // Paste-the-device-CSR → download as a clean .csr file (for GoDaddy submission).
  const [deviceCsr, setDeviceCsr] = useState('');

  async function load() {
    setLoading(true); setLoadErr(null); setBoundary(null);
    try {
      const r = await api.lgwTrunks();
      setTrunks(r.items);
      if (r.last_status && r.last_status !== 200) {
        setBoundary({ status: r.last_status, tracking: r.last_tracking_id, error: r.last_error });
      }
      // Best-effort: pull real live status from the Service-App endpoint and
      // index by trunk id. Never fails the panel if creds are missing.
      try {
        const w = await api.wxcLocalGateways();
        const map: Record<string, { status: string | null; errors: string[] }> = {};
        for (const g of w.local_gateways ?? []) map[g.id] = { status: g.device_status, errors: g.error_codes ?? [] };
        setLiveStatusById(map);
      } catch { /* leave map empty — pill falls back to admin-token status */ }
    } catch (e: any) {
      setLoadErr(e?.message ?? 'load failed');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function doVerifyRenewal() {
    if (!verifyFor || !verifyPem.trim()) return;
    setVerifyBusy(true); setVerifyResult(null);
    try {
      const r = await api.lgwCertVerifyRenewal(verifyFor.fqdn, verifyPem.trim());
      setVerifyResult({ renewed: r.renewed, ct_match: r.ct_match, new_not_after_iso: r.new_not_after_iso, message: r.message });
      await load(); // refresh EXPIRES from the stored cert
    } catch (e: any) {
      setVerifyResult({ renewed: false, ct_match: false, new_not_after_iso: '', message: e?.message ?? 'verify failed' });
    } finally { setVerifyBusy(false); }
  }

  // Read a chosen .pem/.crt/.cer file into the renewal textarea.
  function onRenewFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setRenewFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => setRenewPem(String(reader.result ?? ''));
    reader.readAsText(f);
  }

  async function doRenewUpload() {
    if (!renewFqdn || !renewPem.trim()) return;
    setRenewBusy(true); setRenewResult(null);
    try {
      const r = await api.lgwCertVerifyRenewal(renewFqdn, renewPem.trim());
      setRenewResult({
        ok: r.ok, renewed: r.renewed, ct_match: r.ct_match, ct_reached: r.ct_reached,
        old_not_after_iso: r.old_not_after ? new Date(r.old_not_after * 1000).toISOString().slice(0, 10) : null,
        new_not_after_iso: r.new_not_after_iso, cn: r.cn, issuer: r.issuer, message: r.message,
      });
      await load(); // refresh EXPIRES + KPI tiles from the stored cert
    } catch (e: any) {
      setRenewResult({ ok: false, renewed: false, ct_match: false, ct_reached: false, old_not_after_iso: null, new_not_after_iso: '', cn: '', issuer: '', message: e?.message ?? 'renewal failed' });
    } finally { setRenewBusy(false); }
  }

  async function doFetchCsr(t: LgwTrunk) {
    setBusyTrunkId(t.id); setOpMsg(null); setCsrPreview(null);
    try {
      const r: LgwTrunkCsrFetchResponse = await api.lgwTrunkCsrFetch(t.id);
      if (r.ok) {
        setCsrPreview({ trunkId: t.id, csr_pem: r.csr_pem, bytes: r.csr_bytes });
        setOpMsg({ kind: 'ok', text: `CSR fetched — ${r.csr_bytes} bytes. trackingId: ${r.trackingId ?? '—'}`, trunkId: t.id });
      } else {
        setOpMsg({
          kind: 'warn',
          text: `CSR fetch returned HTTP ${r.http_status}${r.trackingId ? ` (trackingId: ${r.trackingId})` : ''} — ${r.hint ?? 'see body snippet'}`,
          trunkId: t.id,
        });
      }
    } catch (e: any) {
      setOpMsg({ kind: 'err', text: e?.message ?? 'CSR fetch failed', trunkId: t.id });
    } finally { setBusyTrunkId(null); await load(); }
  }

  async function doUploadCert() {
    if (!uploadFor) return;
    setBusyTrunkId(uploadFor.trunkId); setOpMsg(null);
    try {
      const r: LgwTrunkCertUploadResponse = await api.lgwTrunkCertUpload(uploadFor.trunkId, certPem, chainPem || undefined);
      setOpMsg({
        kind: r.ok ? 'ok' : 'warn',
        text: r.ok
          ? (r.cert_expires_at
              ? `Cert accepted by Cisco — expires ${new Date(r.cert_expires_at).toLocaleDateString()}.`
              : `Cert accepted by Cisco, but its expiry could not be parsed${r.parse_warning ? ` — ${r.parse_warning}` : '.'}`)
          : `Cisco rejected: HTTP ${r.cisco_http_status}${r.trackingId ? ` (trackingId: ${r.trackingId})` : ''} — ${r.hint ?? r.cisco_body_snippet ?? ''}`,
        trunkId: uploadFor.trunkId,
      });
      if (r.ok) {
        setUploadFor(null); setCertPem(''); setChainPem('');
      }
    } catch (e: any) {
      setOpMsg({ kind: 'err', text: e?.message ?? 'cert upload failed', trunkId: uploadFor.trunkId });
    } finally { setBusyTrunkId(null); await load(); }
  }

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-tnh-accent font-semibold">admin-oauth</div>
          <div className="font-medium mt-0.5">Webex Calling trunks — TLS certificates</div>
          <div className="text-xs text-gray-500 mt-1 max-w-2xl">
            Fetch each trunk's CSR from <span className="font-mono">webexapis.com/v1/telephony/config/premisePstn/trunks/&#123;id&#125;/csr</span>,
            sign it externally, then upload the cert + chain back. Driven by the Control Hub admin OAuth token.
          </div>
        </div>
        <button onClick={load} disabled={loading} className="btn-ghost text-xs">{loading ? 'Loading…' : '↻ Refresh'}</button>
      </div>
      {loadErr && <div className="text-xs text-tnh-err">{loadErr}</div>}
      {boundary && (
        <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2">
          <div className="font-semibold">Webex returned HTTP {boundary.status}</div>
          {boundary.tracking && <div className="font-mono text-[11px]">trackingId: {boundary.tracking}</div>}
          {boundary.error && <pre className="font-mono text-[11px] whitespace-pre-wrap mt-1">{boundary.error}</pre>}
          <div className="mt-1">If this is 401/403, the admin OAuth scope may not cover trunk admin — check the Integration record at developer.webex.com.</div>
        </div>
      )}
      {/* ── Renew certificate — select an LGW, upload the renewed PEM ───────── */}
      {(() => {
        const candidates = (trunks ?? []).filter((t) => t.peer_identity);
        const sel = candidates.find((t) => t.peer_identity === renewFqdn);
        const selExpIso = sel?.cert_state?.cert_expires_at
          ? new Date(sel.cert_state.cert_expires_at).toISOString().slice(0, 10) : null;
        return (
          <div className="border border-tnh-accent/40 bg-tnh-accent/5 rounded p-3 space-y-2">
            <div className="text-xs font-semibold flex items-center gap-2">
              <span>🔐 Renew certificate</span>
              <span className="text-[11px] font-normal text-gray-500">— select the Local Gateway, upload the renewed cert, get verification</span>
            </div>
            <div className="grid sm:grid-cols-[minmax(0,1fr)_auto] gap-2 items-end">
              <div>
                <label className="block text-[11px] text-gray-500 mb-0.5">Local Gateway</label>
                <select
                  value={renewFqdn}
                  onChange={(e) => { setRenewFqdn(e.target.value); setRenewResult(null); }}
                  className="w-full px-2 py-1.5 rounded border border-tnh-border dark:border-tnh-border-dark bg-white dark:bg-gray-900 text-xs">
                  <option value="">— choose a Local Gateway —</option>
                  {candidates.map((t) => (
                    <option key={t.id} value={t.peer_identity!}>
                      {(t.name || t.id.slice(0, 10))} · {t.peer_identity}{t.port ? `:${t.port}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              {selExpIso && (
                <div className="text-[11px] text-gray-500 pb-1.5">
                  Current expiry: <span className="font-mono">{selExpIso}</span>
                </div>
              )}
            </div>
            {renewFqdn && (
              <>
                <div className="flex items-center gap-2 flex-wrap text-[11px] text-gray-500">
                  <span className="font-semibold text-gray-600 dark:text-gray-300">Step 1 —</span>
                  <span>generate a fresh key + CSR on the gateway, sign at GoDaddy:</span>
                  <button onClick={() => setCsrCmdFor({ trunkId: renewFqdn, fqdn: renewFqdn })}
                          className="btn-ghost text-[11px] text-tnh-accent">Generate key + CSR commands</button>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-[11px] text-gray-600 dark:text-gray-300">Step 2 —</span>
                  <label className="btn-ghost text-[11px] cursor-pointer">
                    📎 Choose cert file
                    <input type="file" accept=".pem,.crt,.cer,.cert,application/x-pem-file" onChange={onRenewFile} className="hidden" />
                  </label>
                  {renewFileName && <span className="text-[11px] text-gray-500 font-mono">{renewFileName}</span>}
                  <span className="text-[11px] text-gray-500">or paste the PEM below</span>
                </div>
                <textarea value={renewPem} onChange={(e) => setRenewPem(e.target.value)} rows={6} spellCheck={false}
                          placeholder="-----BEGIN CERTIFICATE-----&#10;…renewed cert…&#10;-----END CERTIFICATE-----"
                          className="w-full px-2 py-1 rounded border border-tnh-border dark:border-tnh-border-dark bg-transparent text-[11px] font-mono" />
                <div className="flex gap-2 items-center">
                  <button onClick={doRenewUpload} disabled={renewBusy || !renewPem.trim()}
                          className="px-3 py-1.5 rounded bg-tnh-accent text-white text-xs font-semibold disabled:opacity-50">
                    {renewBusy ? 'Verifying…' : 'Upload & verify renewal'}
                  </button>
                  <button onClick={() => { setRenewFqdn(''); setRenewPem(''); setRenewFileName(null); setRenewResult(null); }}
                          className="btn-ghost text-xs">Clear</button>
                </div>
              </>
            )}
            {renewResult && (
              <div className={`text-[11px] rounded p-2 space-y-0.5 ${renewResult.renewed ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'bg-amber-500/10 text-amber-700 dark:text-amber-300'}`}>
                <div className="font-semibold">{renewResult.renewed ? '✓ Renewal verified' : '⚠ Not renewed'}</div>
                <div>{renewResult.message}</div>
                {renewResult.new_not_after_iso && (
                  <div>New expiry: <span className="font-mono">{renewResult.new_not_after_iso}</span>
                    {renewResult.old_not_after_iso && <> (was <span className="font-mono">{renewResult.old_not_after_iso}</span>)</>}
                  </div>
                )}
                {renewResult.cn && <div className="text-gray-500">CN {renewResult.cn} · issuer {renewResult.issuer}</div>}
                <div className="text-gray-500">
                  Public CA log: {renewResult.ct_reached ? (renewResult.ct_match ? 'confirmed ✓' : 'reached, serial not yet listed') : 'not reachable'}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <div className="border border-tnh-border dark:border-tnh-border-dark rounded overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 dark:bg-gray-900/40">
            <tr>
              {['Trunk','Type','Cert status','Expires','Last action','Actions'].map((h) =>
                <th key={h} className="text-left px-3 py-2 font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {(trunks ?? []).length === 0 && !loading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                No premise-PSTN trunks returned by Webex (admin OAuth required, scope must include Calling admin read).
              </td></tr>
            )}
            {(trunks ?? []).map((t) => {
              const status = t.cert_state?.upload_status ?? '—';
              // Expires: a real date when we have a parsed expiry; an honest
              // "unparsed" when a cert is present but its expiry could not be
              // read (never a silent blank); '—' only when no cert is uploaded.
              const expIso = t.cert_state?.cert_expires_at
                ? new Date(t.cert_state.cert_expires_at).toISOString().slice(0, 10)
                : t.cert_state?.has_cert
                ? 'unparsed'
                : '—';
              const expClass = !t.cert_state?.cert_expires_at
                ? 'text-gray-500'
                : (t.cert_state.cert_expires_at - Date.now()) / 86400000 < 30
                ? 'text-rose-600 dark:text-rose-300' : 'text-emerald-600 dark:text-emerald-300';
              return (
                <tr key={t.id} className="border-t border-tnh-border dark:border-tnh-border-dark align-top">
                  <td className="px-3 py-1.5">
                    <div className="font-mono">{t.name || t.id.slice(0, 12)}</div>
                    {t.peer_identity && (
                      <div className="text-[10px] text-gray-500 font-mono">{t.peer_identity}{t.port ? `:${t.port}` : ''}</div>
                    )}
                    {typeof t.location === 'object' && t.location?.name && (
                      <div className="text-[11px] text-gray-500">{t.location.name}</div>
                    )}
                    {(() => {
                      // Prefer the Service-App live status (accurate); fall back to
                      // the admin-token status only if the Service-App lookup had nothing.
                      const live = liveStatusById[t.id];
                      const st = live?.status ?? t.trunk_status ?? null;
                      const errs = (live?.errors?.length ?? t.error_codes?.length ?? 0);
                      return st ? (
                        <span className={`inline-block mt-0.5 text-[10px] px-1.5 py-0.5 rounded-full ${st === 'online' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}>
                          trunk {st}{errs > 0 ? ` · ${errs} err` : ''}
                        </span>
                      ) : null;
                    })()}
                  </td>
                  <td className="px-3 py-1.5">{t.trunkType ?? '—'}{t.deviceType ? ` · ${t.deviceType}` : ''}</td>
                  <td className="px-3 py-1.5">
                    <span className={`pill text-[10px] px-2 py-0.5 rounded-full ${status === 'accepted' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : status === 'rejected' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}`}>
                      {status}
                    </span>
                    {t.cert_state?.last_error && (
                      <div className="text-[10px] text-rose-600 dark:text-rose-300 mt-0.5">{t.cert_state.last_error.slice(0, 100)}</div>
                    )}
                  </td>
                  <td className={`px-3 py-1.5 ${expClass}`}>{expIso}</td>
                  <td className="px-3 py-1.5 text-[11px] text-gray-500">
                    {t.cert_state?.csr_fetched_at ? `CSR ${new Date(t.cert_state.csr_fetched_at).toLocaleString()}` : '—'}
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex gap-1 flex-wrap">
                      <button onClick={() => doFetchCsr(t)} disabled={busyTrunkId === t.id}
                              className="btn-ghost text-[11px]">Fetch CSR</button>
                      <button onClick={() => { setUploadFor({ trunkId: t.id }); setCertPem(''); setChainPem(''); }}
                              className="btn-ghost text-[11px]">Upload Cert</button>
                      {t.peer_identity && (
                        <button onClick={() => setCsrCmdFor({ trunkId: t.id, fqdn: t.peer_identity! })}
                                className="btn-ghost text-[11px]">Generate CSR</button>
                      )}
                      {t.peer_identity && (
                        <button onClick={() => { setVerifyFor({ trunkId: t.id, fqdn: t.peer_identity! }); setVerifyPem(''); setVerifyResult(null); }}
                                className="btn-ghost text-[11px] text-tnh-accent">Verify renewal</button>
                      )}
                    </div>
                    {opMsg && opMsg.trunkId === t.id && (
                      <div className={`text-[11px] mt-1 ${opMsg.kind === 'ok' ? 'text-emerald-600 dark:text-emerald-300' : opMsg.kind === 'warn' ? 'text-amber-600 dark:text-amber-300' : 'text-rose-600 dark:text-rose-300'}`}>
                        {opMsg.text}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {csrCmdFor && (() => {
        const script = buildLgwCsrScript(csrCmdFor.fqdn);
        return (
          <div className="border border-tnh-accent/40 rounded p-3 space-y-2">
            <div className="text-xs font-semibold">Generate key + CSR on <code className="font-mono">{csrCmdFor.fqdn}</code></div>
            <div className="text-[11px] text-gray-500">
              Webex doesn't issue the CSR — it's generated on the Local Gateway. Paste these IOS-XE commands on the device,
              copy the printed <span className="font-mono">CERTIFICATE REQUEST</span> to GoDaddy, then install the signed cert and run <strong>Upload &amp; verify renewal</strong>.
            </div>
            <pre className="font-mono text-[10px] whitespace-pre-wrap bg-gray-50 dark:bg-gray-900/40 p-2 rounded max-h-72 overflow-auto">{script}</pre>
            <div className="flex gap-2">
              <button onClick={() => { navigator.clipboard?.writeText(script); setOpMsg({ kind: 'ok', text: 'CSR commands copied to clipboard.', trunkId: csrCmdFor.trunkId }); }}
                      className="btn-ghost text-xs">Copy commands</button>
              <button onClick={() => {
                const blob = new Blob([script], { type: 'text/plain' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `lgw-csr-${csrCmdFor.fqdn}.txt`;
                a.click();
                URL.revokeObjectURL(a.href);
              }} className="btn-ghost text-xs">Download .txt</button>
              <button onClick={() => { setCsrCmdFor(null); setDeviceCsr(''); }} className="btn-ghost text-xs ml-auto">Close</button>
            </div>
            <div className="border-t border-tnh-border dark:border-tnh-border-dark pt-2 mt-1 space-y-1.5">
              <div className="text-[11px] text-gray-500">
                After <span className="font-mono">crypto pki enroll</span> prints the request, paste the
                <span className="font-mono"> -----BEGIN CERTIFICATE REQUEST-----</span> block here to save it as a clean <code className="font-mono">.csr</code> file for GoDaddy.
              </div>
              <textarea value={deviceCsr} onChange={(e) => setDeviceCsr(e.target.value)} rows={5} spellCheck={false}
                        placeholder="-----BEGIN CERTIFICATE REQUEST-----&#10;…paste what the gateway printed…&#10;-----END CERTIFICATE REQUEST-----"
                        className="w-full px-2 py-1 rounded border border-tnh-border dark:border-tnh-border-dark bg-transparent text-[10px] font-mono" />
              {(() => {
                const v = deviceCsr.trim();
                const valid = v.includes('BEGIN CERTIFICATE REQUEST') && v.includes('END CERTIFICATE REQUEST');
                return (
                  <div className="flex gap-2 items-center">
                    <button disabled={!valid}
                            onClick={() => {
                              const blob = new Blob([v.endsWith('\n') ? v : v + '\n'], { type: 'application/pkcs10' });
                              const a = document.createElement('a');
                              a.href = URL.createObjectURL(blob);
                              a.download = `lgw-${csrCmdFor.fqdn}.csr`;
                              a.click();
                              URL.revokeObjectURL(a.href);
                              setOpMsg({ kind: 'ok', text: `Saved lgw-${csrCmdFor.fqdn}.csr`, trunkId: csrCmdFor.trunkId });
                            }}
                            className="px-3 py-1 rounded bg-tnh-accent text-white text-xs font-semibold disabled:opacity-50">Download .csr</button>
                    <button disabled={!valid}
                            onClick={() => { navigator.clipboard?.writeText(v); setOpMsg({ kind: 'ok', text: 'CSR copied to clipboard.', trunkId: csrCmdFor.trunkId }); }}
                            className="btn-ghost text-xs disabled:opacity-50">Copy CSR</button>
                    {deviceCsr.trim() && !valid && <span className="text-[11px] text-amber-600 dark:text-amber-300">Not a valid PKCS#10 CSR — paste the full BEGIN/END block.</span>}
                  </div>
                );
              })()}
            </div>
          </div>
        );
      })()}

      {csrPreview && (
        <div className="border border-tnh-border dark:border-tnh-border-dark rounded p-3 space-y-2">
          <div className="text-xs font-semibold">CSR for trunk <code className="font-mono">{csrPreview.trunkId}</code> ({csrPreview.bytes} bytes)</div>
          <pre className="font-mono text-[10px] whitespace-pre-wrap bg-gray-50 dark:bg-gray-900/40 p-2 rounded max-h-48 overflow-auto">{csrPreview.csr_pem}</pre>
          <div className="flex gap-2">
            <button onClick={() => {
              navigator.clipboard?.writeText(csrPreview.csr_pem);
              setOpMsg({ kind: 'ok', text: 'CSR copied to clipboard.', trunkId: csrPreview.trunkId });
            }} className="btn-ghost text-xs">Copy</button>
            <button onClick={() => {
              const blob = new Blob([csrPreview.csr_pem], { type: 'application/x-pem-file' });
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = `trunk-${csrPreview.trunkId}.csr.pem`;
              a.click();
              URL.revokeObjectURL(a.href);
            }} className="btn-ghost text-xs">Download .pem</button>
            <button onClick={() => setCsrPreview(null)} className="btn-ghost text-xs ml-auto">Close</button>
          </div>
        </div>
      )}

      {uploadFor && (
        <div className="border border-tnh-border dark:border-tnh-border-dark rounded p-3 space-y-2">
          <div className="text-xs font-semibold">Upload certificate for trunk <code className="font-mono">{uploadFor.trunkId}</code></div>
          {(() => {
            const ut = (trunks ?? []).find((x) => x.id === uploadFor.trunkId);
            return ut?.peer_identity ? (
              <div className="text-[11px] text-gray-500">
                Paste the certificate installed on <code className="font-mono">{ut.peer_identity}{ut.port ? `:${ut.port}` : ''}</code>.
                Webex does not return the cert expiry via API, so uploading the PEM here is what populates the <strong>Expires</strong> column — TriNetHub parses its <code className="font-mono">notAfter</code> and tracks it.
              </div>
            ) : null;
          })()}
          <label className="block text-[11px] text-gray-500">Cert PEM (required)</label>
          <textarea value={certPem} onChange={(e) => setCertPem(e.target.value)} rows={6}
                    spellCheck={false}
                    placeholder="-----BEGIN CERTIFICATE-----&#10;…&#10;-----END CERTIFICATE-----"
                    className="w-full px-2 py-1 rounded border border-tnh-border dark:border-tnh-border-dark bg-transparent text-[11px] font-mono" />
          <label className="block text-[11px] text-gray-500">Chain PEM (optional intermediates)</label>
          <textarea value={chainPem} onChange={(e) => setChainPem(e.target.value)} rows={5}
                    spellCheck={false}
                    placeholder="-----BEGIN CERTIFICATE-----&#10;…intermediate(s)…&#10;-----END CERTIFICATE-----"
                    className="w-full px-2 py-1 rounded border border-tnh-border dark:border-tnh-border-dark bg-transparent text-[11px] font-mono" />
          <div className="flex gap-2">
            <button onClick={doUploadCert} disabled={busyTrunkId === uploadFor.trunkId || !certPem.trim()}
                    className="px-3 py-1 rounded bg-tnh-accent text-white text-xs font-semibold disabled:opacity-50">
              {busyTrunkId === uploadFor.trunkId ? 'Uploading…' : 'Upload to Webex'}
            </button>
            <button onClick={() => setUploadFor(null)} className="btn-ghost text-xs">Cancel</button>
          </div>
        </div>
      )}

      {verifyFor && (
        <div className="border border-tnh-accent/40 rounded p-3 space-y-2">
          <div className="text-xs font-semibold">Verify renewal — <code className="font-mono">{verifyFor.fqdn}</code></div>
          <div className="text-[11px] text-gray-500">
            Paste the <strong>renewed</strong> certificate's PEM. TriNetHub parses its expiry, compares it to the cert on file,
            cross-checks the public CA log to confirm it's the live issued cert, and updates <strong>EXPIRES</strong> —
            resetting the 30-day notice for the new cert.
          </div>
          <textarea value={verifyPem} onChange={(e) => setVerifyPem(e.target.value)} rows={6} spellCheck={false}
                    placeholder="-----BEGIN CERTIFICATE-----&#10;…renewed cert…&#10;-----END CERTIFICATE-----"
                    className="w-full px-2 py-1 rounded border border-tnh-border dark:border-tnh-border-dark bg-transparent text-[11px] font-mono" />
          <div className="flex gap-2">
            <button onClick={doVerifyRenewal} disabled={verifyBusy || !verifyPem.trim()}
                    className="px-3 py-1 rounded bg-tnh-accent text-white text-xs font-semibold disabled:opacity-50">
              {verifyBusy ? 'Verifying…' : 'Upload & verify renewal'}
            </button>
            <button onClick={() => { setVerifyFor(null); setVerifyResult(null); }} className="btn-ghost text-xs">Cancel</button>
          </div>
          {verifyResult && (
            <div className={`text-[11px] mt-1 rounded p-2 ${verifyResult.renewed ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'bg-amber-500/10 text-amber-700 dark:text-amber-300'}`}>
              {verifyResult.renewed ? '✓ ' : '⚠ '}{verifyResult.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// LgwModePanel — CUBE/LGW → LGW mode sub-tab.
//
// Per-gateway Local Gateway configuration: WxC tenant binding, survivability,
// emergency routing, reachability status. Each row has an inline editor so an
// admin can set these without leaving the page. Live from lgw-svc.
// ===========================================================================
function LgwModePanel() {
  const [gateways, setGateways] = useState<LgwGateway[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const g = await api.lgwGateways();
      setGateways(g.gateways);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load gateways');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const fmtSeen = (ms: number | null) => ms ? new Date(ms).toLocaleString() : 'never';
  const statusClass = (s: string) =>
    s === 'reachable' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : s === 'unreachable' || s === 'error' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
    : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300';

  return (
    <div className="space-y-3">
      <div className="card p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-base font-semibold">Local Gateway Mode</div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-3xl">
              Per-gateway Webex Calling Local Gateway settings — WxC tenant binding, PSTN
              survivability, and emergency-call routing. Reachability is reported by the
              cube-svc spoke heartbeat. Add gateways from the <strong>TLS Certificates</strong> tab.
            </p>
          </div>
          <button onClick={load} disabled={loading} className="btn-ghost text-sm disabled:opacity-50">↻ Refresh</button>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
              <tr>
                {['Gateway', 'Mode', 'WxC tenant', 'Survivability', 'Emergency routing', 'Status', 'Last seen', 'Actions'].map((c) => (
                  <th key={c} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500">Loading gateways…</td></tr>}
              {error && <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-rose-600">{error}</td></tr>}
              {!loading && !error && (gateways?.length ?? 0) === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500">
                  No gateways registered. Go to the <strong>TLS Certificates</strong> tab → <strong>+ Add Gateway</strong> to register your first CUBE / Local Gateway.
                </td></tr>
              )}
              {!loading && !error && (gateways ?? []).map((g) => (
                <Fragment key={g.gateway_id}>
                  <tr className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/30">
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{g.name}</div>
                      <div className="text-[10px] text-gray-500 font-mono">{g.host} · {g.role}</div>
                    </td>
                    <td className="px-4 py-2.5 text-xs">{g.lgw_mode}</td>
                    <td className="px-4 py-2.5 text-xs">{g.wxc_tenant || '—'}</td>
                    <td className="px-4 py-2.5 text-xs">
                      <span className={`pill text-[11px] px-2 py-0.5 rounded-full ${g.survivability_enabled ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                        {g.survivability_enabled ? 'enabled' : 'disabled'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs">{g.emergency_routing || '—'}</td>
                    <td className="px-4 py-2.5">
                      <span className={`pill text-[11px] px-2 py-0.5 rounded-full ${statusClass(g.status)}`}>{g.status}</span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{fmtSeen(g.last_seen_at)}</td>
                    <td className="px-4 py-2.5">
                      <button onClick={() => setEditId(editId === g.gateway_id ? null : g.gateway_id)}
                        className="px-2.5 py-1 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-medium">
                        {editId === g.gateway_id ? '× Close' : 'Configure'}
                      </button>
                    </td>
                  </tr>
                  {editId === g.gateway_id && (
                    <tr>
                      <td colSpan={8} className="px-4 py-0">
                        <LgwModeEditForm gateway={g} onDone={() => { setEditId(null); load(); }} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function LgwModeEditForm({ gateway, onDone }: { gateway: LgwGateway; onDone: () => void }) {
  const [mode, setMode] = useState(gateway.lgw_mode);
  const [wxcTenant, setWxcTenant] = useState(gateway.wxc_tenant);
  const [surv, setSurv] = useState(!!gateway.survivability_enabled);
  const [emRouting, setEmRouting] = useState(gateway.emergency_routing);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await api.lgwUpdateGateway(gateway.gateway_id, {
        lgw_mode: mode, wxc_tenant: wxcTenant,
        survivability_enabled: (surv ? 1 : 0) as any, emergency_routing: emRouting,
      });
      onDone();
    } catch (e: any) {
      setError(e?.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="my-2 p-3 rounded-lg border border-indigo-300 bg-indigo-50/40 dark:bg-indigo-900/15">
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Configure LGW — {gateway.name}</div>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">LGW mode</label>
          <select value={mode} onChange={(e) => setMode(e.target.value as any)}
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
            <option value="cube">cube</option><option value="wxc_calling">wxc_calling</option><option value="hybrid">hybrid</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">WxC tenant</label>
          <input value={wxcTenant} onChange={(e) => setWxcTenant(e.target.value)} placeholder="Webex Calling tenant / org id"
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Emergency routing</label>
          <input value={emRouting} onChange={(e) => setEmRouting(e.target.value)} placeholder="E911 routing target / ELIN"
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={surv} onChange={(e) => setSurv(e.target.checked)} />
            PSTN survivability enabled
          </label>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button onClick={save} disabled={busy}
          className="px-4 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium disabled:opacity-50">
          {busy ? 'Saving…' : 'Save configuration'}
        </button>
        {error && <span className="text-sm text-rose-600 dark:text-rose-400">⚠ {error}</span>}
      </div>
    </div>
  );
}

// ===========================================================================
// LgwTelemetryPanel — CUBE/LGW Phase 2/3 live telemetry sub-tabs.
//
// One component drives 5 sub-tabs (peers / trunks / calls / logs / cdr). All
// data is read live from lgw-svc, populated by cube-tunnel-svc (RESTCONF over
// the Long Beach Cloudflare Tunnel). Every control is functional: gateway
// filter, refresh, Export CSV, and for logs a severity filter + download.
// ===========================================================================
type LgwTelemetryKey = 'peers' | 'trunks' | 'calls' | 'logs' | 'cdr';

// Dial-peer leg classification for CUCM ↔ CUBE ↔ LGW/PSTN troubleshooting.
// A peer whose session-target points at a CUCM signaling host is the
// CUCM-facing leg; a peer with any other target is the LGW/PSTN-facing leg;
// a peer with no target (pure inbound match) is shown as inbound.
// Long Beach CUCM signals on 10.18.160.26 (see cucm-tunnel-svc). Extend as needed.
const CUCM_SIGNALING_HOSTS = ['10.18.160.26'];
type DialPeerLeg = 'cucm' | 'pstn' | 'inbound';
function dialPeerLeg(p: CubeDialPeer): DialPeerLeg {
  const tgt = (p.session_target || '').toLowerCase();
  if (tgt && CUCM_SIGNALING_HOSTS.some((h) => tgt.includes(h.toLowerCase()))) return 'cucm';
  if (tgt) return 'pstn';
  return 'inbound';
}
const LEG_META: Record<DialPeerLeg, { label: string; cls: string }> = {
  cucm:    { label: 'Toward CUCM',     cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' },
  pstn:    { label: 'Toward LGW/PSTN', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  inbound: { label: 'Inbound',         cls: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
};

function LgwTelemetryPanel({ areaKey, readOnly = false }: { areaKey: LgwTelemetryKey; readOnly?: boolean }) {
  const [gateways, setGateways] = useState<LgwGateway[]>([]);
  const [gwFilter, setGwFilter] = useState('');     // gateway_id or '' for all
  const [rows, setRows] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [sevMax, setSevMax] = useState(7);          // logs only — IOS severity 0-7
  const [lastSync, setLastSync] = useState<string | null>(null);
  // CUBE↔LGW pooling — live Webex Calling view of the gateways, shown when the
  // spoke has pushed no device telemetry so the CUBE side is never a dead tab.
  const [pooled, setPooled] = useState<WxcLocalGateway[] | null>(null);
  // Dial-peer editor state — only meaningful when areaKey === 'peers'. The
  // composite editKey is `${gateway_id}__${tag}` so we can collapse the open
  // editor without ambiguity. addOpen toggles the "+ Add dial peer" form row.
  const [editKey, setEditKey] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [legFilter, setLegFilter] = useState<'' | DialPeerLeg>(''); // peers only — CUCM/PSTN leg

  // gateway list loads once
  useEffect(() => {
    api.lgwGateways().then((g) => setGateways(g.gateways)).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true); setError(null); setPooled(null);
    try {
      const gw = gwFilter || undefined;
      let result: any[] = [];
      if (areaKey === 'peers') {
        const r = await api.lgwDialPeers(gw); result = r.dial_peers;
      } else if (areaKey === 'trunks') {
        const r = await api.lgwSipTrunks(gw); result = r.sip_trunks;
      } else if (areaKey === 'calls') {
        const r = await api.lgwActiveCalls(gw); result = r.active_calls;
      } else if (areaKey === 'logs') {
        const r = await api.lgwLogs({ gateway_id: gw, severity: sevMax, limit: 500 }); result = r.logs;
      } else {
        const r = await api.lgwCdr({ limit: 1000 }); result = r.cdr;
      }
      setRows(result);
      // CUBE↔LGW pooling — when the spoke has pushed nothing (no spoke, or a
      // stale one), fall back to the live Webex Calling view of the SAME
      // gateways so the CUBE side still shows real production data.
      if ((result ?? []).length === 0) {
        try {
          const hub = await api.wxcLocalGateways();
          setPooled(hub.local_gateways ?? []);
        } catch { /* pooling is best-effort */ }
      }
      // sync status (best-effort)
      try {
        const s = await api.lgwSyncStatus();
        const latest = s.sync_runs[0];
        setLastSync(latest ? new Date(latest.started_at).toLocaleString() : null);
      } catch { /* ignore */ }
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load telemetry');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [areaKey, gwFilter, sevMax]);

  const fmtTs = (ms: number) => ms ? new Date(ms).toLocaleString() : '—';
  const fmtDur = (sec: number) => {
    if (!sec) return '0s';
    const m = Math.floor(sec / 60), s = sec % 60;
    return m ? `${m}m ${s}s` : `${s}s`;
  };
  const sevLabel = (n: number) => ['emerg','alert','crit','err','warn','notice','info','debug'][n] ?? String(n);
  const sevClass = (n: number) =>
    n <= 2 ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200'
    : n === 3 ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
    : n === 4 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  const stateClass = (s: string) =>
    /up|registered|ok/i.test(s) ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : /down|unreg|fail|expired|error|timeout/i.test(s) ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
    : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300';

  const filtered = (rows ?? []).filter((r) => {
    if (areaKey === 'peers' && legFilter && dialPeerLeg(r as CubeDialPeer) !== legFilter) return false;
    if (!filter) return true;
    return JSON.stringify(r).toLowerCase().includes(filter.toLowerCase());
  });

  const exportCsv = () => {
    if (!filtered.length) return;
    const cols = Object.keys(filtered[0]).filter((k) => k !== 'raw_config' && k !== 'raw_json');
    const esc = (v: any) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [cols.join(','), ...filtered.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `cube_${areaKey}_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const headers: Record<LgwTelemetryKey, string[]> = {
    peers:  readOnly
      ? ['Tag', 'Leg', 'Direction', 'Destination pattern', 'Session target', 'Codec', 'State', 'Gateway']
      : ['Tag', 'Leg', 'Direction', 'Destination pattern', 'Session target', 'Codec', 'State', 'Gateway', 'Actions'],
    trunks: ['Trunk', 'Carrier', 'Registration', 'OPTIONS', 'RTT', 'Calls/hr', 'Active', 'Gateway'],
    calls:  ['Call ID', 'Direction', 'Calling', 'Called', 'Codec', 'Duration', 'Trunk', 'Gateway'],
    logs:   ['Time', 'Severity', 'Facility', 'Mnemonic', 'Message'],
    cdr:    ['Disconnected', 'Direction', 'Calling', 'Called', 'Duration', 'Disposition', 'Codec', 'MOS'],
  };

  const peerKey = (p: CubeDialPeer) => `${p.gateway_id}__${p.tag}`;
  const onDeletePeer = async (p: CubeDialPeer) => {
    if (!confirm(`Delete dial peer ${p.tag} on ${p.gateway_name || p.gateway_id.slice(0, 8)}?`)) return;
    try {
      await api.lgwDeleteDialPeer(p.gateway_id, p.tag);
      await load();
    } catch (e: any) {
      alert(`Delete failed: ${e?.message ?? e}`);
    }
  };

  const renderRow = (r: any): ReactNode[] => {
    if (areaKey === 'peers') {
      const p = r as CubeDialPeer;
      const k = peerKey(p);
      const leg = LEG_META[dialPeerLeg(p)];
      const cells: ReactNode[] = [
        p.tag,
        <span key="leg" className={`pill text-[11px] px-2 py-0.5 rounded-full ${leg.cls}`}>{leg.label}</span>,
        p.direction || '—', p.destination_pattern || '—', p.session_target || '—',
        p.codec_class || '—',
        <span key="s" className={`pill text-[11px] px-2 py-0.5 rounded-full ${stateClass(p.state)}`}>{p.state || '—'}</span>,
        p.gateway_name || p.gateway_id.slice(0, 8),
      ];
      if (!readOnly) {
        cells.push(
          <div key="a" className="flex items-center gap-1">
            <button onClick={() => { setEditKey(editKey === k ? null : k); setAddOpen(false); }}
              className="px-2 py-0.5 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-medium">
              {editKey === k ? '× Close' : 'Edit'}
            </button>
            <button onClick={() => onDeletePeer(p)}
              className="px-2 py-0.5 rounded-md bg-rose-600 hover:bg-rose-500 text-white text-[11px] font-medium">
              Delete
            </button>
          </div>);
      }
      return cells;
    }
    if (areaKey === 'trunks') {
      const t = r as CubeSipTrunk;
      return [t.name, t.carrier || '—',
        <span key="r" className={`pill text-[11px] px-2 py-0.5 rounded-full ${stateClass(t.registration_state)}`}>{t.registration_state}</span>,
        <span key="o" className={`pill text-[11px] px-2 py-0.5 rounded-full ${stateClass(t.options_state)}`}>{t.options_state}</span>,
        t.options_last_rtt_ms ? `${t.options_last_rtt_ms}ms` : '—',
        t.calls_in_last_hr, t.calls_active, t.gateway_name || t.gateway_id.slice(0, 8)];
    }
    if (areaKey === 'calls') {
      const c = r as CubeActiveCall;
      return [<span key="i" className="font-mono text-[11px]">{c.call_id}</span>, c.direction || '—',
        c.calling_number || '—', c.called_number || '—', c.codec || '—',
        fmtDur(c.duration_sec), c.trunk_name || '—', c.gateway_name || c.gateway_id.slice(0, 8)];
    }
    if (areaKey === 'logs') {
      const l = r as CubeLogEvent;
      return [fmtTs(l.occurred_at),
        <span key="s" className={`pill text-[11px] px-2 py-0.5 rounded-full ${sevClass(l.severity)}`}>{l.severity} {sevLabel(l.severity)}</span>,
        l.facility || '—', l.mnemonic || '—',
        <span key="m" className="font-mono text-[11px]">{l.message}</span>];
    }
    // cdr
    const d = r as LgwCdr;
    return [fmtTs(d.disconnect_ts), d.direction, d.calling_number || '—', d.called_number || '—',
      fmtDur(d.duration_seconds),
      <span key="x" className={`pill text-[11px] px-2 py-0.5 rounded-full ${d.disposition === 'completed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'}`}>{d.disposition}</span>,
      d.codec || '—', d.mos_score != null ? d.mos_score.toFixed(1) : '—'];
  };

  const titleMap: Record<LgwTelemetryKey, string> = {
    peers: 'Dial Peers', trunks: 'SIP Trunks', calls: 'Active Calls', logs: 'IOS XE Logs', cdr: 'Call Detail Records',
  };

  return (
    <div className="space-y-3">
      {/* action bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <select value={gwFilter} onChange={(e) => setGwFilter(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
          <option value="">All gateways</option>
          {gateways.map((g) => <option key={g.gateway_id} value={g.gateway_id}>{g.name}</option>)}
        </select>
        {areaKey === 'logs' && (
          <select value={sevMax} onChange={(e) => setSevMax(Number(e.target.value))}
            className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm"
            title="Show events at this severity or worse">
            <option value={2}>crit & worse</option>
            <option value={3}>error & worse</option>
            <option value={4}>warning & worse</option>
            <option value={6}>info & worse</option>
            <option value={7}>all (debug)</option>
          </select>
        )}
        {areaKey === 'peers' && (
          <select value={legFilter} onChange={(e) => setLegFilter(e.target.value as '' | DialPeerLeg)}
            className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm"
            title="Filter dial peers by call leg">
            <option value="">All legs</option>
            <option value="cucm">Toward CUCM</option>
            <option value="pstn">Toward LGW/PSTN</option>
            <option value="inbound">Inbound</option>
          </select>
        )}
        <input value={filter} onChange={(e) => setFilter(e.target.value)}
          placeholder={`Filter ${titleMap[areaKey].toLowerCase()}…`}
          className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm w-64" />
        <button onClick={load} disabled={loading} className="btn-ghost text-sm disabled:opacity-50">↻ Refresh</button>
        <button onClick={exportCsv} disabled={!filtered.length}
          className="btn-ghost text-sm disabled:opacity-50">📊 Export CSV</button>
        {areaKey === 'peers' && !readOnly && (
          <button onClick={() => { setAddOpen((v) => !v); setEditKey(null); }}
            className="px-3 py-1.5 rounded-lg bg-tnh-accent hover:opacity-90 text-white text-sm font-medium">
            {addOpen ? '× Cancel' : '+ Add dial peer'}
          </button>
        )}
        <span className="text-xs text-gray-500">
          {loading ? 'Loading…' : `${filtered.length}${(filter || legFilter) ? ` of ${rows?.length ?? 0}` : ''} rows`}
          {lastSync && ` · last sync ${lastSync}`}
        </span>
      </div>

      {/* table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
              <tr>
                {headers[areaKey].map((c) => (
                  <th key={c} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={headers[areaKey].length} className="px-4 py-12 text-center text-sm text-gray-500">Loading {titleMap[areaKey].toLowerCase()}…</td></tr>}
              {error && <tr><td colSpan={headers[areaKey].length} className="px-4 py-8 text-center text-sm text-rose-600">{error}</td></tr>}
              {!loading && !error && filtered.length === 0 && (
                <tr><td colSpan={headers[areaKey].length} className="p-0">
                  {(!filter && pooled && pooled.length > 0) ? (
                    <div className="p-4 space-y-2">
                      <div className="text-sm text-amber-700 dark:text-amber-300">
                        No device-level {titleMap[areaKey].toLowerCase()} from the tunnel yet — showing the live
                        Webex Calling view of these gateways instead (LGW↔CUBE pooled data).
                      </div>
                      <div className="rounded-lg border border-tnh-border dark:border-tnh-border-dark overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-900/40">
                            <tr>{['Gateway', 'Location', 'Trunk type', 'In use', 'Status'].map((c) => (
                              <th key={c} className="text-left px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{c}</th>
                            ))}</tr>
                          </thead>
                          <tbody>
                            {pooled.map((g) => (
                              <tr key={g.id} className="border-t border-tnh-border dark:border-tnh-border-dark">
                                <td className="px-4 py-2 font-medium">{g.name}</td>
                                <td className="px-4 py-2">{g.location_name}</td>
                                <td className="px-4 py-2">{g.trunk_type}</td>
                                <td className="px-4 py-2">{g.in_use ? 'Yes' : 'No'}</td>
                                <td className="px-4 py-2">
                                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${stateClass(g.device_status ?? '')}`}>{g.device_status ?? 'unknown'}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="text-xs text-gray-500">
                        Pooled live from Webex Calling. Device-level {titleMap[areaKey].toLowerCase()} fill in once a
                        TriNetHub spoke is reachable on the gateway.
                      </div>
                    </div>
                  ) : (
                    <div className="px-4 py-12 text-center text-sm text-gray-500">
                      No {titleMap[areaKey].toLowerCase()} {(filter || legFilter) ? 'match the filter.' : 'reported yet — cube-tunnel-svc syncs device telemetry over the Long Beach tunnel once the gateway is reachable.'}
                    </div>
                  )}
                </td></tr>
              )}
              {!loading && !error && areaKey === 'peers' && addOpen && (
                <tr>
                  <td colSpan={headers[areaKey].length} className="px-4 py-0">
                    <DialPeerEditor mode="create" gateways={gateways} defaultGatewayId={gwFilter}
                      onDone={() => { setAddOpen(false); load(); }} />
                  </td>
                </tr>
              )}
              {!loading && !error && filtered.map((r, i) => {
                if (areaKey === 'peers') {
                  const p = r as CubeDialPeer;
                  const k = peerKey(p);
                  return (
                    <Fragment key={k}>
                      <tr className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/30">
                        {renderRow(r).map((cell, j) => (
                          <td key={j} className="px-4 py-2 text-sm whitespace-nowrap">{cell}</td>
                        ))}
                      </tr>
                      {editKey === k && (
                        <tr>
                          <td colSpan={headers[areaKey].length} className="px-4 py-0">
                            <DialPeerEditor mode="edit" gateways={gateways} peer={p}
                              onDone={() => { setEditKey(null); load(); }} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                }
                return (
                  <tr key={i} className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/30">
                    {renderRow(r).map((cell, j) => (
                      <td key={j} className="px-4 py-2 text-sm whitespace-nowrap">{cell}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// DialPeerEditor — inline create/edit form for cube_dial_peers. Used by both
// the LGW-side (lgw_peers) and CUBE-side (peers) tabs. Posts to lgw-svc:
//   POST   /api/lgw/dial-peers              (create)
//   PATCH  /api/lgw/dial-peers/<gw>__<tag>  (update)
// Only fields the user is likely to want to edit are exposed; the rest stay
// as whatever the spoke last scraped (or blank for a new manual entry).
// ===========================================================================
function DialPeerEditor({
  mode, gateways, peer, defaultGatewayId, onDone,
}: {
  mode: 'create' | 'edit';
  gateways: LgwGateway[];
  peer?: CubeDialPeer;
  defaultGatewayId?: string;
  onDone: () => void;
}) {
  const [gatewayId, setGatewayId] = useState<string>(peer?.gateway_id ?? defaultGatewayId ?? gateways[0]?.gateway_id ?? '');
  const [tag, setTag] = useState<string>(peer ? String(peer.tag) : '');
  const [direction, setDirection] = useState<string>(peer?.direction ?? '');
  const [destPattern, setDestPattern] = useState<string>(peer?.destination_pattern ?? '');
  const [incomingCalled, setIncomingCalled] = useState<string>(peer?.incoming_called_number ?? '');
  const [sessionProtocol, setSessionProtocol] = useState<string>(peer?.session_protocol ?? 'sipv2');
  const [sessionTarget, setSessionTarget] = useState<string>(peer?.session_target ?? '');
  const [codecClass, setCodecClass] = useState<string>(peer?.codec_class ?? '');
  const [dtmfRelay, setDtmfRelay] = useState<string>(peer?.dtmf_relay ?? '');
  const [voiceClass, setVoiceClass] = useState<string>(peer?.voice_class ?? '');
  const [state, setState] = useState<string>(peer?.state ?? 'unknown');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setErr(null);
    if (!gatewayId) { setErr('Gateway is required.'); return; }
    const tagNum = Number(tag);
    if (!Number.isFinite(tagNum) || tagNum <= 0) { setErr('Tag must be a positive integer.'); return; }
    setBusy(true);
    try {
      const payload: Partial<CubeDialPeer> & { gateway_id: string; tag: number } = {
        gateway_id: gatewayId, tag: tagNum,
        direction: direction as any,
        voice_class: voiceClass,
        destination_pattern: destPattern,
        incoming_called_number: incomingCalled,
        session_protocol: sessionProtocol,
        session_target: sessionTarget,
        codec_class: codecClass,
        dtmf_relay: dtmfRelay,
        state: state as any,
      };
      if (mode === 'create') {
        await api.lgwCreateDialPeer(payload);
      } else {
        const { gateway_id: _gid, tag: _tag, ...patch } = payload;
        await api.lgwUpdateDialPeer(gatewayId, tagNum, patch);
      }
      onDone();
    } catch (e: any) {
      setErr(e?.message ?? 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="my-2 p-3 rounded-lg border border-indigo-300 bg-indigo-50/40 dark:bg-indigo-900/15">
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
        {mode === 'create' ? 'New dial peer' : `Edit dial peer ${peer?.tag} on ${peer?.gateway_name || peer?.gateway_id.slice(0, 8)}`}
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Gateway</label>
          <select value={gatewayId} onChange={(e) => setGatewayId(e.target.value)} disabled={mode === 'edit'}
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm disabled:opacity-60">
            <option value="">— select —</option>
            {gateways.map((g) => <option key={g.gateway_id} value={g.gateway_id}>{g.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Tag</label>
          <input value={tag} onChange={(e) => setTag(e.target.value)} disabled={mode === 'edit'}
            placeholder="e.g. 9999" inputMode="numeric"
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm disabled:opacity-60" />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Direction</label>
          <select value={direction} onChange={(e) => setDirection(e.target.value)}
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
            <option value="">—</option><option value="inbound">inbound</option>
            <option value="outbound">outbound</option><option value="both">both</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Destination pattern</label>
          <input value={destPattern} onChange={(e) => setDestPattern(e.target.value)}
            placeholder="e.g. 1T or +1[2-9]..[2-9]......"
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Incoming called number</label>
          <input value={incomingCalled} onChange={(e) => setIncomingCalled(e.target.value)}
            placeholder="for inbound matching"
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Session protocol</label>
          <input value={sessionProtocol} onChange={(e) => setSessionProtocol(e.target.value)}
            placeholder="sipv2"
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Session target</label>
          <input value={sessionTarget} onChange={(e) => setSessionTarget(e.target.value)}
            placeholder="sip-server / ipv4:host / dns:fqdn"
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Voice class</label>
          <input value={voiceClass} onChange={(e) => setVoiceClass(e.target.value)}
            placeholder="codec / sip-profiles tag"
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Codec class</label>
          <input value={codecClass} onChange={(e) => setCodecClass(e.target.value)}
            placeholder="e.g. g711ulaw / g729r8"
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">DTMF relay</label>
          <input value={dtmfRelay} onChange={(e) => setDtmfRelay(e.target.value)}
            placeholder="rtp-nte / sip-notify"
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm" />
        </div>
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">State</label>
          <select value={state} onChange={(e) => setState(e.target.value)}
            className="block w-full mt-1 px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
            <option value="unknown">unknown</option><option value="up">up</option>
            <option value="down">down</option><option value="admin-down">admin-down</option>
            <option value="error">error</option>
          </select>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button onClick={save} disabled={busy}
          className="px-4 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium disabled:opacity-50">
          {busy ? 'Saving…' : (mode === 'create' ? 'Create dial peer' : 'Save changes')}
        </button>
        <button onClick={onDone} disabled={busy}
          className="px-3 py-1.5 rounded-lg text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50">
          Cancel
        </button>
        {err && <span className="text-sm text-rose-600 dark:text-rose-400">⚠ {err}</span>}
      </div>
    </div>
  );
}

// ===========================================================================
// BackupConfigMenu — top-level action shown on the CUBE module, on both the
// LGW and CUBE sides. Two modes:
//   1. TriNetHub-config backup — JSON snapshot of D1 (gateways + dial peers
//      + SIP trunks + certs). Server-assembled, returned as an attachment.
//   2. Device running-config backup — live `show running-config` pulled
//      from a chosen gateway via the cube-svc spoke. If no capture exists
//      yet (no spoke / unreachable device), the server's 409 message is
//      surfaced verbatim — never fabricates output.
// ===========================================================================
function BackupConfigMenu() {
  const [open, setOpen] = useState(false);
  const [gateways, setGateways] = useState<LgwGateway[]>([]);
  const [picking, setPicking] = useState<'trinethub' | 'running' | null>(null);
  const [selectedGw, setSelectedGw] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // Lazy-load the gateway list the first time the menu opens.
  const openMenu = async () => {
    setOpen((v) => !v); setMsg(null); setPicking(null);
    if (gateways.length === 0) {
      try {
        const r = await api.lgwGateways();
        setGateways(r.gateways);
      } catch { /* leave empty — the menu still works for the all-tenants snapshot */ }
    }
  };

  const downloadTriNetHubBackup = (gwId?: string) => {
    setBusy(true); setMsg(null);
    try {
      const a = document.createElement('a');
      a.href = api.lgwBackupConfigUrl(gwId || undefined);
      // Letting the browser handle the download avoids buffering large
      // tenant snapshots in JS. The server sets Content-Disposition.
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setMsg('TriNetHub-config snapshot download started.');
      setPicking(null); setOpen(false);
    } catch (e: any) {
      setMsg(`Backup failed: ${e?.message ?? e}`);
    } finally {
      setBusy(false);
    }
  };

  const downloadRunningConfig = async (gwId: string) => {
    if (!gwId) { setMsg('Pick a gateway first.'); return; }
    setBusy(true); setMsg(null);
    try {
      // Fetch ourselves so we can surface backend errors (e.g. 409 no_capture_yet)
      // instead of silently triggering a no-op download.
      const res = await fetch(api.lgwGatewayRunningConfigUrl(gwId), { credentials: 'include' });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { const j = await res.json(); if (j?.error) detail = j.error; } catch {}
        setMsg(`Backup unavailable: ${detail}`);
        return;
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') ?? '';
      const m = /filename="([^"]+)"/.exec(cd);
      const fname = m?.[1] ?? `${gwId}-running-config.txt`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const captured = res.headers.get('X-Captured-At');
      setMsg(captured
        ? `Running-config download started — captured ${new Date(Number(captured)).toLocaleString()}.`
        : 'Running-config download started.');
      setPicking(null); setOpen(false);
    } catch (e: any) {
      setMsg(`Backup failed: ${e?.message ?? e}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative">
      <button onClick={openMenu}
        className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-900/30">
        💾 Backup config ▾
      </button>
      {open && (
        <div className="absolute z-20 top-full mt-1 right-0 w-[360px] rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-white dark:bg-gray-900 shadow-lg p-3 space-y-2">
          {!picking && (
            <>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Pick a backup mode</div>
              <button onClick={() => setPicking('trinethub')}
                className="w-full text-left p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800">
                <div className="text-sm font-medium">TriNetHub-config snapshot</div>
                <div className="text-xs text-gray-500">JSON: gateways + dial peers + SIP trunks + certs from D1</div>
              </button>
              <button onClick={() => setPicking('running')}
                className="w-full text-left p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800">
                <div className="text-sm font-medium">Device running-config</div>
                <div className="text-xs text-gray-500">Live `show running-config` via the cube-svc spoke</div>
              </button>
            </>
          )}
          {picking === 'trinethub' && (
            <>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">TriNetHub-config snapshot</div>
              <select value={selectedGw} onChange={(e) => setSelectedGw(e.target.value)}
                className="w-full px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
                <option value="">All gateways (full tenant snapshot)</option>
                {gateways.map((g) => <option key={g.gateway_id} value={g.gateway_id}>{g.name}</option>)}
              </select>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadTriNetHubBackup(selectedGw)} disabled={busy}
                  className="px-3 py-1.5 rounded-lg bg-tnh-accent text-white text-sm font-medium disabled:opacity-50">
                  {busy ? 'Preparing…' : 'Download JSON'}
                </button>
                <button onClick={() => setPicking(null)} disabled={busy}
                  className="px-3 py-1.5 rounded-lg text-sm text-gray-600 dark:text-gray-300">Back</button>
              </div>
            </>
          )}
          {picking === 'running' && (
            <>
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Device running-config</div>
              {gateways.length === 0 ? (
                <div className="text-sm text-gray-500">No gateways registered yet.</div>
              ) : (
                <select value={selectedGw} onChange={(e) => setSelectedGw(e.target.value)}
                  className="w-full px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
                  <option value="">— select gateway —</option>
                  {gateways.map((g) => <option key={g.gateway_id} value={g.gateway_id}>{g.name} ({g.host})</option>)}
                </select>
              )}
              <div className="flex items-center gap-2">
                <button onClick={() => downloadRunningConfig(selectedGw)} disabled={busy || !selectedGw}
                  className="px-3 py-1.5 rounded-lg bg-tnh-accent text-white text-sm font-medium disabled:opacity-50">
                  {busy ? 'Fetching…' : 'Download running-config'}
                </button>
                <button onClick={() => setPicking(null)} disabled={busy}
                  className="px-3 py-1.5 rounded-lg text-sm text-gray-600 dark:text-gray-300">Back</button>
              </div>
              <div className="text-[11px] text-gray-500">
                Returns the latest snapshot pushed by the cube-svc spoke. If the device hasn't been
                reachable, the server surfaces that state — no fabricated output.
              </div>
            </>
          )}
          {msg && <div className="text-xs text-gray-600 dark:text-gray-300 pt-1 border-t border-tnh-border dark:border-tnh-border-dark">{msg}</div>}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// LgwHubPanel — CUBE/LGW → Local Gateways sub-tab.
//
// Lists every Local Gateway registered in Webex Control Hub, live from the
// Webex Calling API (premise-PSTN trunks) via wxc-svc. No spoke, no D1 — the
// list reflects Control Hub directly, so it always matches what the Webex
// admin sees. Each row is enriched with the trunk detail (outbound proxy,
// capacity) by the service before it reaches here.
// ===========================================================================
function LgwHubPanel() {
  const [rows, setRows] = useState<WxcLocalGateway[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.wxcLocalGateways();
      setRows(r.local_gateways ?? []);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load Local Gateways from Webex Control Hub');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = (rows ?? []).filter((r) =>
    !filter || JSON.stringify(r).toLowerCase().includes(filter.toLowerCase()));

  const statusClass = (s: string | null) =>
    s && /online|registered|ok|up|active/i.test(s)
      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : s && /offline|down|unreg|fail|error/i.test(s)
      ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
    : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300';

  const cols = ['Name', 'Location', 'Trunk type', 'Outbound proxy', 'Max calls', 'In use', 'Status'];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <input value={filter} onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter Local Gateways…"
          className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm w-64" />
        <button onClick={load} disabled={loading} className="btn-ghost text-sm disabled:opacity-50">↻ Refresh</button>
        <span className="text-xs text-gray-500">
          {loading ? 'Loading…' : `${filtered.length} Local Gateway${filtered.length === 1 ? '' : 's'} in Webex Control Hub`}
        </span>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
              <tr>
                {cols.map((c) => (
                  <th key={c} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={cols.length} className="px-4 py-12 text-center text-sm text-gray-500">Loading Local Gateways from Webex Control Hub…</td></tr>}
              {error && <tr><td colSpan={cols.length} className="px-4 py-8 text-center text-sm text-rose-600">{error}</td></tr>}
              {!loading && !error && filtered.length === 0 && (
                <tr><td colSpan={cols.length} className="px-4 py-12 text-center text-sm text-gray-500">
                  No Local Gateways {filter ? 'match the filter.' : 'found in this Webex Calling org.'}
                </td></tr>
              )}
              {!loading && !error && filtered.map((g) => (
                <tr key={g.id} className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/30">
                  <td className="px-4 py-2 font-medium whitespace-nowrap">{g.name}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{g.location_name}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{g.trunk_type}</td>
                  <td className="px-4 py-2 font-mono text-[11px] whitespace-nowrap">{g.outbound_proxy ?? '—'}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{g.max_concurrent_calls ?? '—'}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{g.in_use ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${statusClass(g.device_status)}`}>
                      {g.device_status ?? 'unknown'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-gray-500">
        Live from Webex Control Hub — Webex Calling premise-PSTN trunks (wxc-svc). No spoke required.
      </p>
    </div>
  );
}

// ===========================================================================
// LgwWebexPanel — CUBE/LGW → Route Lists / Dial Plans / Locations sub-tabs.
//
// One component, three live Webex Calling views. Route lists and dial plans
// come from the premise-PSTN config API; locations from the org's location
// list. All read live via wxc-svc — no spoke, no D1, no placeholder.
// ===========================================================================
type LgwWebexKey = 'routes' | 'dialplans' | 'locations';

function LgwWebexPanel({ areaKey }: { areaKey: LgwWebexKey }) {
  const [rows, setRows] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true); setError(null);
    try {
      if (areaKey === 'routes') {
        const r = await api.wxcRouteLists(); setRows(r.route_lists ?? []);
      } else if (areaKey === 'dialplans') {
        const r = await api.wxcDialPlans(); setRows(r.dial_plans ?? []);
      } else {
        const r = await api.wxcLocations(); setRows(r.locations ?? []);
      }
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load from Webex Control Hub');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [areaKey]);

  const filtered = (rows ?? []).filter((r) =>
    !filter || JSON.stringify(r).toLowerCase().includes(filter.toLowerCase()));

  const meta: Record<LgwWebexKey, { title: string; cols: string[] }> = {
    routes:    { title: 'route lists', cols: ['Route list', 'Location', 'Route group'] },
    dialplans: { title: 'dial plans',  cols: ['Dial plan', 'Route', 'Route type'] },
    locations: { title: 'locations',   cols: ['Location', 'City', 'State', 'Country', 'Time zone'] },
  };
  const { title, cols } = meta[areaKey];

  const renderRow = (r: any): ReactNode[] => {
    if (areaKey === 'routes') {
      const x = r as WxcRouteList;
      return [x.name, x.location_name, x.route_group_name];
    }
    if (areaKey === 'dialplans') {
      const x = r as WxcDialPlan;
      return [x.name, x.route_name, x.route_type];
    }
    const x = r as WxcLocation;
    return [x.name, x.address?.city ?? '—', x.address?.state ?? '—', x.address?.country ?? '—', x.timeZone ?? '—'];
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <input value={filter} onChange={(e) => setFilter(e.target.value)}
          placeholder={`Filter ${title}…`}
          className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm w-64" />
        <button onClick={load} disabled={loading} className="btn-ghost text-sm disabled:opacity-50">↻ Refresh</button>
        <span className="text-xs text-gray-500">
          {loading ? 'Loading…' : `${filtered.length} ${title} in Webex Control Hub`}
        </span>
      </div>
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
              <tr>
                {cols.map((c) => (
                  <th key={c} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={cols.length} className="px-4 py-12 text-center text-sm text-gray-500">Loading {title} from Webex Control Hub…</td></tr>}
              {error && <tr><td colSpan={cols.length} className="px-4 py-8 text-center text-sm text-rose-600">{error}</td></tr>}
              {!loading && !error && filtered.length === 0 && (
                <tr><td colSpan={cols.length} className="px-4 py-12 text-center text-sm text-gray-500">
                  No {title} {filter ? 'match the filter.' : 'configured in this Webex Calling org.'}
                </td></tr>
              )}
              {!loading && !error && filtered.map((r, i) => (
                <tr key={r.id ?? i} className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/30">
                  {renderRow(r).map((cell, j) => (
                    <td key={j} className="px-4 py-2 text-sm whitespace-nowrap">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-gray-500">Live from Webex Control Hub via wxc-svc — no spoke required.</p>
    </div>
  );
}

// ===========================================================================
//  New panels for the CUBE/LGW SPLIT (BUILD-TASK-lgw-cube-split).
//  - Local Gateway module: every tab is wired to wxc-svc (Webex Calling cloud)
//  - CUBE module:          every tab is wired to lgw-svc (cube-svc spoke)
//  Where a concept has no direct equivalent on one side, the panel renders an
//  honest empty / "managed on the other side" state — no fabrication.
// ===========================================================================

// ── Local Gateway · Dial peers ────────────────────────────────────────────
// Dial peers are the #1 troubleshooting surface for PSTN + Webex Calling, so
// the LGW module shows the SAME device dial peers as the CUBE module (two views
// of one physical Local Gateway) — read-only here, editable in CUBE. Below the
// peers we keep the cloud-side trunk context (outbound proxy each trunk presents
// to Webex), which is the Webex Calling half of the same call path.
function LgwCloudDialPeersPanel() {
  const [rows, setRows] = useState<WxcLocalGateway[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.wxcLocalGateways()
      .then((r) => { setRows(r.local_gateways ?? []); setLoading(false); })
      .catch((e) => { setError(e?.message ?? 'Failed to load trunks'); setLoading(false); });
  }, []);
  return (
    <div className="space-y-3">
      {/* Architecture rule: the LGW (Webex Calling) module sources from the WxC
          Hub; the on-prem device dial peers source from the LB tunnel and live
          in the CUBE module. This tab is therefore hub-only — it never reads the
          tunnel (that was the cross-wiring bug). Same pattern as Active calls. */}
      <div className="rounded-lg border border-amber-300 bg-amber-50/70 dark:bg-amber-900/15 p-3 text-xs">
        <strong className="text-amber-800 dark:text-amber-200">Cloud view only.</strong>{' '}
        This tab pools the Local Gateways straight from the Webex Calling cloud API (WxC Hub) — the premise-PSTN
        trunk each LGW presents, its type and registration status. The IOS-XE <code className="font-mono">dial-peer voice</code>
        entries themselves are device config: see the <strong>CUBE</strong> module → <em>Dial peers</em>, which reads
        them over the Long Beach tunnel.
      </div>
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
            <tr>{['Local Gateway','Location','Trunk type','Outbound proxy','In use','Status'].map((c) => (
              <th key={c} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{c}</th>
            ))}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">Loading Local Gateways…</td></tr>}
            {error && <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-rose-600">{error}</td></tr>}
            {!loading && !error && (rows ?? []).length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500">No premise-PSTN trunks in this Webex Calling org.</td></tr>
            )}
            {!loading && !error && (rows ?? []).map((t) => (
              <tr key={t.id} className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/30">
                <td className="px-4 py-2 font-medium">{t.name}</td>
                <td className="px-4 py-2 whitespace-nowrap">{t.location_name}</td>
                <td className="px-4 py-2 whitespace-nowrap">{t.trunk_type}</td>
                <td className="px-4 py-2 font-mono text-[11px]">{t.outbound_proxy ?? '—'}</td>
                <td className="px-4 py-2">{t.in_use ? 'Yes' : 'No'}</td>
                <td className="px-4 py-2 whitespace-nowrap text-xs">{t.device_status ?? 'unknown'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Local Gateway · Active calls (cloud) ──────────────────────────────────
// Webex exposes per-trunk capacity (max concurrent calls) and the trunk's
// in_use indicator. Per-call detail is on the device (see CUBE module).
function LgwCloudActiveCallsPanel() {
  const [rows, setRows] = useState<WxcLocalGateway[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.wxcLocalGateways()
      .then((r) => { setRows(r.local_gateways ?? []); setLoading(false); })
      .catch((e) => { setError(e?.message ?? 'Failed to load trunks'); setLoading(false); });
  }, []);
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-amber-300 bg-amber-50/70 dark:bg-amber-900/15 p-3 text-xs">
        <strong className="text-amber-800 dark:text-amber-200">Cloud view only.</strong>{' '}
        The Webex Calling API exposes per-trunk concurrent-call capacity and an in-use indicator. Per-call
        detail (calling/called numbers, codec, duration) requires the on-box <code className="font-mono">show call active voice brief</code>
        capture — see the <strong>CUBE</strong> module → <em>Active calls</em>.
      </div>
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
            <tr>{['Trunk','Location','Concurrent / Max','Active','Status'].map((c) => (
              <th key={c} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{c}</th>
            ))}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500">Loading trunks…</td></tr>}
            {error && <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-rose-600">{error}</td></tr>}
            {!loading && !error && (rows ?? []).length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500">No trunks registered.</td></tr>
            )}
            {!loading && !error && (rows ?? []).map((t) => (
              <tr key={t.id} className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/30">
                <td className="px-4 py-2 font-medium">{t.name}</td>
                <td className="px-4 py-2 whitespace-nowrap">{t.location_name}</td>
                <td className="px-4 py-2 whitespace-nowrap font-mono text-[11px]">— / {t.max_concurrent_calls ?? '—'}</td>
                <td className="px-4 py-2 whitespace-nowrap">{t.in_use ? 'Yes' : 'No'}</td>
                <td className="px-4 py-2 whitespace-nowrap text-xs">{t.device_status ?? 'unknown'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Local Gateway · Backup config (cloud) ──────────────────────────────────
// Click → triggers a download from wxc-svc /api/wxc/lgw-backup (JSON bundle
// of trunks + route lists + dial plans + locations). Pairs with CUBE's
// device-side running-config + D1 snapshot download.
function LgwCloudBackupPanel() {
  const trigger = () => {
    const a = document.createElement('a');
    a.href = `${(import.meta as any).env.VITE_API_BASE ?? 'https://api.trinethub.com'}/api/wxc/lgw-backup`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };
  return (
    <div className="card p-5 space-y-3">
      <div className="text-base font-semibold">Webex Calling LGW configuration backup</div>
      <p className="text-sm text-gray-500 dark:text-gray-400 max-w-3xl">
        Downloads a JSON snapshot of every Webex Calling object a Local Gateway depends on for this tenant:
        premise-PSTN trunks (with outboundProxy + capacity), route lists, dial plans, and locations.
        This is the <strong>cloud</strong> side of the backup. The device-side <code className="font-mono">show running-config</code>
        backup is in the <strong>CUBE</strong> module → <em>Backup config</em>.
      </p>
      <div>
        <button onClick={trigger}
          className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold">
          ⬇ Download Webex LGW backup (.json)
        </button>
      </div>
      <p className="text-[11px] text-gray-500">
        Source: <code className="font-mono">wxc-svc /api/wxc/lgw-backup</code>. The file streams directly from the
        Worker; if the Service App is missing any of the underlying scopes, the snapshot's <code className="font-mono">errors</code>
        block surfaces the failure verbatim.
      </p>
    </div>
  );
}

// ── CUBE · Status (device) ────────────────────────────────────────────────
// Aggregates lgw_gateways (the LGW registry) with the cube-svc spoke sync
// status so the admin sees both the device inventory AND whether the spoke
// has reported in.
function CubeStatusPanel() {
  const [rows, setRows] = useState<LgwGateway[] | null>(null);
  const [sync, setSync] = useState<{ sync_runs: LgwSyncRun[]; total: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    Promise.all([api.lgwGateways(), api.lgwSyncStatus().catch(() => ({ sync_runs: [], total: 0 }))])
      .then(([g, s]) => { setRows(g.gateways ?? []); setSync(s as any); setLoading(false); })
      .catch((e) => { setError(e?.message ?? 'Failed to load device status'); setLoading(false); });
  }, []);
  const latestSyncByGw = new Map<string, LgwSyncRun>();
  for (const r of (sync?.sync_runs ?? [])) {
    const existing = latestSyncByGw.get(r.gateway_id);
    if (!existing || (r.started_at ?? 0) > (existing.started_at ?? 0)) latestSyncByGw.set(r.gateway_id, r);
  }
  const fmtAge = (ms: number | null) => {
    if (!ms) return 'never';
    const mins = Math.floor((Date.now() - ms) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };
  const statusClass = (s: string) =>
    s === 'reachable' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
    : s === 'unreachable' || s === 'error' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
    : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  return (
    <div className="space-y-3">
      <div className="text-xs text-gray-500">
        Device-side status from <code className="font-mono">lgw-svc /api/lgw/gateways</code> + <code className="font-mono">/api/lgw/sync-status</code>.
        The cube-svc spoke's last sync timestamp confirms the device is still phoning home.
      </div>
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
            <tr>{['Gateway','Host','Mode','Status','Last seen','IOS version','Last spoke sync'].map((c) => (
              <th key={c} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{c}</th>
            ))}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading device status…</td></tr>}
            {error && <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-rose-600">{error}</td></tr>}
            {!loading && !error && (rows ?? []).length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">
                No CUBE/LGW gateways registered yet. Add one from the <strong>Local Gateway</strong> module → <em>TLS Certificates</em> tab → <em>+ Add Gateway</em>.
              </td></tr>
            )}
            {!loading && !error && (rows ?? []).map((g) => {
              const lastSync = latestSyncByGw.get(g.gateway_id);
              return (
                <tr key={g.gateway_id} className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/30">
                  <td className="px-4 py-2 font-medium">{g.name}</td>
                  <td className="px-4 py-2 font-mono text-[11px]">{g.host}</td>
                  <td className="px-4 py-2 text-xs">{g.lgw_mode}</td>
                  <td className="px-4 py-2"><span className={`text-[11px] px-2 py-0.5 rounded-full ${statusClass(g.status)}`}>{g.status}</span></td>
                  <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">{fmtAge(g.last_seen_at ? g.last_seen_at * 1000 : null)}</td>
                  <td className="px-4 py-2 text-xs font-mono">{g.ios_version || '—'}</td>
                  <td className="px-4 py-2 text-xs whitespace-nowrap">{lastSync ? fmtAge(lastSync.started_at ?? null) : 'no spoke sync yet'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── CUBE · Route Lists / Dial Plans / Locations (device) ──────────────────
// Honest "managed in Webex" / "spoke does not push this yet" empty-state for
// the three tabs that don't have a current device-side equivalent populated.
function CubeManagedInWebexPanel({ which }: { which: 'routes' | 'dialplans' | 'locations' }) {
  const meta: Record<typeof which, { title: string; line1: string; line2: string }> = {
    routes:    {
      title: 'Device routing config',
      line1: 'The cube-svc spoke does not push route-list config rows yet. The cloud Webex Calling route lists (the source of truth for routing into your trunks) live in the Local Gateway module.',
      line2: 'See: Local Gateway → Route Lists.',
    },
    dialplans: {
      title: 'Device dial-plan config',
      line1: 'The cube-svc spoke does not push dial-plan config rows yet. The cloud Webex Calling dial plans (dial pattern → route binding) live in the Local Gateway module.',
      line2: 'See: Local Gateway → Dial Plans.',
    },
    locations: {
      title: 'Locations',
      line1: 'Locations are a Webex Calling concept — they bind users / numbers / trunks to a site. The CUBE device has no equivalent.',
      line2: 'See: Local Gateway → Locations.',
    },
  };
  const m = meta[which];
  return (
    <div className="card p-6 text-center space-y-2">
      <div className="text-base font-semibold text-gray-700 dark:text-gray-200">{m.title}</div>
      <p className="text-sm text-gray-500 max-w-2xl mx-auto">{m.line1}</p>
      <p className="text-xs text-tnh-accent">{m.line2}</p>
    </div>
  );
}

// ── CUBE · Backup config (device) ──────────────────────────────────────────
// Inline UI for the two device-side backup actions:
//   1. D1 snapshot — JSON dump of every lgw-svc owned table (gateways +
//      dial peers + SIP trunks + certs). Always works.
//   2. show running-config — IOS-XE running-config pulled by the cube-svc
//      spoke. Returns honest 409 no_capture_yet until the spoke pushes one.
// ── CUBE · TLS Certificates (device) ──────────────────────────────────────
// Live PKI inventory pulled by the cube-svc spoke from each IOS-XE device via
// `show crypto pki certificates`. Real notAfter / notBefore are parsed off
// the device and stored in cube_device_certs. The Total / Healthy /
// Expiring / Critical / Expired tiles are computed server-side (lgw-svc) so
// the SPA just renders. If a gateway is reachable but the spoke hasn't pushed
// certs yet, the device is named in the "Awaiting first sync" empty state —
// we never fabricate a notAfter.
function CubeDeviceCertsPanel() {
  const [data, setData] = useState<{
    certs: CubeDeviceCert[];
    counts: { total: number; healthy: number; expiring: number; critical: number; expired: number; unparsed: number };
    gateways: Array<{ gateway_id: string; name: string; last_seen_at: number | null }>;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gwFilter, setGwFilter] = useState<string>('');
  const [filter, setFilter] = useState('');

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.lgwCubeCerts(gwFilter || undefined);
      setData({ certs: r.certs ?? [], counts: r.counts, gateways: r.gateways ?? [] });
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load device certs');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [gwFilter]);

  const counts = data?.counts ?? { total: 0, healthy: 0, expiring: 0, critical: 0, expired: 0, unparsed: 0 };
  const fmtDate = (epochSec: number | null) => epochSec ? new Date(epochSec * 1000).toISOString().slice(0, 10) : '—';
  const fmtAgeMs = (ms: number | null) => {
    if (!ms) return 'never';
    const mins = Math.floor((Date.now() - ms) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    return hrs < 24 ? `${hrs}h ago` : `${Math.floor(hrs / 24)}d ago`;
  };
  const expiryClass = (days: number | null, status: string): string => {
    if (status === 'expired' || (days != null && days <= 0)) return 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200';
    if (days == null) return 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
    if (days <= 7)    return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200';
    if (days <= 30)   return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
  };
  const certs = (data?.certs ?? []).filter((c) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return [c.cn, c.gateway_name, c.trustpoint, c.serial_hex, c.cert_type, c.issuer_dn, c.subject_dn]
      .some((v) => (v ?? '').toLowerCase().includes(q));
  });

  // Gateways the spoke knows about but for which it has not pushed certs yet.
  const gwsWithoutCerts = (data?.gateways ?? []).filter((g) =>
    !(data?.certs ?? []).some((c) => c.gateway_id === g.gateway_id)
  );

  return (
    <div className="space-y-3">
      {/* Tiles — populated from real data via lgw-svc */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {[
          { label: 'Total certs',     val: counts.total,    cls: 'text-gray-700 dark:text-gray-200' },
          { label: 'Healthy (>30d)',  val: counts.healthy,  cls: 'text-emerald-600 dark:text-emerald-300' },
          { label: 'Expiring (7-30d)', val: counts.expiring, cls: 'text-amber-600 dark:text-amber-300' },
          { label: 'Critical (≤7d)',  val: counts.critical, cls: 'text-red-600 dark:text-red-300' },
          { label: 'Expired',         val: counts.expired,  cls: 'text-rose-600 dark:text-rose-300' },
        ].map((k) => (
          <div key={k.label} className="card p-3">
            <div className={`text-2xl font-semibold ${k.cls}`}>{loading ? '—' : k.val}</div>
            <div className="text-[11px] text-gray-500 mt-0.5">{k.label}</div>
          </div>
        ))}
      </div>

      {/* Action bar */}
      <div className="card p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-base font-semibold">On-box PKI Certificates (live via spoke)</div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-3xl">
              Pulled from each IOS-XE device by the cube-svc spoke via{' '}
              <code className="font-mono text-xs">show crypto pki certificates</code>. The expiry column shows the device's
              real <code className="font-mono text-xs">notAfter</code> — no manual upload required. Refreshes on every spoke
              cycle (≈5 min). If a device is unreachable the spoke marks the gateway down; once it comes back, certs
              re-sync on the next cycle.
            </p>
          </div>
          <button onClick={load} disabled={loading} className="btn-ghost text-sm disabled:opacity-50">↻ Refresh</button>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <select value={gwFilter} onChange={(e) => setGwFilter(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
            <option value="">All gateways</option>
            {(data?.gateways ?? []).map((g) => <option key={g.gateway_id} value={g.gateway_id}>{g.name}</option>)}
          </select>
          <input value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by CN / issuer / trustpoint / serial…"
            className="ml-auto px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm w-80" />
        </div>
      </div>

      {/* Cert inventory */}
      <div className="card overflow-hidden">
        <div className="px-4 py-2.5 border-b border-tnh-border dark:border-tnh-border-dark flex items-center gap-3 flex-wrap">
          <div className="font-semibold">Device PKI inventory</div>
          <span className="text-xs text-gray-500">
            {loading ? 'Loading…' : `${certs.length}${filter ? ` of ${data?.certs.length ?? 0}` : ''} certificates`}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
              <tr>
                {['CN', 'Gateway', 'Trustpoint', 'Type', 'Issuer', 'Expires (notAfter)', 'Status', 'Last spoke sync'].map((c) => (
                  <th key={c} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-500">Loading device PKI inventory…</td></tr>}
              {error && <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-rose-600">{error}</td></tr>}
              {!loading && !error && certs.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">
                  {filter
                    ? 'No certs match the filter.'
                    : (data?.gateways.length === 0
                       ? 'No CUBE gateways registered yet. Add one from the Local Gateway module → TLS Certificates tab → + Add Gateway.'
                       : 'No PKI certificates pushed by the spoke yet — devices are reachable but the cube-svc spoke has not run a cycle, or the spoke version on the customer-LAN host has not been redeployed to include the new pki_certificates scrape (this build adds it).')}
                </td></tr>
              )}
              {!loading && !error && certs.map((c) => (
                <tr key={`${c.gateway_id}__${c.trustpoint}__${c.serial_hex}`} className="border-t border-tnh-border dark:border-tnh-border-dark hover:bg-gray-50 dark:hover:bg-gray-900/30">
                  <td className="px-4 py-2.5">
                    <div className="font-mono text-xs">{c.cn || '(no CN)'}</div>
                    <div className="text-[10px] text-gray-500">{c.usage_str || '—'}</div>
                  </td>
                  <td className="px-4 py-2.5 text-xs">{c.gateway_name || c.gateway_id.slice(0, 8)}</td>
                  <td className="px-4 py-2.5 text-xs font-mono">{c.trustpoint || '—'}</td>
                  <td className="px-4 py-2.5 text-xs">{c.cert_type || '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-600 dark:text-gray-300">{(c.issuer_dn || '').replace(/cn=/i, '').split(',')[0] || '—'}</td>
                  <td className="px-4 py-2.5 text-xs whitespace-nowrap">{fmtDate(c.not_after)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${expiryClass(c.days_until_expiry, c.status_live)}`}>
                      {c.days_until_expiry == null
                        ? (c.status_live || 'unknown')
                        : c.days_until_expiry <= 0 ? `expired ${-c.days_until_expiry}d ago`
                        : `${c.days_until_expiry}d left`}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmtAgeMs(c.synced_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Gateways with no certs yet — surface them so the user knows the
          gateway is registered but the spoke hasn't pushed certs for it. */}
      {!loading && !error && gwsWithoutCerts.length > 0 && (
        <div className="card p-3 text-xs">
          <div className="font-semibold text-gray-700 dark:text-gray-200 mb-1">
            Awaiting first cert sync ({gwsWithoutCerts.length})
          </div>
          <ul className="space-y-0.5 text-gray-600 dark:text-gray-300">
            {gwsWithoutCerts.map((g) => (
              <li key={g.gateway_id}>
                <code className="font-mono">{g.name}</code> — last seen{' '}
                {g.last_seen_at ? new Date(g.last_seen_at).toLocaleString() : 'never'}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function CubeBackupPanel() {
  const [gateways, setGateways] = useState<LgwGateway[]>([]);
  const [selectedGw, setSelectedGw] = useState<string>('');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.lgwGateways().then((r) => {
      setGateways(r.gateways ?? []);
      if (!selectedGw && r.gateways?.[0]) setSelectedGw(r.gateways[0].gateway_id);
    }).catch(() => { /* show empty selector */ });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const downloadD1Snapshot = (gwId?: string) => {
    setMsg(null);
    const a = document.createElement('a');
    a.href = api.lgwBackupConfigUrl(gwId || undefined);
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setMsg('D1 snapshot download started.');
  };

  const downloadRunningConfig = async (gwId: string) => {
    if (!gwId) { setMsg('Pick a gateway first.'); return; }
    setBusy(true); setMsg(null);
    try {
      const res = await fetch(api.lgwGatewayRunningConfigUrl(gwId), { credentials: 'include' });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { const j = await res.json(); if (j?.error || j?.message) detail = j.message || j.error; } catch {}
        setMsg(`Running-config unavailable: ${detail}`);
        return;
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') ?? '';
      const m = /filename="([^"]+)"/.exec(cd);
      const fname = m?.[1] ?? `${gwId}-running-config.txt`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const captured = res.headers.get('X-Captured-At');
      setMsg(captured
        ? `Running-config download started — captured ${new Date(Number(captured)).toLocaleString()}.`
        : 'Running-config download started.');
    } catch (e: any) {
      setMsg(`Backup failed: ${e?.message ?? e}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="card p-5 space-y-3">
        <div className="text-base font-semibold">TriNetHub config snapshot (D1)</div>
        <p className="text-sm text-gray-500 max-w-3xl">
          JSON dump of every lgw-svc-owned row for this tenant — gateways, dial peers, SIP trunks, certs.
          Always available (the hub's own data).
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => downloadD1Snapshot()} disabled={busy}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold disabled:opacity-50">
            ⬇ Snapshot — all gateways
          </button>
          {gateways.length > 0 && (
            <>
              <span className="text-xs text-gray-500">or scoped to one:</span>
              <select value={selectedGw} onChange={(e) => setSelectedGw(e.target.value)}
                className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
                {gateways.map((g) => <option key={g.gateway_id} value={g.gateway_id}>{g.name}</option>)}
              </select>
              <button onClick={() => downloadD1Snapshot(selectedGw)} disabled={busy || !selectedGw}
                className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-900/30 disabled:opacity-50">
                ⬇ Snapshot — selected gateway
              </button>
            </>
          )}
        </div>
      </div>

      <div className="card p-5 space-y-3">
        <div className="text-base font-semibold">IOS-XE running-config (live device)</div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Triggers <code className="font-mono">show running-config</code> via the cube-svc spoke and downloads the
          plain-text capture. If the spoke has not pushed a snapshot yet (no spoke / device unreachable), the
          server returns an honest <code className="font-mono">no_capture_yet</code> — never a fake.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          {gateways.length === 0 ? (
            <span className="text-xs text-gray-500">No gateways registered yet — add one via the Local Gateway module → TLS Certificates tab.</span>
          ) : (
            <>
              <select value={selectedGw} onChange={(e) => setSelectedGw(e.target.value)}
                className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm">
                {gateways.map((g) => <option key={g.gateway_id} value={g.gateway_id}>{g.name} ({g.host})</option>)}
              </select>
              <button onClick={() => downloadRunningConfig(selectedGw)} disabled={busy || !selectedGw}
                className="px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium disabled:opacity-50">
                {busy ? 'Fetching…' : '⬇ Download running-config'}
              </button>
            </>
          )}
        </div>
      </div>

      {msg && <div className="text-sm text-tnh-accent">{msg}</div>}
    </div>
  );
}

function AreaPanel({ spec, area, state }: { spec: ModuleSpec; area: FeatureArea; state: string }) {
  const isConnected = state === 'connected';
  const primaryToneClass = area.primary?.tone === 'webex'
    ? 'bg-violet-600 hover:bg-violet-500'
    : 'bg-indigo-600 hover:bg-indigo-500';

  return (
    <div className="space-y-3">
      {/* Action bar (Webex Control Hub pattern: search left, primary right) */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <input
            placeholder={`Search ${area.label.toLowerCase()}…`}
            disabled={!isConnected}
            className="px-3 py-1.5 rounded-lg border border-tnh-border dark:border-tnh-border-dark bg-transparent text-sm w-64 disabled:opacity-50"
          />
          <button disabled className="btn-ghost text-sm disabled:opacity-50">Filter</button>
          <button disabled className="btn-ghost text-sm disabled:opacity-50">Export CSV</button>
        </div>
        {area.primary && (
          <button
            disabled={!isConnected}
            className={`text-white text-sm font-semibold px-4 py-1.5 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed ${primaryToneClass}`}
            title={isConnected ? '' : 'Connect this module first'}
          >
            {area.primary.label}
          </button>
        )}
      </div>

      {/* Skeleton table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 border-b border-tnh-border dark:border-tnh-border-dark">
              <tr>
                {area.columns.map((c) => (
                  <th key={c} className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Empty state row */}
              <tr>
                <td colSpan={area.columns.length} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 grid place-items-center text-gray-400 text-lg">
                      {isConnected ? '⟳' : '∅'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {(() => {
                        // Vendor-aware messages:
                        //   Cloud (Webex) modules: hub → webexapis.com / api.wxcc-*.cisco.com (direct, no spoke)
                        //   On-prem (Cisco)       : hub → spoke container on customer LAN → device
                        const isCloud = spec.vendor === 'Webex';
                        if (!isConnected) {
                          return `Connect ${spec.title} in the Configuration Manager to populate this view.`;
                        }
                        if (isCloud) {
                          return `${area.label} for ${spec.title} — wiring this view to the live Webex API endpoint is the next iteration.`;
                        }
                        return `Waiting for the spoke to sync ${area.label.toLowerCase()} from your on-prem ${spec.title} deployment.`;
                      })()}
                    </div>
                    {!isConnected && (
                      <Link to="/configuration" className="text-tnh-accent text-xs hover:underline">
                        Open Configuration Manager →
                      </Link>
                    )}
                    {isConnected && spec.vendor !== 'Webex' && (
                      <Link to="/spokes" className="text-tnh-accent text-xs hover:underline">
                        Manage spoke install → (required for on-prem modules)
                      </Link>
                    )}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {area.api_note && (
          <div className="px-4 py-2 border-t border-tnh-border dark:border-tnh-border-dark text-[11px] text-gray-500 bg-gray-50 dark:bg-gray-900/40">
            {area.api_note}
          </div>
        )}
      </div>
    </div>
  );
}

function ConnectionPill({ state, configured, total }: { state: 'unknown' | 'not_configured' | 'partial' | 'connected'; configured: number; total: number }) {
  if (state === 'unknown') return <span className="pill bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">checking…</span>;
  if (state === 'not_configured') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 text-xs font-semibold">
        <span className="w-1.5 h-1.5 rounded-full bg-rose-500" /> Not connected
      </span>
    );
  }
  if (state === 'partial') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 text-xs font-semibold">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Setup {configured}/{total}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 text-xs font-semibold">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Connected
    </span>
  );
}
